<script setup lang="ts">
import {
  Activity as ActivityIcon,
  Bookmark as BookmarkIcon,
  ChevronLeft as ChevronLeftIcon,
  Download,
  Eye as EyeIcon,
  Funnel as FunnelIcon,
  Globe as GlobeIcon,
  EyeOff as EyeOffIcon,
  Layers as LayersIcon,
  MapPinned as MapPinnedIcon,
  PanelTopOpen as PanelTopOpenIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  SquarePlus as SquarePlusIcon,
  Upload as UploadIcon,
  Route as RouteIcon,
  Share2 as Share2Icon,
  Users as UsersIcon,
  X as XIcon,
} from '@lucide/vue'
import { useDebounceFn } from '@vueuse/core'
import maplibregl, {
  type FitBoundsOptions,
  type LngLatBoundsLike,
  type LngLatLike,
} from 'maplibre-gl'
import {
  ref,
  type ComponentPublicInstance,
  provide,
  shallowRef,
  watch,
  watchEffect,
  nextTick,
  computed,
  type Ref,
  onUnmounted,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import OfflineDownloadDialog from '@/components/dialogs/OfflineDownloadDialog.vue'
import CreateCollectionIcon from '@/components/icons/CreateCollectionIcon.vue'
import AddToCollectionHost from '@/components/map/AddToCollectionHost.vue'
import CanvasSheet from '@/components/map/CanvasSheet.vue'
import CreateMenu from '@/components/map/CreateMenu.vue'
import DetailSheet from '@/components/map/DetailSheet.vue'
import DrawingTrackLayer from '@/components/map/layers/DrawingTrackLayer.vue'
import LibraryTourLayer from '@/components/map/layers/LibraryTourLayer.vue'
import PhotoMarkerLayer from '@/components/map/layers/PhotoMarkerLayer.vue'
import PoiBrowseLayer from '@/components/map/layers/PoiBrowseLayer.vue'
import RecordingTrackLayer from '@/components/map/layers/RecordingTrackLayer.vue'
import RoutePointLayer from '@/components/map/layers/RoutePointLayer.vue'
import SmartSearchPinLayer from '@/components/map/layers/SmartSearchPinLayer.vue'
import TrackEditReferenceLayer from '@/components/map/layers/TrackEditReferenceLayer.vue'
import TrackSegmentSelectionLayer from '@/components/map/layers/TrackSegmentSelectionLayer.vue'
import WaypointMarkerLayer from '@/components/map/layers/WaypointMarkerLayer.vue'
import WorkspacePOIMarkerLayer from '@/components/map/layers/WorkspacePOIMarkerLayer.vue'
import SearchCollectionLayer from '@/components/map/layers/WorkspaceSearchLayer.vue'
import WorkspaceTourLayer from '@/components/map/layers/WorkspaceTourLayer.vue'
import WorkspaceTrackProfileMetricLayer from '@/components/map/layers/WorkspaceTrackProfileMetricLayer.vue'
import WorkspaceWaypointDotLayer from '@/components/map/layers/WorkspaceWaypointDotLayer.vue'
import LayersList from '@/components/map/LayersList.vue'
import LayersPanel from '@/components/map/LayersPanel.vue'
import MapBackButton from '@/components/map/MapBackButton.vue'
import MapBrandWordmark from '@/components/map/MapBrandWordmark.vue'
import MapCollectionTitle from '@/components/map/MapCollectionTitle.vue'
import MapContentPanel from '@/components/map/MapContentPanel.vue'
import MapLibreRoot from '@/components/map/MapLibreRoot.vue'
import MapSmartSearch from '@/components/map/MapSmartSearch.vue'
import MapToolSheet from '@/components/map/MapToolSheet.vue'
import MapZoomIndicator from '@/components/map/MapZoomIndicator.vue'
import MobileNavigationHud from '@/components/map/MobileNavigationHud.vue'
import MobileRenameActionSheetHost from '@/components/map/MobileRenameActionSheetHost.vue'
import MobileSearchSurface from '@/components/map/MobileSearchSurface.vue'
import MobileTabBar from '@/components/map/MobileTabBar.vue'
import AddWaypointModal from '@/components/map/modals/AddWaypointModal.vue'
import GpxDownloadOptionsModal from '@/components/map/modals/GpxDownloadOptionsModal.vue'
import ShareWorkspaceModal from '@/components/map/modals/ShareWorkspaceModal.vue'
import OfflineMapAreaSelector from '@/components/map/OfflineMapAreaSelector.vue'
import PoiBrowseSheet from '@/components/map/PoiBrowseSheet.vue'
import PoiCategoryBrowser from '@/components/map/PoiCategoryBrowser.vue'
import POIDetailPanel from '@/components/map/toolbar/POIDetailPanel.vue'
import RouteDetailSheet from '@/components/map/toolbar/RouteDetailSheet.vue'
import TourDetailPanel from '@/components/map/toolbar/TourDetailPanel.vue'
import TrackAndWaypointList from '@/components/map/toolbar/TrackAndWaypointList.vue'
import TrackDrawingControlPanel from '@/components/map/toolbar/TrackDrawingControlPanel.vue'
import TrackEditControlPanel from '@/components/map/toolbar/TrackEditControlPanel.vue'
import TrackHelperPointsToolbox from '@/components/map/toolbar/TrackHelperPointsToolbox.vue'
import TrackPlanningSheet from '@/components/map/toolbar/TrackPlanningSheet.vue'
import TrackProfileToolbox, {
  type TrackProfileSummary,
} from '@/components/map/toolbar/TrackProfileToolbox.vue'
import WorkspaceMobileToolbar from '@/components/map/WorkspaceMobileToolbar.vue'
import WorkspaceOverviewFilterControls from '@/components/map/WorkspaceOverviewFilterControls.vue'
import WorkspaceToolbar from '@/components/map/WorkspaceToolbar.vue'
import MapHeader from '@/components/MapHeader.vue'
import PhotoSlideshow from '@/components/PhotoSlideshow.vue'
import type { ActionMenuItem } from '@/components/ui/actionMenu'
import ActionMenuList from '@/components/ui/ActionMenuList.vue'
import BaseFab from '@/components/ui/BaseFab.vue'
import BaseMenu from '@/components/ui/BaseMenu.vue'
import { useEventListener } from '@/composables/useEventListener'
import { usePoiBrowse } from '@/composables/usePoiBrowse'
import { useSyncStatusIndicator } from '@/composables/useSyncStatusIndicator'
import { buildTourPrimaryActionItems } from '@/composables/useTourPrimaryActionItems'
import { useTrackRoutingHost } from '@/composables/useTrackRoutingHost'
import {
  isAddToCollectionPickerOpen,
  openAddToCollection,
  poiToAddToCollectionPoint,
} from '@/services/addToCollectionService'
import { apiService, type WorkspaceSearchItem } from '@/services/apiService'
import {
  getLastCollectionRouteTarget,
  hasLastCollectionContext,
  rememberCollectionsOverviewContext,
  rememberLastCollectionContext,
} from '@/services/collectionContextService'
import { showConfirm } from '@/services/dialogService'
import { downloadFile } from '@/services/downloadService'
import { parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isMobileRenameSheetOpen, openMobileRenameSheet } from '@/services/mobileRenameService'
import { requireOnlineModificationAccess } from '@/services/offlineModificationGuardService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import {
  pinCollectionItems,
  togglePOIPinned,
  toggleTourPinned,
  unpinCollectionItems,
} from '@/services/pinnedService'
import { isTauri } from '@/services/platformService'
import {
  ROUTE_MODE_LEAVE_REQUEST_EVENT,
  requestRouteModeLeave,
  type RouteModeLeaveRequestDetail,
} from '@/services/routeModeLeaveGuardService'
import { isKnownRoutingProfileId, resolveTrackRoutingProfileId } from '@/services/routingProfiles'
import {
  FLOATING_TAB_BAR_HEIGHT,
  floatingSheetBottomOffset,
  readSafeAreaInsetBottomPx,
  safeAreaTopOffset,
  useSafeAreaHeaderClearance,
} from '@/services/safeAreaService'
import { settingsService } from '@/services/settingsService'
import type { SmartSearchLocationEventDetail } from '@/services/smartSearchService'
import { syncService } from '@/services/syncService'
import { showError, showSuccess } from '@/services/toastService'
import { shouldHideAscentForTrack } from '@/services/tourTypeService'
import { TrackColorService } from '@/services/trackColorService'
import { invalidateTrackResolutionCaches } from '@/services/trackResolutionCacheService'
import {
  createWorkspaceTrackRoutingAdapter,
  createLibraryTrackRoutingAdapter,
  type TrackRoutingAdapter,
} from '@/services/trackRoutingAdapter'
import { isSmallScreen } from '@/services/uiService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import {
  uiStatePersistenceService,
  type MapViewportSnapshot,
} from '@/services/uiStatePersistenceService'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useMapStore } from '@/stores/mapStore'
import { useMobileBatchSelectionStore } from '@/stores/mobileBatchSelectionStore'
import { useNavigationStore } from '@/stores/navigationStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useRecordingStore } from '@/stores/recordingStore'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Photo } from '@/types/photo'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour, Track, Segment } from '@/types/tour'
import { getCreatorByline } from '@/utils/creatorByline'
import {
  isMobileCollectionChromeVisible as resolveMobileCollectionChromeVisible,
  isMobileDetailLevel as resolveMobileDetailLevel,
} from '@/utils/mobileNavLevel'
import { requireItemEditable } from '@/utils/permissionDialog'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import { getEffectiveTourName, getWorkspaceTourDownloadName } from '@/utils/tourName'
import { getRootSourceTour, getRootSourceTourClientId } from '@/utils/tourSource'
import { buildTrackEditReferenceDiffSegments } from '@/utils/trackEditReferenceDiff'
import { getBestTrackLineString } from '@/utils/trackGeometry'
import {
  buildTrackProfileAnalyses,
  findCurrentLocationOnTrackProfile,
  findTrackProfilePositionAtDistance,
  partitionTrackProfileLegsByRange,
  type TrackProfileAnalysis,
  type TrackProfileLocationMatch,
  type TrackProfileMetricId,
  mergeTrackProfileLegs,
  type TrackProfileMergedSegment,
  type TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'
import { resolveTrackProfileTrack } from '@/utils/trackProfileSelection'
import { getTrackRoutePoints } from '@/utils/trackRoutePoints'
import { getWorkspaceAuthorByline } from '@/utils/workspaceAuthorByline'
import { Z_INDEX } from '@/utils/zIndex'

const emit = defineEmits<{
  'view-photo': [photoId: string]
  'trigger-gpx-upload': []
  'trigger-photo-upload': []
  'trigger-mixed-upload': []
}>()

const CURRENT_LOCATION_VIEWPORT_MAX_AGE_MS = 10 * 60 * 1000
const CURRENT_LOCATION_VIEWPORT_MATCH_MIN_DISTANCE_M = 250
const CURRENT_LOCATION_VIEWPORT_MATCH_ACCURACY_MULTIPLIER = 3
const COLLECTION_BOUNDS_LOCATION_MARGIN_RATIO = 0.35
const COLLECTION_BOUNDS_LOCATION_MIN_MARGIN_DEGREES = 0.05

const props = withDefaults(
  defineProps<{
    headerRef?: InstanceType<typeof import('@/components/MapHeader.vue').default> | null
    renderHeader?: boolean
  }>(),
  {
    headerRef: null,
    renderHeader: true,
  },
)

const router = useRouter()
const route = useRoute()
const isOfflineMapAreaSelector = computed(() => route.name === 'offline-map-areas')
const authStore = useAuthStore()
const workspaceStore = useWorkspaceStore()

// Mobile sync status button (top-right). Desktop keeps its header sync icon; on mobile MapHeader
// is hidden, so this floating button restores a directly-tappable sync affordance.
const {
  iconComponent: syncIconComponent,
  iconStateClass: syncIconStateClass,
  title: syncButtonTitle,
  needsAttention: syncNeedsAttention,
  badgeCount: syncBadgeCount,
  badgeToneClass: syncBadgeToneClass,
  criticalPresence: syncCriticalPresence,
  isSignedOutWithQueue: syncSignedOutWithQueue,
} = useSyncStatusIndicator()
const openSyncStatus = () => window.dispatchEvent(new CustomEvent('open-sync-status'))
const photoStore = usePhotoStore()
const uiStore = useUiStore()
const libraryStore = useLibraryStore()
const mapStore = useMapStore()
const mobileBatchSelectionStore = useMobileBatchSelectionStore()
const navigationStore = useNavigationStore()
const recordingStore = useRecordingStore()
const searchWorkspaceStore = useSearchWorkspaceStore()
// Use store properties directly - storeToRefs is not needed for simple reactive access
const tours = computed(() => workspaceStore?.tours ?? [])
const workspacePOIs = computed(() => workspaceStore?.pois ?? [])
const isLayersPanelVisible = computed(() => uiStore?.isLayersPanelVisible ?? false)
const isSidebarVisible = computed(() => uiStore?.isSidebarVisible ?? true)
const isMobileBottomPanelExpanded = computed(() => uiStore?.isMobileBottomPanelExpanded ?? false)
const isTrackDrawingActive = computed(() => uiStore?.isTrackDrawingActive ?? false)
const isMobileTourDetailVisible = ref(false)
// Live height of the open Route sheet, used to pad the map fit above it.
const routeSheetHeight = ref<number | null>(null)
// Live height of the mobile route-planner sheet, so the map controls can follow its snap state
// (visible at peek, hidden once it expands) — the planner has no "route sheet"/drawer to key off.
const planningSheetHeight = ref<number | null>(null)
// Canvas tab — a draggable placeholder sheet over the map (content added later).
const isCanvasSheetOpen = ref(false)

// Immersive "Full map" mode is a browsing-only view; auto-exit it the moment a task mode
// (track draw/edit, navigation) or the landing overlay takes over, so the user is never left
// in a task with all the chrome hidden.
watch(
  () =>
    isTrackDrawingActive.value ||
    uiStore.isTrackEditMode ||
    navigationStore.isActive ||
    uiStore.isLandingPageVisible,
  (taskModeActive) => {
    if (taskModeActive && uiStore.isImmersiveMapMode) uiStore.setImmersiveMapMode(false)
  },
)
const isOfflineDialogOpen = ref(false)
const navigationTrackPickerTour = ref<Tour | null>(null)
const routeWorkspaceId = computed(() =>
  typeof route.params.workspaceId === 'string' && route.params.workspaceId.length > 0
    ? route.params.workspaceId
    : null,
)
const isCollectionContextRoute = computed(
  () =>
    route.name === 'workspace-detail' ||
    route.name === 'tour-detail' ||
    route.name === 'photo-detail',
)

watch(
  [() => route.name, routeWorkspaceId, isCollectionContextRoute],
  ([routeName, workspaceId, isCollectionContext]) => {
    if (routeName === 'workspaces') {
      rememberCollectionsOverviewContext()
      return
    }
    if (!isCollectionContext) return
    rememberLastCollectionContext(workspaceId)
  },
  { immediate: true },
)

// The mobile basemap-POI "Add to" sheet (PoiBrowseSheet) — when open we hide the collection bottom
// panel so the two sheets don't overlap.
const { selectedPoiCard: poiBrowseSelected, clearSelectedPoiCard } = usePoiBrowse()
const isPoiBrowseSheetOpen = computed(() => isSmallScreen.value && !!poiBrowseSelected.value)
const isCollectionsOverviewContext = computed(
  () => routeWorkspaceId.value === null && !workspaceStore.clientId,
)
const showCreateMenuFab = computed(
  () =>
    authStore.isAuthenticated &&
    !mobileBatchSelectionStore.isActive &&
    !isAddToCollectionPickerOpen.value &&
    !isMobileRenameSheetOpen.value &&
    (route.name === 'pinned' ||
      (routeWorkspaceId.value !== null &&
        !uiStore.isInWorkspacesOverview &&
        !uiStore.isAllActivitiesView)),
)
const mobileSurfaceActionFab = computed<null | 'new-collection' | 'import'>(() => {
  if (!authStore.isAuthenticated) return null
  if (
    mobileBatchSelectionStore.isActive ||
    isAddToCollectionPickerOpen.value ||
    isMobileRenameSheetOpen.value
  ) {
    return null
  }
  if (route.name === 'workspaces') return 'new-collection'
  if (route.name === 'library' && uiStore.isAllActivitiesView && !isMobileDetailLevel.value) {
    return 'import'
  }
  return null
})
const mobileSurfaceActionFabLabel = computed(() =>
  mobileSurfaceActionFab.value === 'new-collection' ? 'New collection' : 'Import files',
)
const showHighHeaderPrimaryAction = computed(
  () => showCreateMenuFab.value || mobileSurfaceActionFab.value !== null,
)
const handleMobileSurfaceActionFab = () => {
  if (mobileSurfaceActionFab.value === 'new-collection') {
    window.dispatchEvent(new CustomEvent('header-action-create-workspace'))
    return
  }
  if (mobileSurfaceActionFab.value === 'import') {
    window.dispatchEvent(new CustomEvent('trigger-library-import'))
  }
}
const shouldTemporarilySuppressSearchWorkspaceVisibility = computed(
  () => routeWorkspaceId.value !== null || uiStore.isAllActivitiesView,
)

const mapInstance = shallowRef<maplibregl.Map | null>(null)
const mapLoaded = ref(false)

provide('mapInstance', mapInstance)
provide('mapLoaded', mapLoaded)
provide(
  'mapHeaderHeight',
  computed(
    () =>
      ((props.headerRef ?? localHeaderRef.value)?.$el as HTMLElement | undefined)?.offsetHeight ??
      48,
  ),
)
const MOBILE_PANEL_COLLAPSED_HEIGHT = 64
provide(
  'mapBottomInset',
  computed(() => {
    if (!isSmallScreen.value) return 0
    return uiStore.mobileBottomPanelHeight ?? MOBILE_PANEL_COLLAPSED_HEIGHT
  }),
)

const mapRef = ref<InstanceType<typeof MapLibreRoot> | null>(null)
const hadPersistedFollowOnMount = isTauri() && uiStatePersistenceService.isFollowLocationActive()
let mapInteractionEpoch = 0
let isUserMapInteractionActive = false
const localHeaderRef = ref<ComponentPublicInstance | null>(null)
const toolbarRef = ref<InstanceType<typeof WorkspaceToolbar> | null>(null)
const mobileToolbarRef = ref<InstanceType<typeof WorkspaceMobileToolbar> | null>(null)
const photoLayerRef = ref<InstanceType<typeof PhotoMarkerLayer> | null>(null)
const restoredSavedViewportWorkspaceId = ref<string | null>(null)
type MobileNavTab = 'collections' | 'library' | 'pinned' | 'search' | null
const selectedMobileNavTab = ref<MobileNavTab>(null)

// Keep track of last bounds for search-collections overview so we can re-apply
// zoom with proper padding once UI elements (header/toolbar) are measured.
const lastSearchWorkspacesBounds = shallowRef<maplibregl.LngLatBounds | null>(null)
let explicitlyFitOverviewBounds: maplibregl.LngLatBounds | null = null

const selectedPOIId = ref<string | null>(null)
const retainedUnpinnedTourDetail = ref<Tour | null>(null)
const retainedUnpinnedPOIDetail = ref<WorkspacePOI | null>(null)
const selectedWaypointDetail = ref<{
  tourId: string
  waypointIndex: number
  source: 'list' | 'map' | null
} | null>(null)
const selectedWorkspaceTourDetail = computed(() => {
  const selectedTour = workspaceStore.selectedTour
  if (selectedTour) return selectedTour
  if (retainedUnpinnedTourDetail.value?.clientId === workspaceStore.selectedTourId) {
    return retainedUnpinnedTourDetail.value
  }
  return null
})
const selectedPOI = computed(
  () =>
    workspaceStore.pois.find((candidate) => candidate.clientId === selectedPOIId.value) ??
    (retainedUnpinnedPOIDetail.value?.clientId === selectedPOIId.value
      ? retainedUnpinnedPOIDetail.value
      : null),
)
const isActivePinnedWorkspace = () =>
  route.name === 'pinned' ||
  (workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned')
const selectedWorkspaceWaypointIndex = computed(() => {
  const detail = selectedWaypointDetail.value
  const tour = selectedWorkspaceTourDetail.value
  if (!detail || !tour || tour.clientId !== detail.tourId) return null
  if (tour.waypoints.length === 0) return detail.waypointIndex
  return detail.waypointIndex >= 0 && detail.waypointIndex < tour.waypoints.length
    ? detail.waypointIndex
    : null
})
const selectedLibraryWaypointIndex = computed(() => {
  const detail = selectedWaypointDetail.value
  const tour = libraryStore.detailTour
  if (!detail || !tour || tour.clientId !== detail.tourId) return null
  const waypointCount = tour.waypoints?.length ?? 0
  if (waypointCount === 0) return detail.waypointIndex
  return detail.waypointIndex >= 0 && detail.waypointIndex < waypointCount
    ? detail.waypointIndex
    : null
})
const selectedWaypointOpenSource = computed(() => selectedWaypointDetail.value?.source ?? null)
// Resolved context for the mobile waypoint detail sheet (collection or library). Mirrors the
// inline-waypoint computeds in TourDetailPanel so POIDetailPanel gets identical props, but hosts it
// in its own bottom sheet instead of swapping it into the Route sheet's Details tab.
const mobileWaypointDetailTour = computed(() => {
  const detail = selectedWaypointDetail.value
  if (!detail) return null
  if (uiStore.isAllActivitiesView) {
    return libraryStore.detailTour?.clientId === detail.tourId ? libraryStore.detailTour : null
  }
  return workspaceStore.selectedTour?.clientId === detail.tourId
    ? workspaceStore.selectedTour
    : null
})
const mobileWaypointDetailIndex = computed(() =>
  uiStore.isAllActivitiesView
    ? selectedLibraryWaypointIndex.value
    : selectedWorkspaceWaypointIndex.value,
)
const mobileWaypointDetailWaypoint = computed(() => {
  const tour = mobileWaypointDetailTour.value
  const index = mobileWaypointDetailIndex.value
  if (!tour || index === null) return null
  const waypoints = tour.waypoints ?? []
  if (index < 0 || index >= waypoints.length) return null
  return waypoints[index] ?? null
})
const mobileWaypointDetailVisible = computed(() => {
  const tour = mobileWaypointDetailTour.value
  const index = mobileWaypointDetailIndex.value
  if (!tour || index === null) return true
  return !uiStore.isWaypointHidden(`${tour.clientId}_${index}`)
})
const handleMobileWaypointVisibilityToggle = () => {
  const tour = mobileWaypointDetailTour.value
  const index = mobileWaypointDetailIndex.value
  if (!tour || index === null) return
  uiStore.toggleSingleWaypointVisibility(`${tour.clientId}_${index}`)
}

// Mobile detail sheets (collection POI / tour waypoint). When one is open we hide the Route sheet,
// the collection bottom toolbar and the create FAB so the surfaces don't stack — same coordination
// pattern as `isPoiBrowseSheetOpen`.
const isMobilePoiDetailSheetOpen = computed(
  () =>
    isSmallScreen.value &&
    !uiStore.isAllActivitiesView &&
    !!selectedPOI.value &&
    uiStore.activePanel === 'poiDetail' &&
    !isTrackDrawingActive.value &&
    !uiStore.isLandingPageVisible,
)
const isMobileWaypointDetailSheetOpen = computed(
  () =>
    isSmallScreen.value &&
    !!mobileWaypointDetailWaypoint.value &&
    !isTrackDrawingActive.value &&
    !uiStore.isTrackEditMode &&
    !navigationStore.isActive &&
    // User-invoked by tapping a waypoint — works in immersive too (matches POI detail / the basemap
    // POI card, which are not suppressed in immersive either).
    !uiStore.isLandingPageVisible,
)
const isMobileDetailSheetOpen = computed(
  () => isMobilePoiDetailSheetOpen.value || isMobileWaypointDetailSheetOpen.value,
)
// Any of the non-modal map drawers (Map type / Map layers / Map content) on mobile. They're mutually
// exclusive (uiStore.activeMapDrawer) and take over the bottom — the toolbar / route sheet / FAB hide.
// Opening a detail or "Add to" sheet closes the drawer (and vice versa), so only one shows at a time.
// These are user-invoked from the always-visible control cluster, so they work in immersive mode too
// (the sheet shows over the clean map; closing returns to immersive) — not suppressed there.
const isMobileMapDrawerOpen = computed(
  () =>
    isSmallScreen.value &&
    uiStore.isAnyMapDrawerVisible &&
    !uiStore.isLandingPageVisible &&
    !isTrackDrawingActive.value &&
    !uiStore.isTrackEditMode &&
    !navigationStore.isActive,
)
const selectedWorkspaceWaypointId = computed(() =>
  selectedWorkspaceWaypointIndex.value === null || !workspaceStore.selectedTour
    ? null
    : `${workspaceStore.selectedTour.clientId}_${selectedWorkspaceWaypointIndex.value}`,
)
const selectedLibraryWaypointId = computed(() =>
  selectedLibraryWaypointIndex.value === null || !libraryStore.detailTour
    ? null
    : `${libraryStore.detailTour.clientId}_${selectedLibraryWaypointIndex.value}`,
)

const hasPendingItemRoute = () => {
  const routeTourId = route.params.tourId
  return typeof routeTourId === 'string' && routeTourId.length > 0
}
const shouldAutoMoveWorkspaceContent = () =>
  uiStore.shouldAutoMoveMap('workspace-content', { hasPendingItemRoute: hasPendingItemRoute() })
const finalizeWorkspaceContentMovement = () =>
  uiStore.finalizeAutomaticMapMovement('workspace-content', {
    hasPendingItemRoute: hasPendingItemRoute(),
  })
const shouldAutoMoveOverviewContent = () => uiStore.shouldAutoMoveMap('overview-content')
const finalizeOverviewContentMovement = () =>
  uiStore.finalizeAutomaticMapMovement('overview-content')
const shouldAutoMoveRouteItem = () => uiStore.shouldAutoMoveMap('route-item')
const finalizeRouteItemMovement = () => uiStore.finalizeAutomaticMapMovement('route-item')

const isAddWaypointModalOpen = ref(false)
const addWaypointModalScope = ref<'tour-waypoint' | 'workspace-poi'>('tour-waypoint')
const isWaypointPlacementActive = ref(false)
const waypointPlacementScope = ref<'tour-waypoint' | 'workspace-poi'>('tour-waypoint')
const pendingWaypointName = ref<string | null>(null)
const pendingWaypointType = ref<string | null>(null)
const pendingWaypointDescription = ref<string | null>(null)
const hiddenTourDetailPanelForTrackEditTourId = ref<string | null>(null)
// Store a pending flyTo request from Nominatim selection if map not ready yet
const pendingWaypointFlyTo = ref<{ lat: number; lon: number; zoom?: number } | null>(null)
const pendingMapFlyTo = ref<{ lat: number; lon: number; zoom?: number } | null>(null)

// Mobile toolbar visibility toggle during drawing/edit
const isMobileToolbarVisible = ref(true)
const DESKTOP_SIDEBAR_PANEL_MARGIN = 8
const DESKTOP_SIDEBAR_LEFT_INSET = 16
const DESKTOP_SIDEBAR_WIDTH = 480
const FLOATING_MAP_CHROME_SIZE = 40
const FLOATING_MAP_CHROME_GAP = 12
const FLOATING_MAP_CHROME_EDGE_MARGIN = 16
const OVERVIEW_FILTER_FLOAT_PILL_ESTIMATED_WIDTH = 120
const FALLBACK_COLLAPSED_RAIL_RIGHT = 248
const DESKTOP_SMART_SEARCH_MAX_WIDTH = 448
const desktopPanelTop = useSafeAreaHeaderClearance(48, 12)
const desktopPanelVisibleTop = computed(
  () => `calc(${desktopPanelTop.value} + ${DESKTOP_SIDEBAR_PANEL_MARGIN}px)`,
)
const WORKSPACE_TOUR_EDIT_TOUCH_LAYER_ID = 'workspace-tour-edit-touch-layer'
type RoutingScope = 'workspace' | 'library'
const mapChromeContainerRef = ref<HTMLElement | null>(null)
const collapsedRailShellRef = ref<HTMLElement | null>(null)
const collapsedRailMeasureRef = ref<HTMLElement | null>(null)
const desktopCreateMenuPosition = ref<{ left: number; top: string }>({ left: 0, top: '0px' })
const desktopShareButtonPosition = ref<{ left: number; top: string }>({ left: 0, top: '0px' })
const mapChromeLayoutEpoch = ref(0)

const _onTrackDrawingExit = () => {
  isMobileToolbarVisible.value = true
  activeRoutingScope.value = null
  activeRoutingTourId.value = null
  discardRoutingTourOnCancel.value = false
}

const activeRoutingScope = ref<RoutingScope | null>(null)
const activeRoutingTourId = ref<string | null>(null)
const discardRoutingTourOnCancel = ref(false)
const collectionRoutingMapContentHidden = ref(false)

watch(
  () => [uiStore.isTrackDrawingActive, uiStore.isTrackEditMode] as const,
  ([isDrawing, isEditing]) => {
    if (isDrawing || isEditing) return
    activeRoutingScope.value = null
    activeRoutingTourId.value = null
  },
  { immediate: true },
)

const hideCollectionRoutingMapContent = () => {
  collectionRoutingMapContentHidden.value = true
  uiStore.setPhotosVisible(false)
  uiStore.setWaypointsVisible(false)
  uiStore.setPOIsVisible(false)
}

const restoreCollectionRoutingMapContent = () => {
  if (!collectionRoutingMapContentHidden.value) return
  collectionRoutingMapContentHidden.value = false
  uiStore.setPhotosVisible(true)
  uiStore.setWaypointsVisible(true)
  uiStore.setPOIsVisible(true)
}

const getDefaultRoutingScope = (): RoutingScope | null => {
  if (uiStore.isAllActivitiesView) return 'library'
  if (workspaceStore.clientId) return 'workspace'
  return null
}

const resolveRoutingScopeForTour = (
  tourId: string | null | undefined,
  preferredScope?: RoutingScope | null,
): RoutingScope | null => {
  if (preferredScope) return preferredScope
  if (activeRoutingScope.value) return activeRoutingScope.value
  if (uiStore.isAllActivitiesView) return 'library'
  if (tourId && workspaceStore.getTourById(tourId)) return 'workspace'
  if (tourId && libraryStore.detailTour?.clientId === tourId) return 'library'
  return getDefaultRoutingScope()
}

const getActiveRoutingScope = (): RoutingScope | null =>
  activeRoutingScope.value ?? getDefaultRoutingScope()

const _workspaceRoutingAdapter = createWorkspaceTrackRoutingAdapter({
  workspaceStore,
  resolveTargetTourId: () => resolveTargetTourId(),
  onExit: _onTrackDrawingExit,
})

const _libraryRoutingAdapter = createLibraryTrackRoutingAdapter({
  libraryStore,
  resolveTargetTourId: () => libraryStore.detailTour?.clientId ?? null,
  onExit: _onTrackDrawingExit,
})

// Proxy that delegates to the correct adapter at call-time based on the active
// route-routing scope. This keeps one host instance while avoiding implicit
// "no workspace means library" behavior on overview screens.
const _isLibraryMode = () => getActiveRoutingScope() === 'library'

const trackRoutingAdapter: TrackRoutingAdapter = {
  resolveTargetTourId: () => resolveTargetTourId(),
  getTour: (tourId) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.getTour(tourId)
      : _workspaceRoutingAdapter.getTour(tourId),
  runInSyncBatch: (op) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.runInSyncBatch!(op)
      : _workspaceRoutingAdapter.runInSyncBatch!(op),
  addTrack: (tourId, data) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.addTrack(tourId, data)
      : _workspaceRoutingAdapter.addTrack(tourId, data),
  deleteTrack: (tourId, trackClientId) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.deleteTrack(tourId, trackClientId)
      : _workspaceRoutingAdapter.deleteTrack(tourId, trackClientId),
  addRoutePoint: (tourId, data) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.addRoutePoint(tourId, data)
      : _workspaceRoutingAdapter.addRoutePoint(tourId, data),
  insertRoutePointAt: (tourId, insertIndex, data) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.insertRoutePointAt(tourId, insertIndex, data)
      : _workspaceRoutingAdapter.insertRoutePointAt(tourId, insertIndex, data),
  insertRoutePointsAt: (tourId, insertIndex, data) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.insertRoutePointsAt!(tourId, insertIndex, data)
      : _workspaceRoutingAdapter.insertRoutePointsAt!(tourId, insertIndex, data),
  deleteRoutePoint: (tourId, routePointIndex) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.deleteRoutePoint(tourId, routePointIndex)
      : _workspaceRoutingAdapter.deleteRoutePoint(tourId, routePointIndex),
  updateRoutePointPosition: (tourId, routePointIndex, newLat, newLon) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.updateRoutePointPosition(tourId, routePointIndex, newLat, newLon)
      : _workspaceRoutingAdapter.updateRoutePointPosition(tourId, routePointIndex, newLat, newLon),
  reorderTrackRoutePoints: (tourId, trackClientId, orderedRoutePointClientIds) =>
    _isLibraryMode()
      ? (
          _libraryRoutingAdapter.reorderTrackRoutePoints ??
          _workspaceRoutingAdapter.reorderTrackRoutePoints!
        )(tourId, trackClientId, orderedRoutePointClientIds)
      : _workspaceRoutingAdapter.reorderTrackRoutePoints!(
          tourId,
          trackClientId,
          orderedRoutePointClientIds,
        ),
  replaceSegments: (tourId, trackClientId, deletedIndices, newSegments, optionsArg) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.replaceSegments(
          tourId,
          trackClientId,
          deletedIndices,
          newSegments,
          optionsArg,
        )
      : _workspaceRoutingAdapter.replaceSegments(
          tourId,
          trackClientId,
          deletedIndices,
          newSegments,
          optionsArg,
        ),
  replaceAllSegments: (tourId, trackClientId, oldSegmentClientIds, newSegments) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.replaceAllSegments(
          tourId,
          trackClientId,
          oldSegmentClientIds,
          newSegments,
        )
      : _workspaceRoutingAdapter.replaceAllSegments(
          tourId,
          trackClientId,
          oldSegmentClientIds,
          newSegments,
        ),
  commitTrackRouteDraft: (input) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.commitTrackRouteDraft!(input)
      : _workspaceRoutingAdapter.commitTrackRouteDraft!(input),
  onLineStringUpdated: (tourId, trackClientId, lineString) =>
    _isLibraryMode()
      ? _libraryRoutingAdapter.onLineStringUpdated?.(tourId, trackClientId, lineString)
      : _workspaceRoutingAdapter.onLineStringUpdated?.(tourId, trackClientId, lineString),
  onDrawingDiscard: () => {
    if (!discardRoutingTourOnCancel.value) return
    const tourId = activeRoutingTourId.value
    const scope = activeRoutingScope.value
    discardRoutingTourOnCancel.value = false
    if (!tourId || !scope) return

    const discard =
      scope === 'library' ? libraryStore.deleteTour(tourId) : workspaceStore.clearTour(tourId)
    void discard.catch((error) => {
      logService.error(LOG_CATEGORIES.MAP, 'Failed to remove discarded route placeholder', {
        tourId,
        scope,
        error,
      })
      showError('Failed to discard the new route')
    })
  },
  onExit: _onTrackDrawingExit,
}

const trackRoutingHost = useTrackRoutingHost({
  adapter: trackRoutingAdapter,
  mapInstance,
  getTouchTargetLayerId: () => WORKSPACE_TOUR_EDIT_TOUCH_LAYER_ID,
  onIdleMapClick: () => {
    if (!isMobileToolbarVisible.value) {
      isMobileToolbarVisible.value = true
      return true
    }
    return false
  },
})

const {
  trackDrawing,
  drawingTrackPoints,
  drawingTrackSegments,
  drawingTrackClientId,
  drawingPendingRoutePoints,
  canUndoTrackDrawing,
  canRedoTrackDrawing,
  canSaveTrackDrawing,
  isCalculatingRoute,
  canUndoTrackEdit,
  canRedoTrackEdit,
  canSaveTrackEdit,
  isSparseTrackSetupOpen,
  isApplyingSparseTrackSetup,
  sparseTrackSetup,
  sparseTrackPreviewRoutePoints,
  closeTrackDrawing,
  closeTrackEdit: closeTrackEditSession,
  saveTrackDrawing,
  saveTrackEdit: saveTrackEditSession,
  undoLastSegment,
  redoLastSegment,
  addDrawingRoutePoint,
  undoEditModeSegment,
  redoTrackEdit,
  markRoutePointChanged,
  maybeOpenSparseTrackSetup,
  updateSparseTrackPointCount,
  confirmSparseTrackSetup,
  handleMapClick: handleRoutingMapClick,
  handleRoutePointDragend,
  handleTrackEditInsertDrop,
  handlePendingDeleted,
  handlePendingDragend,
  initEditModeLastClickedPoint,
  resetEditState,
  activeDrawingOverlayColor: _activeDrawingOverlayColor,
  activeDraftTour,
  captureEditReferenceTrack,
  editReferenceCoordinates,
  editReferenceTrackSegments,
} = trackRoutingHost

let ignoreNextRouteSelectionMapClick = false

const handleWorkspaceMapClick = async (event: Parameters<typeof handleRoutingMapClick>[0]) => {
  if (isOfflineMapAreaSelector.value) return

  if (ignoreNextRouteSelectionMapClick) {
    ignoreNextRouteSelectionMapClick = false
    return
  }

  if (
    workspaceStore.selectedTourId &&
    !uiStore.isAllActivitiesView &&
    !uiStore.isTrackDrawingActive &&
    !uiStore.isTrackEditMode
  ) {
    if (isSelectedRouteSegmentActive.value) {
      clearSelectedRouteSegmentSelection()
    }
    workspaceStore.setSelectedTourId(null)
    workspaceStore.setHoveredTourId(null)
    uiStore.hideTourDetailPanel()
    clearSelectedTourProfileMetricOverlayState()
    return
  }

  if (isSelectedRouteSegmentActive.value) {
    handleCloseSelectedRouteProfile()
    return
  }

  await handleRoutingMapClick(event)
}

// In library mode, newly drawn tracks are always rendered orange (selection colour)
// so they stand out against other tracks during drawing.
const activeDrawingOverlayColor = computed(() => {
  if (_isLibraryMode() && uiStore.isTrackDrawingActive) return '#ea580c'
  return _activeDrawingOverlayColor.value
})

const canReverseTrackDrawing = computed(
  () => trackRoutingHost.trackDrawing.canReverseTrackDrawing.value,
)
const canReverseTrackEdit = computed(() => trackRoutingHost.trackEdit.canReverseTrackEdit.value)
const reverseTrackDrawingRoute = () => trackRoutingHost.trackDrawing.reverseTrackDrawingRoute()
const reverseTrackEditRoute = () => trackRoutingHost.trackEdit.reverseTrackEditRoute()
// Drag-reorder from the route-point list: dispatch to the active planning context (the session's
// run-reorder auto-detects, but dispatch explicitly for clarity).
const handleReorderRoutePoints = (
  _tourId: string,
  _trackClientId: string | null,
  orderedRoutePointClientIds: string[],
) => {
  if (uiStore.isTrackDrawingActive) {
    return trackRoutingHost.trackDrawing.reorderTrackDrawingRoute(orderedRoutePointClientIds)
  }
  return trackRoutingHost.trackEdit.reorderTrackEditRoute(orderedRoutePointClientIds)
}

// --- Mobile planning sheet (replaces the floating control panels on small screens) ---------------
// One bottom sheet drives both drawing and edit; these computeds/handlers fan out to the active mode.
const isMobilePlanningSheetActive = computed(
  () => isSmallScreen.value && (uiStore.isTrackDrawingActive || isTrackEditToolbarVisible.value),
)
const planningSheetMode = computed<'drawing' | 'edit'>(() =>
  uiStore.isTrackDrawingActive ? 'drawing' : 'edit',
)
const planningSheetTrackClientId = computed(() =>
  uiStore.isTrackDrawingActive ? drawingTrackClientId.value : uiStore.editingTrackClientId,
)
const planningCanReverse = computed(() =>
  uiStore.isTrackDrawingActive ? canReverseTrackDrawing.value : canReverseTrackEdit.value,
)
const planningCanUndo = computed(() =>
  uiStore.isTrackDrawingActive ? canUndoTrackDrawing.value : canUndoTrackEdit.value,
)
const planningCanRedo = computed(() =>
  uiStore.isTrackDrawingActive ? canRedoTrackDrawing.value : canRedoTrackEdit.value,
)
const planningCanSave = computed(() =>
  uiStore.isTrackDrawingActive ? canSaveTrackDrawing.value : canSaveTrackEdit.value,
)
const planningIsBusy = computed(() =>
  uiStore.isTrackDrawingActive ? false : isTrackEditToolbarBusy.value,
)
const planningStatusText = computed(() =>
  uiStore.isTrackDrawingActive ? null : trackEditToolbarStatusText.value,
)
const planningReverse = () =>
  uiStore.isTrackDrawingActive ? reverseTrackDrawingRoute() : reverseTrackEditRoute()
const planningUndo = () =>
  uiStore.isTrackDrawingActive ? undoLastSegment() : undoEditModeSegment()
const planningRedo = () => (uiStore.isTrackDrawingActive ? redoLastSegment() : redoTrackEdit())
const planningSave = () =>
  uiStore.isTrackDrawingActive ? handleSaveTrackDrawing() : saveTrackEdit()
// Floating top-left close (X): the routing session already confirms discarding unsaved work
// (confirmDiscardDraft), so no extra dialog here — same single warning as the desktop Cancel button.
const planningCancel = () =>
  uiStore.isTrackDrawingActive ? handleCloseTrackDrawing() : closeTrackEdit()
const renderedTours = computed(() => {
  const draftTour = activeDraftTour.value
  if (!draftTour) return tours.value

  let replaced = false
  const nextTours = tours.value.map((tour) => {
    if (tour.clientId !== draftTour.clientId) return tour
    replaced = true
    return draftTour
  })

  return replaced ? nextTours : [...nextTours, draftTour]
})
const drawingTrack = computed(() => {
  if (!uiStore.isTrackDrawingActive || !drawingTrackClientId.value) {
    return null
  }

  const draftTour = activeDraftTour.value
  const draftTrack = draftTour?.tracks.find(
    (candidate) => candidate.clientId === drawingTrackClientId.value,
  )
  if (draftTrack) {
    return draftTrack
  }

  const tour = getKnownRoutingTour(resolveTargetTourId(), getActiveRoutingScope())
  return tour?.tracks.find((candidate) => candidate.clientId === drawingTrackClientId.value) ?? null
})
const drawingTrackSegmentsForProfile = computed(() => {
  if (drawingTrackSegments.value.length > 0) {
    return drawingTrackSegments.value
  }

  return drawingTrack.value?.segments ?? []
})
const preparingTrackEdit = ref<{ tourId: string; trackClientId: string } | null>(null)
const isSavingTrackEdit = ref(false)
const isTrackEditToolbarVisible = computed(
  () =>
    !isSavingTrackEdit.value &&
    !isSparseTrackSetupOpen.value &&
    (uiStore.isTrackEditMode || preparingTrackEdit.value !== null),
)
const isTrackEditToolbarBusy = computed(
  () => preparingTrackEdit.value !== null || isApplyingSparseTrackSetup.value,
)
const trackEditToolbarStatusText = computed(() => {
  if (preparingTrackEdit.value) return 'Loading track routing…'
  if (isApplyingSparseTrackSetup.value) return 'Adding helper points…'
  if (isCalculatingRoute.value) return 'Routing…'
  return null
})
const hasMobileTourDetailToggle = computed(
  () =>
    isSmallScreen.value &&
    !!workspaceStore._selectedTourId &&
    !isTrackDrawingActive.value &&
    !uiStore.isTrackEditMode,
)

// True while the unified mobile Route sheet covers the bottom of the screen. The collection
// list drawer and the create (+) FAB must hide so they don't stack behind / over the sheet.
const isMobileRouteSheetOpen = computed(
  () =>
    isSmallScreen.value &&
    uiStore.activePanel === 'tourDetail' &&
    isMobileTourDetailVisible.value &&
    !isTrackDrawingActive.value &&
    !uiStore.isTrackEditMode &&
    !navigationStore.isActive,
)

// Mobile "detail level": a collection is opened, or a track/route detail is open (collection OR
// Library). Drives the Instagram-style chrome: the tab bar hides, the floating top-left back button
// shows, and the bottom sheets reclaim the tab bar's height. Pure logic lives in `isMobileDetailLevel`.
const isMobileDetailLevel = computed(() =>
  resolveMobileDetailLevel({
    isSmallScreen: isSmallScreen.value,
    isImmersive: uiStore.isImmersiveMapMode,
    isLanding: uiStore.isLandingPageVisible,
    isTrackDrawing: isTrackDrawingActive.value,
    isTrackEdit: uiStore.isTrackEditMode,
    isNavigating: navigationStore.isActive,
    isRouteSheetOpen: isMobileRouteSheetOpen.value,
    hasWorkspaceId: routeWorkspaceId.value !== null,
    isInOverview: uiStore.isInWorkspacesOverview,
  }),
)
const isMobileCollectionChromeVisible = computed(() =>
  resolveMobileCollectionChromeVisible({
    isSmallScreen: isSmallScreen.value,
    isImmersive: uiStore.isImmersiveMapMode,
    isLanding: uiStore.isLandingPageVisible,
    isTrackDrawing: isTrackDrawingActive.value,
    isTrackEdit: uiStore.isTrackEditMode,
    isNavigating: navigationStore.isActive,
    isRouteSheetOpen: isMobileRouteSheetOpen.value,
    hasWorkspaceId: routeWorkspaceId.value !== null,
    isInOverview: uiStore.isInWorkspacesOverview,
  }),
)

// When the tab bar is hidden (detail levels) the floating sheets drop by its 64px to sit just above
// the home indicator, giving the map + sheet more room. Provided so every mobile sheet stays in
// lock-step (RouteDetailSheet, the collection drawer) instead of duplicating the offset string.
const mobileFloatingSheetBottomOffset = computed(() =>
  isOfflineMapAreaSelector.value ||
  isMobileDetailLevel.value ||
  (uiStore.isImmersiveMapMode && isMobileRouteSheetOpen.value)
    ? floatingSheetBottomOffset(0)
    : floatingSheetBottomOffset(),
)
provide('floatingSheetBottomOffset', mobileFloatingSheetBottomOffset)

const isMobileBottomPanelChromeVisible = computed(
  () =>
    isSmallScreen.value &&
    !isTrackDrawingActive.value &&
    !uiStore.isTrackEditMode &&
    isMobileToolbarVisible.value &&
    !isSelectedRouteSegmentActive.value &&
    !isMobileRouteSheetOpen.value &&
    !isCanvasSheetOpen.value &&
    !uiStore.isSmartSearchOpen &&
    !navigationStore.isActive &&
    !uiStore.isLandingPageVisible &&
    !uiStore.isMobileMapView &&
    !uiStore.isImmersiveMapMode &&
    !isPoiBrowseSheetOpen.value &&
    !isMobileDetailSheetOpen.value &&
    !isMobileMapDrawerOpen.value,
)

const isMobileTabBarChromeVisible = computed(
  () =>
    isSmallScreen.value &&
    !isTrackDrawingActive.value &&
    !uiStore.isTrackEditMode &&
    isMobileToolbarVisible.value &&
    !navigationStore.isActive &&
    !uiStore.isLandingPageVisible &&
    !uiStore.isImmersiveMapMode &&
    !isMobileDetailLevel.value &&
    !mobileBatchSelectionStore.isActive,
)

const mobilePanelSideGuardStyle = computed(() => ({
  bottom: mobileFloatingSheetBottomOffset.value,
  height: `max(0px, calc(${uiStore.mobileBottomPanelHeight ?? MOBILE_PANEL_COLLAPSED_HEIGHT}px - ${mobileFloatingSheetBottomOffset.value}))`,
  zIndex: Z_INDEX.MOBILE_TOOLBAR - 2,
}))

const mobileTabBarBottomGuardStyle = computed(() => ({
  height: 'max(env(safe-area-inset-bottom, 0px), 10px)',
  zIndex: Z_INDEX.MOBILE_TOOLBAR - 2,
}))

// The round map-control cluster is bottom-anchored at a fixed, stable offset on the right edge
// (owned by MapLibreRoot's CSS) — it no longer tracks any sheet's live height, so a sheet can never
// drag it up into the top header. Here we only decide *visibility*: fade it out once a sheet expands
// past its peek (the sheet becomes the focus and would otherwise sit over the cluster). Route sheet
// peek ≤ ~180px, mid ≈ elevation-profile height — 280px cleanly separates peek from mid/full.
const ROUTE_SHEET_EXPANDED_PX = 280
const mapControlsHidden = computed(() => {
  // In immersive mode all sheets are hidden and the cluster's button is the only way out — keep it.
  if (!isSmallScreen.value || uiStore.isImmersiveMapMode) return false
  // While planning, the planner sheet is the only bottom surface: keep the controls visible at peek
  // (so the user can still locate / change layers / hide elements) and fade them once it expands.
  if (isMobilePlanningSheetActive.value) {
    const planningH = planningSheetHeight.value
    return planningH != null && planningH > ROUTE_SHEET_EXPANDED_PX
  }
  const routeOpen = isMobileRouteSheetOpen.value
  const routeH = routeSheetHeight.value
  const routeExpanded = routeOpen && routeH != null && routeH > ROUTE_SHEET_EXPANDED_PX
  const drawerExpanded = isMobileBottomPanelExpanded.value && !routeOpen
  return routeExpanded || drawerExpanded
})

const mapControlsInteractionDisabled = computed(() => {
  if (!isSmallScreen.value || uiStore.isImmersiveMapMode) return false
  if (isMobileRouteSheetOpen.value || isMobilePlanningSheetActive.value) {
    return mapControlsHidden.value
  }
  return isMobileBottomPanelExpanded.value
})

// Mobile scale-bar bottom offset: float it just above the COLLAPSED bottom surface (the route
// sheet's peek or the collection drawer's peek), clamped so it never chases an expanding sheet up
// into mid-air. When a sheet expands it simply sits behind it (fine — the sheet is the focus then).
// Desktop uses the CSS default (bottom-right corner). `mobileBottomPanelHeight` is the measured
// occupied-from-bottom of the drawer; the route sheet uses a fixed peek base (not its live height).
const MOBILE_SCALE_MAX_BOTTOM_PX = 180
const ROUTE_SHEET_SCALE_BASE_PX = 150
const mapScaleBottomOffset = computed(() => {
  if (!isSmallScreen.value) return null
  const occupied = Math.max(
    uiStore.mobileBottomPanelHeight ?? 64,
    isMobileRouteSheetOpen.value ? ROUTE_SHEET_SCALE_BASE_PX : 0,
  )
  return `${Math.min(MOBILE_SCALE_MAX_BOTTOM_PX, Math.round(occupied + 14))}px`
})

watchEffect(() => {
  if (typeof document === 'undefined') return
  const root = document.documentElement.style
  const hidden = mapControlsHidden.value
  root.setProperty('--mmc-opacity', hidden ? '0' : '1')
  root.setProperty('--mmc-pe', mapControlsInteractionDisabled.value ? 'none' : 'auto')
  if (mapScaleBottomOffset.value) root.setProperty('--map-scale-bottom', mapScaleBottomOffset.value)
  else root.removeProperty('--map-scale-bottom')
})

onUnmounted(() => {
  const root = document.documentElement.style
  root.removeProperty('--mmc-opacity')
  root.removeProperty('--mmc-pe')
  root.removeProperty('--map-scale-bottom')
})

const showTourDetailPanel = (
  options: { revealOnMobile?: boolean; initialMode?: RouteSheetMode } = {},
) => {
  uiStore.showTourDetailPanel()
  if (!isSmallScreen.value) return
  nextRouteSheetInitialMode.value = options.initialMode ?? lastRouteSheetMode.value
  // The unified Route sheet is the default surface on mobile — reveal it unless a caller
  // explicitly opts out.
  isMobileTourDetailVisible.value = options.revealOnMobile !== false
}

const showPOIDetailPanel = () => {
  uiStore.showPOIDetailPanel()
}

const closeTourDetailPanel = () => {
  // Back from the Route sheet returns to the collection list and clears the selection on every
  // platform: the list row de-highlights and the map shows the full collection again (reopen by
  // tapping the tour in the list). Previously mobile kept the tour selected for an old action-bar
  // reopen affordance that the unified Route sheet replaced.
  selectedWaypointDetail.value = null
  retainedUnpinnedTourDetail.value = null
  workspaceStore.setSelectedTourId(null)
  uiStore.hideTourDetailPanel()
}

// The floating top-left back button (mobile detail levels) goes back exactly one level.
const goBackOneLevel = () => {
  if (isMobileRouteSheetOpen.value) {
    // Library track-open also sets the toolbar's `currentView`; route it through the toolbar so that
    // state resets too. Collection track-open uses the well-trodden close path.
    if (uiStore.isAllActivitiesView) void mobileToolbarRef.value?.handleBackToWorkspaces()
    else closeTourDetailPanel()
    return
  }
  // Collection (workspace-detail) level → return to the overview.
  void mobileToolbarRef.value?.handleBackToWorkspaces()
}

const mobileBackAriaLabel = computed(() =>
  isMobileRouteSheetOpen.value
    ? uiStore.isAllActivitiesView
      ? 'Back to library'
      : 'Back to collection'
    : 'Back to collections',
)

const loadedRouteCollectionTitle = computed(() => {
  const workspaceId = routeWorkspaceId.value
  if (!workspaceId || workspaceStore.clientId !== workspaceId) return ''
  if (workspaceStore._isLoading === true) return ''
  return workspaceStore.name?.trim() || ''
})

const getActiveMapContextTitleTour = () => {
  if (mapTrackContextMenu.value.visible && mapTrackContextMenu.value.tourId) {
    return workspaceStore.getTourById(mapTrackContextMenu.value.tourId)
  }
  if (libraryMapContextMenu.value.visible && libraryMapContextMenu.value.tourId) {
    return (
      libraryStore.tours.find((tour) => tour.clientId === libraryMapContextMenu.value.tourId) ??
      null
    )
  }
  return null
}

const getActiveMapContextTitleWorkspace = () => {
  if (!mapWorkspaceContextMenu.value.visible || !mapWorkspaceContextMenu.value.workspaceId) {
    return null
  }
  return (
    searchWorkspaceStore.selectedWorkspaces.find(
      (workspace) => workspace.clientId === mapWorkspaceContextMenu.value.workspaceId,
    ) ?? null
  )
}

// Top-centre nav-bar title on mobile detail levels (takes the wordmark's spot). It reflects the
// CURRENT screen (iOS HIG): the track name when a track is open (the parent collection is implied by
// the back button), otherwise the collection name. Empty in the Library list (no collection) — the
// wordmark stays there instead.
const mobileTopTitle = computed(() => {
  // While planning, the title reads "New route" (drawing) or the edited route's name (edit), with the
  // collection name underneath (see mobileTopParent) — the same breadcrumb pattern as an open track.
  if (isMobilePlanningSheetActive.value) {
    if (uiStore.isTrackDrawingActive) return 'New route'
    return activeDraftTour.value?.name?.trim() || 'Edit route'
  }
  const contextTour = getActiveMapContextTitleTour()
  if (contextTour) return (getEffectiveTourName(contextTour) || '').trim()
  const contextWorkspace = getActiveMapContextTitleWorkspace()
  if (contextWorkspace) return contextWorkspace.name?.trim() || ''
  if (!isMobileCollectionChromeVisible.value) return ''
  if (isMobileRouteSheetOpen.value) {
    const tour = uiStore.isAllActivitiesView
      ? libraryStore.detailTour
      : selectedWorkspaceTourDetail.value
    return tour ? (getEffectiveTourName(tour) || '').trim() : ''
  }
  if (uiStore.isAllActivitiesView) return ''
  return loadedRouteCollectionTitle.value
})

// When a track is open inside a collection, the title becomes a breadcrumb: the collection name sits
// (muted) before the track name so the collection context stays visible. Empty in the library's
// all-activities view (no parent collection) and at the collection level itself.
const mobileTopParent = computed(() => {
  // Planning: the collection name sits muted under "New route" / the edited route's name.
  if (isMobilePlanningSheetActive.value) {
    return uiStore.isAllActivitiesView ? '' : loadedRouteCollectionTitle.value
  }
  if (mapTrackContextMenu.value.visible && getActiveMapContextTitleTour()) {
    return uiStore.isAllActivitiesView ? '' : workspaceStore.name?.trim() || ''
  }
  if (!isMobileCollectionChromeVisible.value || !isMobileRouteSheetOpen.value) return ''
  if (uiStore.isAllActivitiesView) return ''
  return loadedRouteCollectionTitle.value
})

const mobileTopTitleByline = computed(() => {
  if (isMobilePlanningSheetActive.value || uiStore.isAllActivitiesView) return null

  const contextTour = getActiveMapContextTitleTour()
  if (contextTour) return 'canEdit' in contextTour ? getCreatorByline(contextTour) : null
  if (!isMobileCollectionChromeVisible.value || !isMobileRouteSheetOpen.value) return null

  return selectedWorkspaceTourDetail.value
    ? getCreatorByline(selectedWorkspaceTourDetail.value)
    : null
})

const mobileTopByline = computed(() => {
  if (
    isMobilePlanningSheetActive.value ||
    isMobileRouteSheetOpen.value ||
    uiStore.isAllActivitiesView ||
    route.name === 'pinned' ||
    routeWorkspaceId.value === null
  ) {
    return null
  }
  return getWorkspaceAuthorByline({
    isOwner: workspaceStore.isOwner,
    ownerNames: workspaceStore.ownerNames,
    currentUserName: authStore.user?.name,
  })
})

// The open track's accent colour, shown as a dot before its name in the floating title — so the
// colour identity lives in the header and the sheet no longer repeats the name + dot.
const mobileTopDotColor = computed(() => {
  // Planning: the active route's accent dot (the live overlay colour), matching the open-track title.
  if (isMobilePlanningSheetActive.value) {
    if (_activeDrawingOverlayColor.value) return _activeDrawingOverlayColor.value
    return activeDraftTour.value ? resolveTourAccentColor(activeDraftTour.value) : null
  }
  const contextTour = getActiveMapContextTitleTour()
  if (contextTour) return resolveTourAccentColor(contextTour)
  const contextWorkspace = getActiveMapContextTitleWorkspace()
  if (contextWorkspace) return contextWorkspace.color || null
  if (!isMobileCollectionChromeVisible.value || !isMobileRouteSheetOpen.value) return null
  const tour = uiStore.isAllActivitiesView
    ? libraryStore.detailTour
    : selectedWorkspaceTourDetail.value
  return tour ? resolveTourAccentColor(tour) : null
})

// Create FAB sits above the tab bar normally; once the tab bar is hidden at a detail level it drops
// to just above the home indicator (reclaiming the 64px) so it doesn't float with a gap beneath it.
const mobileCreateFabBottom = computed(() => {
  if (uiStore.isMobileMapView) {
    const lift = isMobileDetailLevel.value ? 12 : 76
    return `calc(max(env(safe-area-inset-bottom, 0px), 10px) + ${lift}px)`
  }
  const panelHeight =
    uiStore.isMobileBottomPanelExpanded || uiStore.mobileBottomPanelHighProgress > 0
      ? uiStore.mobileBottomPanelExpandedHeight
      : (uiStore.mobileBottomPanelHeight ?? 64)
  return `${panelHeight + 24}px`
})
const mobileHighHandoffStyle = computed(() => ({
  opacity: 1 - uiStore.mobileBottomPanelHighProgress,
  pointerEvents:
    uiStore.mobileBottomPanelHighProgress < 0.65 ? ('auto' as const) : ('none' as const),
}))
const isNormalMobileCollectionDetail = computed(
  () =>
    isMobileCollectionChromeVisible.value &&
    !isMobileRouteSheetOpen.value &&
    route.name !== 'pinned' &&
    routeWorkspaceId.value !== null,
)
const mobileBackHandoffStyle = computed(() => {
  if (!isNormalMobileCollectionDetail.value) return mobileHighHandoffStyle.value
  const progress = uiStore.mobileBottomPanelExpandedProgress
  return {
    opacity: 1 - progress,
    pointerEvents: progress < 0.65 ? ('auto' as const) : ('none' as const),
  }
})
const mobileFabZIndex = Z_INDEX.MOBILE_TOOLBAR - 1

const openPOIDetailPanel = (poiId: string, _source: 'list' | 'map' = 'map') => {
  if (selectedPOIId.value === poiId && uiStore.activePanel === 'poiDetail') {
    closePOIDetailPanel()
    return
  }

  selectedWaypointDetail.value = null
  if (workspaceStore.selectedTourId) {
    workspaceStore.setSelectedTourId(null)
  }
  retainedUnpinnedPOIDetail.value = null
  selectedPOIId.value = poiId
  uiStore.setHoveredWaypoint(`poi_${poiId}`)
  showPOIDetailPanel()
}

const closePOIDetailPanel = () => {
  if (selectedPOIId.value && uiStore.hoveredWaypointId === `poi_${selectedPOIId.value}`) {
    uiStore.setHoveredWaypoint(null)
  }
  selectedPOIId.value = null
  retainedUnpinnedPOIDetail.value = null
  uiStore.hidePOIDetailPanel()
}

const retainUnpinnedTourDetail = (tour: Tour) => {
  if (!isActivePinnedWorkspace()) return
  retainedUnpinnedTourDetail.value = tour
}

const retainUnpinnedPOIDetail = (poi: WorkspacePOI) => {
  if (!isActivePinnedWorkspace()) return
  retainedUnpinnedPOIDetail.value = poi
}

const replacePOIDetailSelection = (poiId: string) => {
  retainedUnpinnedPOIDetail.value = null
  selectedPOIId.value = poiId
  uiStore.setHoveredWaypoint(`poi_${poiId}`)
}

const closeSelectedTourWaypointDetail = () => {
  selectedWaypointDetail.value = null
}

const openSelectedTourWaypointDetail = async (
  tourId: string,
  waypointIndex: number,
  source: 'list' | 'map' | null = null,
) => {
  if (selectedPOIId.value) {
    closePOIDetailPanel()
  }

  if (
    selectedWaypointDetail.value?.tourId === tourId &&
    selectedWaypointDetail.value?.waypointIndex === waypointIndex &&
    uiStore.activePanel === 'tourDetail'
  ) {
    closeSelectedTourWaypointDetail()
    return
  }

  if (uiStore.isAllActivitiesView) {
    if (libraryStore.detailTour?.clientId !== tourId) {
      const listTour = libraryStore.tours.find((tour) => tour.clientId === tourId)
      if (listTour) {
        libraryStore.selectTourForDetail(listTour, { focusMap: false })
      }
      await libraryStore.loadFullTourDetail(tourId)
    }

    selectedWaypointDetail.value = { tourId, waypointIndex, source }
    showTourDetailPanel({ revealOnMobile: true })
    return
  }

  if (workspaceStore.selectedTourId !== tourId) {
    workspaceStore.selectTour(tourId)
  }

  selectedWaypointDetail.value = { tourId, waypointIndex, source }
  showTourDetailPanel({ revealOnMobile: true })
}

// Opening a map drawer (type/layers/content) closes any open detail / "Add to" sheet, so only one
// bottom sheet shows at a time. The reverse — opening a detail sheet closes the drawer — lives in
// uiStore.showTourDetailPanel / showPOIDetailPanel and PoiBrowseLayer.
watch(
  () => uiStore.activeMapDrawer,
  (drawer) => {
    if (!drawer) return
    if (selectedPOIId.value) closePOIDetailPanel()
    if (selectedWaypointDetail.value) closeSelectedTourWaypointDetail()
    if (poiBrowseSelected.value) clearSelectedPoiCard()
  },
)

watch(
  () =>
    [
      isSmallScreen.value,
      uiStore.isAllActivitiesView
        ? (libraryStore.detailTour?.clientId ?? null)
        : (selectedWorkspaceTourDetail.value?.clientId ?? null),
      uiStore.activePanel,
    ] as const,
  ([smallScreen, selectedTourId, activePanel]) => {
    // The unified Route sheet is the default surface on mobile: it is visible whenever a tour
    // detail is active (dismiss by collapsing to peek, or by closing the detail panel).
    isMobileTourDetailVisible.value =
      smallScreen && !!selectedTourId && activePanel === 'tourDetail'
    if (!isMobileTourDetailVisible.value) routeSheetHeight.value = null
  },
  { immediate: true },
)

watch(
  () =>
    [
      uiStore.activePanel,
      uiStore.isAllActivitiesView
        ? (libraryStore.detailTour?.clientId ?? null)
        : (selectedWorkspaceTourDetail.value?.clientId ?? null),
      uiStore.isAllActivitiesView
        ? (libraryStore.detailTour?.waypoints?.length ?? 0)
        : (selectedWorkspaceTourDetail.value?.waypoints.length ?? 0),
    ] as const,
  ([activePanel, currentTourId, waypointCount]) => {
    const detail = selectedWaypointDetail.value
    if (!detail) return

    if (
      activePanel !== 'tourDetail' ||
      !currentTourId ||
      detail.tourId !== currentTourId ||
      detail.waypointIndex < 0 ||
      (waypointCount > 0 && detail.waypointIndex >= waypointCount)
    ) {
      selectedWaypointDetail.value = null
    }
  },
  { immediate: true },
)

const maybeRestoreTourDetailPanelAfterTrackEdit = () => {
  const tourId = hiddenTourDetailPanelForTrackEditTourId.value
  hiddenTourDetailPanelForTrackEditTourId.value = null
  if (!tourId || !isSmallScreen.value || uiStore.activePanel !== null) return
  const activeDetailTourId = uiStore.isAllActivitiesView
    ? (libraryStore.detailTour?.clientId ?? null)
    : (workspaceStore.selectedTour?.clientId ?? null)
  if (activeDetailTourId !== tourId) return
  showTourDetailPanel({ revealOnMobile: true })
}

const refreshTrackResolutionAfterRoutingExit = async () => {
  await nextTick()
  invalidateTrackResolutionCaches()
}

const closeTrackEdit = async () => {
  await closeTrackEditSession()
  if (uiStore.isTrackEditMode) return
  if (!_isLibraryMode()) {
    restoreCollectionRoutingMapContent()
  }
  maybeRestoreTourDetailPanelAfterTrackEdit()
  await refreshTrackResolutionAfterRoutingExit()
}

const saveTrackEdit = async () => {
  isSavingTrackEdit.value = true
  try {
    await saveTrackEditSession()
    if (uiStore.isTrackEditMode) return
    if (!_isLibraryMode()) {
      restoreCollectionRoutingMapContent()
    }
    maybeRestoreTourDetailPanelAfterTrackEdit()
    await refreshTrackResolutionAfterRoutingExit()
  } finally {
    isSavingTrackEdit.value = false
  }
}

const handleCloseTrackDrawing = async () => {
  await closeTrackDrawing()
  if (uiStore.isTrackDrawingActive) return
  if (!_isLibraryMode()) {
    restoreCollectionRoutingMapContent()
  }
  await refreshTrackResolutionAfterRoutingExit()
}

const handleSaveTrackDrawing = async () => {
  await saveTrackDrawing()
  if (uiStore.isTrackDrawingActive) return
  if (!_isLibraryMode()) {
    restoreCollectionRoutingMapContent()
  }
  await refreshTrackResolutionAfterRoutingExit()
}

const handleRouteModeLeaveRequest = (event: Event) => {
  const detail = (event as CustomEvent<RouteModeLeaveRequestDetail>).detail
  if (!detail || typeof detail.resolve !== 'function') return

  if (!uiStore.isTrackDrawingActive && !uiStore.isTrackEditMode) {
    detail.resolve(true)
    return
  }

  event.preventDefault()
  void (async () => {
    try {
      if (uiStore.isTrackDrawingActive) {
        await handleCloseTrackDrawing()
      }
      if (uiStore.isTrackEditMode) {
        await closeTrackEdit()
      }
      detail.resolve(!uiStore.isTrackDrawingActive && !uiStore.isTrackEditMode)
    } catch (error) {
      logService.error(LOG_CATEGORIES.UI, 'Failed to resolve route mode leave request', { error })
      detail.resolve(false)
    }
  })()
}

const editReferenceBeforeLayerId = computed(() => {
  if (!uiStore.isTrackEditMode || !uiStore.editingTrackTourId) return undefined
  const trackIndex = trackRoutingHost.resolveEditingTrackIndex()
  return trackIndex === null
    ? undefined
    : `tour-outline-${uiStore.editingTrackTourId}-${trackIndex}`
})

const editingTrack = computed(() => {
  if (!uiStore.isTrackEditMode || !uiStore.editingTrackTourId || !uiStore.editingTrackClientId) {
    return null
  }

  const draftTour = activeDraftTour.value
  const tour =
    draftTour?.clientId === uiStore.editingTrackTourId
      ? draftTour
      : getTrackTourById(uiStore.editingTrackTourId)
  return (
    tour?.tracks.find((candidate) => candidate.clientId === uiStore.editingTrackClientId) ?? null
  )
})

const editingTrackLayerIds = computed(() => {
  if (!uiStore.isTrackEditMode || !uiStore.editingTrackTourId || !uiStore.editingTrackClientId) {
    return [] as string[]
  }

  return [WORKSPACE_TOUR_EDIT_TOUCH_LAYER_ID]
})

const editingTrackCoordinates = computed(() => {
  const track = editingTrack.value
  if (!track) return [] as [number, number][]

  const lineString = getBestTrackLineString(track)
  return lineString ? parseLineString(lineString) : ([] as [number, number][])
})

const editingTrackSegments = computed(() => editingTrack.value?.segments ?? [])
const editingTrackHoverTitle = computed(
  () =>
    editingTrack.value?.name?.trim() ||
    getTrackTourById(uiStore.editingTrackTourId ?? '')?.name ||
    'Track',
)
// Sidebar occupies `left-4` (16px) + 480px width = 496px from the left edge.
// Give the profile box the same 12px gap on the left as on the right.
const SIDEBAR_RIGHT_EDGE_PX = 496
const profileBoxLeftMargin = computed(() =>
  !isSmallScreen.value && isSidebarVisible.value ? SIDEBAR_RIGHT_EDGE_PX + 12 : 12,
)
const mobileCollapsedPanelClearance = 'calc(env(safe-area-inset-bottom) + 200px)'
const selectedProfileBottomOffset = computed(() =>
  isSmallScreen.value && isSelectedRouteSegmentActive.value
    ? 'env(safe-area-inset-bottom)'
    : isSmallScreen.value && navigationStore.isActive
      ? 'calc(env(safe-area-inset-bottom) + 92px)'
      : isSmallScreen.value && !uiStore.isMobileBottomPanelExpanded
        ? mobileCollapsedPanelClearance
        : undefined,
)
const selectedProfilePanelClass = computed(() =>
  isSmallScreen.value && isSelectedRouteSegmentActive.value
    ? 'z-[106] !max-h-[62vh] !rounded-t-2xl !rounded-b-none !border-x-0 !border-b-0 shadow-2xl'
    : 'z-[106]',
)
const selectedProfileBodyClass = computed(() =>
  isSmallScreen.value && isSelectedRouteSegmentActive.value ? '!max-h-[calc(62vh-72px)]' : '',
)
const isSelectedTourProfileOpen = ref(false)
const selectedTourProfileOpenKey = ref(0)

// Mobile unified Route sheet (Details · Profile · Waypoints). `isMobileTourDetailVisible`
// is its open flag; the chart's "color by" metric lives in `routeSheetMetric`.
type RouteSheetTab = 'details' | 'profile' | 'waypoints'
type RouteSheetMode = 'peek' | 'mid' | 'full'
// Selecting a route lands on the Profile tab (its shape/terrain is what you scan first).
const routeSheetTab = ref<RouteSheetTab>('profile')
const lastRouteSheetMode = ref<RouteSheetMode>('mid')
const nextRouteSheetInitialMode = ref<RouteSheetMode>('mid')
const routeSheetMetric = ref<TrackProfileMetricId>('gradient')

// Each newly-selected route opens on the Profile tab again.
watch(
  () =>
    (uiStore.isAllActivitiesView
      ? libraryStore.detailTour?.clientId
      : workspaceStore.selectedTour?.clientId) ?? null,
  (tourId, previousTourId) => {
    if (tourId && tourId !== previousTourId) routeSheetTab.value = 'profile'
  },
)

// A profile "section" is one category of a breakdown bar (gradient tints the chart only,
// so only the Surfaces / Way types metrics drive the map overlay).
type ProfileBreakdownMetric = 'surface' | 'wayType'
type ProfileSection = { metricId: ProfileBreakdownMetric; categoryId: string }
type ProfileSectionFocus = ProfileSection & { segmentIndex: number }
type CurrentLocationUpdate = {
  lat: number
  lon: number
  accuracyM: number | null
  timestamp: number
  source: 'passive' | 'tracking'
}

const trackProfileCollapsed = ref(true)
const trackProfileCursorDistanceM = ref<number | null>(null)
const trackProfileVisibleRangeM = ref<TrackProfileVisibleRange | null>(null)
const trackProfileHoveredSection = ref<ProfileSection | null>(null)
const trackProfileInteractionResetKey = ref(0)
const trackProfileFocusedBreakdown = ref<ProfileSectionFocus | null>(null)
const routeModeSelectedMetric = ref<TrackProfileMetricId>('gradient')
const selectedTourProfileTrackClientId = ref<string | null>(null)
const selectedTourProfileCursorDistanceM = ref<number | null>(null)
const selectedTourProfileVisibleRangeM = ref<TrackProfileVisibleRange | null>(null)
const isSelectedRouteSegmentActive = ref(false)
const selectedRouteSegmentRangeM = ref<TrackProfileVisibleRange | null>(null)
const selectedTourProfileHoveredSection = ref<ProfileSection | null>(null)
const selectedTourProfileInteractionResetKey = ref(0)
const selectedTourProfileFocusedBreakdown = ref<ProfileSectionFocus | null>(null)
const currentLocation = ref<CurrentLocationUpdate | null>(null)

// While an A→B sub-selection is active, hide the on-map photos, waypoints and POIs so they don't
// clutter (or overlap) the focused segment and its A/B handles. Capture-and-restore per element:
// only re-show what we actually hid, so a manual "off" toggle is never overridden on Clear.
let photosHiddenForSegment = false
let waypointsHiddenForSegment = false
let poisHiddenForSegment = false
watch(isSelectedRouteSegmentActive, (active) => {
  if (active) {
    if (uiStore.arePhotosVisible) {
      photosHiddenForSegment = true
      uiStore.setPhotosVisible(false)
    }
    if (uiStore.areWaypointsVisible) {
      waypointsHiddenForSegment = true
      uiStore.setWaypointsVisible(false)
    }
    if (uiStore.arePOIsVisible) {
      poisHiddenForSegment = true
      uiStore.setPOIsVisible(false)
    }
  } else {
    if (photosHiddenForSegment) {
      photosHiddenForSegment = false
      uiStore.setPhotosVisible(true)
    }
    if (waypointsHiddenForSegment) {
      waypointsHiddenForSegment = false
      uiStore.setWaypointsVisible(true)
    }
    if (poisHiddenForSegment) {
      poisHiddenForSegment = false
      uiStore.setPOIsVisible(true)
    }
  }
})

// Which metric (if any) colors the map: from a live bar hover, else the clicked focus.
const selectedTourProfileActiveMetric = computed<ProfileBreakdownMetric | null>(
  () =>
    selectedTourProfileHoveredSection.value?.metricId ??
    selectedTourProfileFocusedBreakdown.value?.metricId ??
    null,
)
const selectedTourProfileHighlightedCategoryId = computed(
  () =>
    selectedTourProfileHoveredSection.value?.categoryId ??
    selectedTourProfileFocusedBreakdown.value?.categoryId ??
    null,
)
const routeModeInteractedBreakdown = computed<ProfileSection | null>(
  () => trackProfileHoveredSection.value ?? trackProfileFocusedBreakdown.value,
)
const routeModeActiveMetric = computed<ProfileBreakdownMetric | null>(() => {
  const section = routeModeInteractedBreakdown.value
  return section && routeModeSelectedMetric.value === section.metricId ? section.metricId : null
})
const routeModeHighlightedCategoryId = computed(() => {
  const section = routeModeInteractedBreakdown.value
  return section && routeModeSelectedMetric.value === section.metricId ? section.categoryId : null
})
const routeModeOverlayMetric = computed<TrackProfileMetricId>(() => routeModeSelectedMetric.value)
const drawingTrackProfileAnalyses = computed(() =>
  buildTrackProfileAnalyses(drawingTrackSegmentsForProfile.value, activeDrawingOverlayColor.value),
)
const drawingTrackProfileAnalysis = computed(
  () => drawingTrackProfileAnalyses.value[routeModeOverlayMetric.value],
)
const isDrawingMetricOverlayVisible = computed(
  () => uiStore.isTrackDrawingActive && drawingTrackProfileAnalysis.value.legs.length > 0,
)
const editingTrackProfileAnalyses = computed(() =>
  buildTrackProfileAnalyses(editingTrackSegments.value, activeDrawingOverlayColor.value),
)
const editingTrackProfileAnalysis = computed(
  () => editingTrackProfileAnalyses.value[routeModeOverlayMetric.value],
)
const isEditingMetricOverlayVisible = computed(
  () => uiStore.isTrackEditMode && editingTrackProfileAnalysis.value.legs.length > 0,
)
const isRouteModeMetricOverlayVisible = computed(
  () => isDrawingMetricOverlayVisible.value || isEditingMetricOverlayVisible.value,
)
// Drawing and edit mode are mutually exclusive; this picks the active context's analyses.
const routeModeActiveAnalyses = computed(() =>
  uiStore.isTrackEditMode ? editingTrackProfileAnalyses.value : drawingTrackProfileAnalyses.value,
)
const routeModeCursorPoint = computed(() =>
  trackProfileCursorDistanceM.value === null
    ? null
    : findTrackProfilePositionAtDistance(
        routeModeActiveAnalyses.value.gradient.points,
        trackProfileCursorDistanceM.value,
      ),
)
const resolveFocusedBreakdownLeg = (
  analysis: TrackProfileAnalysis,
  breakdownFocus: { categoryId: string; segmentIndex: number } | null,
) => {
  if (!breakdownFocus) return null
  const mergedSegments = mergeTrackProfileLegs(
    analysis.legs.filter((leg) => leg.categoryId === breakdownFocus.categoryId),
  )
  if (mergedSegments.length === 0) return null
  return mergedSegments[breakdownFocus.segmentIndex % mergedSegments.length] ?? null
}
const routeModeFocusedBreakdownSegment = computed(() => {
  const focus = trackProfileFocusedBreakdown.value
  if (!focus) return null
  if (routeModeSelectedMetric.value !== focus.metricId) return null
  return resolveFocusedBreakdownLeg(routeModeActiveAnalyses.value[focus.metricId], focus)
})
const selectedTourProfileTour = computed(() => {
  if (uiStore.isTrackEditMode) return null
  if (uiStore.isAllActivitiesView) return libraryStore.detailTour
  return selectedWorkspaceTourDetail.value
})
const selectedTourProfileTrack = computed(() =>
  resolveTrackProfileTrack(
    selectedTourProfileTour.value?.tracks ?? [],
    selectedTourProfileTrackClientId.value,
  ),
)
const selectedTourProfileHidesAscent = computed(() => {
  const track = selectedTourProfileTrack.value
  if (!track) return false
  return shouldHideAscentForTrack(track, selectedTourProfileTour.value?.type)
})
const selectedTourProfileHoverTitle = computed(
  () =>
    selectedTourProfileTrack.value?.name?.trim() || selectedTourProfileTour.value?.name || 'Track',
)
const selectedTourProfileColor = computed(() =>
  selectedTourProfileTour.value && selectedTourProfileTrack.value
    ? TrackColorService.getTourColor(
        selectedTourProfileTour.value.clientId,
        selectedTourProfileTrack.value.color,
      )
    : TrackColorService.getDefaultColor(),
)
const selectedTourProfileAnalyses = computed(() =>
  buildTrackProfileAnalyses(
    selectedTourProfileTrack.value?.segments ?? [],
    selectedTourProfileColor.value,
  ),
)
// The chart + cursor positioning always run off the gradient analysis (same geometry).
const selectedTourProfileAnalysis = computed(() => selectedTourProfileAnalyses.value.gradient)

// True while the selected tour's full detail (segments + elevation) is still being fetched, so
// the profile surfaces can show a spinner instead of a premature "No elevation data" message.
const isSelectedTourDetailLoading = computed(() =>
  uiStore.isAllActivitiesView
    ? libraryStore.isLoadingDetail
    : workspaceStore.isSelectedTourDetailLoading,
)

// The selected tour's own colour, fed to the A→B selection overlay so it highlights in a brighter
// shade of that colour rather than a fixed red.
const selectedTourAccentColor = computed(() =>
  workspaceStore.selectedTour ? resolveTourAccentColor(workspaceStore.selectedTour) : '#3388ff',
)
const selectedTourSurfacePatternTrack = computed(() => {
  if (uiStore.isTrackDrawingActive || uiStore.isTrackEditMode) return null
  if (uiStore.activePanel !== 'tourDetail') return null

  const tour = selectedTourProfileTour.value
  const track = selectedTourProfileTrack.value
  const trackClientId = selectedTourProfileTrackClientId.value ?? track?.clientId ?? null
  const surfaceAnalysis = selectedTourProfileAnalyses.value.surface

  if (!tour || !track || !trackClientId || surfaceAnalysis.legs.length === 0) return null

  return {
    tourId: tour.clientId,
    trackClientId,
    analysis: surfaceAnalysis,
    baseColor: selectedTourProfileColor.value,
    surfacePattern: true,
  }
})
const selectedRouteSegmentRange = computed<TrackProfileVisibleRange | null>(() => {
  if (!isSelectedRouteSegmentActive.value) return null
  if (selectedTourProfileAnalysis.value.points.length < 2) return null

  return (
    selectedRouteSegmentRangeM.value ?? {
      start: 0,
      end: selectedTourProfileAnalysis.value.totalDistanceM,
    }
  )
})
const selectedTourProfilePanelVisibleRangeM = computed<TrackProfileVisibleRange | null>(() =>
  isSelectedRouteSegmentActive.value
    ? selectedRouteSegmentRange.value
    : selectedTourProfileVisibleRangeM.value,
)
// Sum elevation gain/loss and the horizontal distance covered while climbing vs descending,
// then derive the average uphill / downhill grade (MapOut-style inline grades).
function accumulateProfileGrades(points: Array<{ ele: number | null; distanceM: number }>) {
  let elevationGainM = 0
  let elevationLossM = 0
  let uphillDistanceM = 0
  let downhillDistanceM = 0
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]
    const current = points[index]
    if (previous.ele === null || current.ele === null) continue
    const delta = current.ele - previous.ele
    const horizontal = Math.max(0, current.distanceM - previous.distanceM)
    if (delta > 0) {
      elevationGainM += delta
      uphillDistanceM += horizontal
    } else if (delta < 0) {
      elevationLossM += Math.abs(delta)
      downhillDistanceM += horizontal
    }
  }
  return {
    elevationGainM,
    elevationLossM,
    ascentGradePercent: uphillDistanceM > 0 ? (elevationGainM / uphillDistanceM) * 100 : null,
    descentGradePercent: downhillDistanceM > 0 ? -(elevationLossM / downhillDistanceM) * 100 : null,
  }
}

const selectedRouteSegmentInfo = computed(() => {
  const range = selectedRouteSegmentRange.value
  const analysis = selectedTourProfileAnalysis.value
  if (!range || analysis.points.length < 2) return null

  const startPoint = findTrackProfilePositionAtDistance(analysis.points, range.start)
  const endPoint = findTrackProfilePositionAtDistance(analysis.points, range.end)
  if (!startPoint || !endPoint) return null

  const points = [
    startPoint,
    ...analysis.points.filter(
      (point) => point.distanceM > range.start && point.distanceM < range.end,
    ),
    endPoint,
  ]
  const grades = accumulateProfileGrades(points)
  const distanceM = Math.max(0, range.end - range.start)
  const averageGrade =
    startPoint.ele !== null && endPoint.ele !== null && distanceM > 0
      ? ((endPoint.ele - startPoint.ele) / distanceM) * 100
      : null

  return {
    distanceM,
    startDistanceM: range.start,
    endDistanceM: range.end,
    startElevationM: startPoint.ele,
    endElevationM: endPoint.ele,
    elevationGainM: grades.elevationGainM,
    elevationLossM: grades.elevationLossM,
    averageGrade,
    ascentGradePercent: grades.ascentGradePercent,
    descentGradePercent: grades.descentGradePercent,
  }
})

// Average uphill / downhill grade for the full profiled track, fed to the stats row when no
// A→B segment is selected (MapOut shows these small next to ascent / descent).
const profiledTrackGrades = computed(() =>
  accumulateProfileGrades(selectedTourProfileAnalysis.value.points),
)

// A→B segment metrics for the Route sheet's stats row (null unless a segment is selected).
const selectedRouteSegmentStats = computed(() => {
  if (!isSelectedRouteSegmentActive.value) return null
  const info = selectedRouteSegmentInfo.value
  if (!info) return null
  return {
    distanceM: info.distanceM,
    gainM: info.elevationGainM,
    lossM: info.elevationLossM,
  }
})

// Inline uphill / downhill grades for the stats row — the A→B segment's when selected, else
// the whole profiled track's.
const routeSheetAscentGrade = computed(() =>
  selectedTourProfileHidesAscent.value
    ? null
    : isSelectedRouteSegmentActive.value
      ? (selectedRouteSegmentInfo.value?.ascentGradePercent ?? null)
      : profiledTrackGrades.value.ascentGradePercent,
)
const routeSheetDescentGrade = computed(() =>
  isSelectedRouteSegmentActive.value
    ? (selectedRouteSegmentInfo.value?.descentGradePercent ?? null)
    : profiledTrackGrades.value.descentGradePercent,
)
const formatSegmentDistance = (distanceM: number) =>
  distanceM >= 1000 ? `${(distanceM / 1000).toFixed(1)} km` : `${Math.round(distanceM)} m`
const formatSegmentElevation = (elevationM: number | null) =>
  elevationM === null ? '—' : `${Math.round(elevationM)} m`
const formatSignedSegmentElevation = (elevationM: number) => {
  const rounded = Math.round(elevationM)
  return rounded > 0 ? `+${rounded} m` : `${rounded} m`
}
const formatSegmentGrade = (grade: number | null) =>
  grade === null ? '—' : `${grade > 0 ? '+' : ''}${grade.toFixed(1)}%`
const selectedRouteProfileSummary = computed<TrackProfileSummary | null>(() => {
  const info = selectedRouteSegmentInfo.value
  if (!info) return null
  const hideAscent = selectedTourProfileHidesAscent.value

  const compact = hideAscent
    ? `A → B ${formatSegmentDistance(info.distanceM)} / -${Math.round(info.elevationLossM)} m`
    : `A → B ${formatSegmentDistance(info.distanceM)} ${formatSignedSegmentElevation(
        info.elevationGainM,
      )} / -${Math.round(info.elevationLossM)} m ${formatSegmentGrade(info.averageGrade)}`

  return {
    primary: hideAscent
      ? `A → B ${formatSegmentDistance(info.distanceM)} / -${Math.round(info.elevationLossM)} m`
      : `A → B ${formatSegmentDistance(info.distanceM)} ${formatSignedSegmentElevation(
          info.elevationGainM,
        )} / -${Math.round(info.elevationLossM)} m`,
    compact,
    metrics: hideAscent
      ? undefined
      : {
          distance: formatSegmentDistance(info.distanceM),
          gain: formatSignedSegmentElevation(info.elevationGainM),
          loss: `${Math.round(info.elevationLossM)} m`,
          grade: formatSegmentGrade(info.averageGrade),
        },
    secondary: hideAscent
      ? `${formatSegmentDistance(info.startDistanceM)} - ${formatSegmentDistance(info.endDistanceM)}`
      : `${formatSegmentDistance(info.startDistanceM)} - ${formatSegmentDistance(
          info.endDistanceM,
        )} ${formatSegmentElevation(info.startElevationM)} → ${formatSegmentElevation(
          info.endElevationM,
        )} ${formatSegmentGrade(info.averageGrade)}`,
  }
})
type ScreenPoint = { x: number; y: number }
type ScreenRect = { left: number; top: number; right: number; bottom: number }

const clipLineToRect = (
  start: ScreenPoint,
  end: ScreenPoint,
  rect: ScreenRect,
): { startT: number; endT: number } | null => {
  const dx = end.x - start.x
  const dy = end.y - start.y
  let startT = 0
  let endT = 1

  const clipEdge = (p: number, q: number) => {
    if (p === 0) return q >= 0
    const t = q / p
    if (p < 0) {
      if (t > endT) return false
      if (t > startT) startT = t
      return true
    }

    if (t < startT) return false
    if (t < endT) endT = t
    return true
  }

  if (
    !clipEdge(-dx, start.x - rect.left) ||
    !clipEdge(dx, rect.right - start.x) ||
    !clipEdge(-dy, start.y - rect.top) ||
    !clipEdge(dy, rect.bottom - start.y)
  ) {
    return null
  }

  return startT <= endT ? { startT, endT } : null
}

const getInitialRouteSegmentViewportPadding = () => {
  const padding = { ...calculateViewportPadding() }
  if (!isSmallScreen.value) return padding

  // Match the MapOut-style usable map band: exclude persistent controls and
  // the compact route profile / mobile action panel without over-reserving space.
  padding.top = Math.max(padding.top, 96)
  // While the Route sheet is open, reserve its full height (+ tab bar) so the A→B segment
  // centres in the visible map band above the sheet, not behind it.
  if (isMobileRouteSheetOpen.value) {
    const sheetHeight = routeSheetHeight.value ?? Math.floor(window.innerHeight * 0.52)
    padding.bottom = sheetHeight + 64 + 20
  } else {
    padding.bottom = Math.max(padding.bottom, 246)
  }
  padding.left = Math.max(padding.left, 12)
  padding.right = Math.max(padding.right, 84)

  return padding
}

const resolveVisibleTrackProfileRange = (
  analysis: TrackProfileAnalysis,
): TrackProfileVisibleRange | null => {
  const map = mapInstance.value
  if (!map || analysis.points.length < 2) return null

  const canvas = map.getCanvas()
  const padding = getInitialRouteSegmentViewportPadding()
  const rect: ScreenRect = {
    left: padding.left,
    top: padding.top,
    right: Math.max(padding.left + 1, canvas.clientWidth - padding.right),
    bottom: Math.max(padding.top + 1, canvas.clientHeight - padding.bottom),
  }

  let visibleStartM: number | null = null
  let visibleEndM: number | null = null

  for (let pointIndex = 1; pointIndex < analysis.points.length; pointIndex += 1) {
    const startPoint = analysis.points[pointIndex - 1]
    const endPoint = analysis.points[pointIndex]
    const projectedStart = map.project([startPoint.lon, startPoint.lat])
    const projectedEnd = map.project([endPoint.lon, endPoint.lat])
    const clipped = clipLineToRect(projectedStart, projectedEnd, rect)
    if (!clipped) continue

    const segmentDistanceM = endPoint.distanceM - startPoint.distanceM
    if (segmentDistanceM <= 0) continue

    const clippedStartM = startPoint.distanceM + segmentDistanceM * clipped.startT
    const clippedEndM = startPoint.distanceM + segmentDistanceM * clipped.endT
    visibleStartM = visibleStartM === null ? clippedStartM : Math.min(visibleStartM, clippedStartM)
    visibleEndM = visibleEndM === null ? clippedEndM : Math.max(visibleEndM, clippedEndM)
  }

  if (visibleStartM === null || visibleEndM === null || visibleEndM - visibleStartM < 25) {
    return null
  }

  return {
    start: Math.max(0, visibleStartM),
    end: Math.min(analysis.totalDistanceM, visibleEndM),
  }
}

const currentLocationMarkerForAnalysis = (
  analysis: TrackProfileAnalysis,
): TrackProfileLocationMatch | null =>
  findCurrentLocationOnTrackProfile(
    analysis,
    currentLocation.value
      ? {
          lat: currentLocation.value.lat,
          lon: currentLocation.value.lon,
          accuracyM: currentLocation.value.accuracyM,
        }
      : null,
  )
const drawingTrackProfileCurrentLocation = computed(() =>
  currentLocationMarkerForAnalysis(drawingTrackProfileAnalysis.value),
)
const editingTrackProfileCurrentLocation = computed(() =>
  currentLocationMarkerForAnalysis(editingTrackProfileAnalysis.value),
)
const selectedTourProfileCurrentLocation = computed(() =>
  currentLocationMarkerForAnalysis(selectedTourProfileAnalysis.value),
)
// Overlay analysis: the active metric's legs while interacting, else gradient (cursor-only).
const selectedTourProfileOverlayAnalysis = computed(() =>
  selectedTourProfileActiveMetric.value
    ? selectedTourProfileAnalyses.value[selectedTourProfileActiveMetric.value]
    : selectedTourProfileAnalyses.value.gradient,
)
// The layer mounts whenever the tour profile is open (cursor marker stays on the plain
// route); it only paints metric legs when a metric is active (cursorOnly === false).
const isSelectedTourProfileLayerVisible = computed(
  () =>
    !uiStore.isTrackEditMode &&
    uiStore.activePanel === 'tourDetail' &&
    selectedTourProfileAnalysis.value.points.length > 0,
)
const suppressedWorkspaceTourTrack = computed(() => {
  if (!isRouteModeMetricOverlayVisible.value) return null

  if (uiStore.isTrackDrawingActive && drawingTrackClientId.value) {
    const targetTourId = resolveTargetTourId(getActiveRoutingScope())
    if (!targetTourId) return null
    return { tourId: targetTourId, trackClientId: drawingTrackClientId.value }
  }

  if (uiStore.isTrackEditMode && uiStore.editingTrackTourId && uiStore.editingTrackClientId) {
    return { tourId: uiStore.editingTrackTourId, trackClientId: uiStore.editingTrackClientId }
  }

  return null
})
const workspaceMetricColorizedTrack = computed(() => {
  if (
    uiStore.isTrackDrawingActive &&
    drawingTrackClientId.value &&
    drawingTrackProfileAnalysis.value.legs.length > 0
  ) {
    const targetTourId = resolveTargetTourId(getActiveRoutingScope())
    if (!targetTourId) return null

    return {
      tourId: targetTourId,
      trackClientId: drawingTrackClientId.value,
      analysis: drawingTrackProfileAnalysis.value,
    }
  }

  if (
    uiStore.isTrackEditMode &&
    uiStore.editingTrackTourId &&
    uiStore.editingTrackClientId &&
    editingTrackProfileAnalysis.value.legs.length > 0
  ) {
    return {
      tourId: uiStore.editingTrackTourId,
      trackClientId: uiStore.editingTrackClientId,
      analysis: editingTrackProfileAnalysis.value,
    }
  }

  if (selectedTourSurfacePatternTrack.value) {
    return selectedTourSurfacePatternTrack.value
  }

  return null
})
const selectedTourProfileCursorPoint = computed(() =>
  selectedTourProfileCursorDistanceM.value === null
    ? null
    : findTrackProfilePositionAtDistance(
        selectedTourProfileAnalysis.value.points,
        selectedTourProfileCursorDistanceM.value,
      ),
)
const mapTrackContextMenu = ref<{
  visible: boolean
  tourId: string | null
  trackClientId: string | null
  trackIndex: number | null
  x: number
  y: number
}>({
  visible: false,
  tourId: null,
  trackClientId: null,
  trackIndex: null,
  x: 0,
  y: 0,
})
const mapTrackOverlapMenu = ref<{
  visible: boolean
  candidates: Array<{
    tourId: string
    trackClientId: string
    trackIndex: number
  }>
  x: number
  y: number
}>({
  visible: false,
  candidates: [],
  x: 0,
  y: 0,
})
const mapPOIContextMenu = ref<{
  visible: boolean
  poiId: string | null
  x: number
  y: number
}>({
  visible: false,
  poiId: null,
  x: 0,
  y: 0,
})
const mapWorkspaceContextMenu = ref<{
  visible: boolean
  workspaceId: string | null
  x: number
  y: number
}>({
  visible: false,
  workspaceId: null,
  x: 0,
  y: 0,
})
const mapTourDownloadTarget = ref<Tour | null>(null)
const mapTourDownloadingIds = ref<Set<string>>(new Set())
const isMapTourDownloading = computed(
  () =>
    !!mapTourDownloadTarget.value &&
    mapTourDownloadingIds.value.has(mapTourDownloadTarget.value.clientId),
)
const MAP_CONTEXT_MENU_WIDTH_PX = 224
const MAP_CONTEXT_MENU_ROW_HEIGHT_PX = 40
const MAP_CONTEXT_MENU_VERTICAL_PADDING_PX = 8
const MAP_CONTEXT_MENU_GAP_PX = 8
const MAP_CONTEXT_MENU_MARGIN_PX = 12

const getMapContextMenuBounds = () => {
  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1024
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 768
  let left = MAP_CONTEXT_MENU_MARGIN_PX
  let top = MAP_CONTEXT_MENU_MARGIN_PX
  const right = viewportWidth - MAP_CONTEXT_MENU_MARGIN_PX
  let bottom = viewportHeight - MAP_CONTEXT_MENU_MARGIN_PX

  if (isSmallScreen.value) {
    const mobileNavBottomOffset = Math.max(readSafeAreaInsetBottomPx(), 10)
    const mobileNavTop =
      viewportHeight - mobileNavBottomOffset - FLOATING_TAB_BAR_HEIGHT - MAP_CONTEXT_MENU_MARGIN_PX
    bottom = Math.min(bottom, mobileNavTop)

    const mobilePanel = mobileToolbarRef.value?.$el as HTMLElement | undefined
    const panelTop = mobilePanel?.getBoundingClientRect?.().top
    if (typeof panelTop === 'number' && panelTop > 0) {
      bottom = Math.min(bottom, panelTop - MAP_CONTEXT_MENU_MARGIN_PX)
    }
  } else {
    const sidebar = toolbarRef.value?.$el as HTMLElement | undefined
    const sidebarRect = sidebar?.getBoundingClientRect?.()
    if (sidebarRect && sidebarRect.right > left) {
      left = Math.min(sidebarRect.right + MAP_CONTEXT_MENU_MARGIN_PX, right)
    }

    const header = localHeaderRef.value?.$el as HTMLElement | undefined
    const headerRect = header?.getBoundingClientRect?.()
    if (headerRect && headerRect.bottom > top) {
      top = Math.min(headerRect.bottom + MAP_CONTEXT_MENU_MARGIN_PX, bottom)
    }
  }

  return { left, top, right, bottom }
}

const positionMapContextMenu = (x: number, y: number, itemCount: number) => {
  const bounds = getMapContextMenuBounds()
  const estimatedHeight =
    itemCount * MAP_CONTEXT_MENU_ROW_HEIGHT_PX + MAP_CONTEXT_MENU_VERTICAL_PADDING_PX
  const availableWidth = Math.max(0, bounds.right - bounds.left)
  const availableHeight = Math.max(0, bounds.bottom - bounds.top)
  const width = Math.min(MAP_CONTEXT_MENU_WIDTH_PX, availableWidth)
  const height = Math.min(estimatedHeight, availableHeight)

  let left = x + MAP_CONTEXT_MENU_GAP_PX
  if (left + width > bounds.right) {
    left = x - width - MAP_CONTEXT_MENU_GAP_PX
  }
  left = Math.min(Math.max(left, bounds.left), Math.max(bounds.left, bounds.right - width))

  let top = y + MAP_CONTEXT_MENU_GAP_PX
  if (top + height > bounds.bottom) {
    top = y - height - MAP_CONTEXT_MENU_GAP_PX
  }
  top = Math.min(Math.max(top, bounds.top), Math.max(bounds.top, bounds.bottom - height))

  return {
    left: `${Math.round(left)}px`,
    top: `${Math.round(top)}px`,
    width: `${Math.round(width)}px`,
    maxHeight: `${Math.round(availableHeight)}px`,
  }
}

// Library tour map context menu (desktop right-click and mobile tap)
const libraryMapContextMenu = ref<{
  visible: boolean
  tourId: string | null
  trackClientId: string | null
  trackIndex: number | null
  x: number
  y: number
}>({
  visible: false,
  tourId: null,
  trackClientId: null,
  trackIndex: null,
  x: 0,
  y: 0,
})
const libraryContextMenuDownloading = ref(false)

const closeLibraryMapContextMenu = () => {
  if (
    libraryMapContextMenu.value.visible &&
    libraryStore.hoveredTourId === libraryMapContextMenu.value.tourId
  ) {
    libraryStore.clearHoveredTour()
  }
  libraryMapContextMenu.value.visible = false
}

const selectedLibraryMapContextTour = computed(() =>
  libraryMapContextMenu.value.tourId
    ? (libraryStore.tours.find(
        (candidate) => candidate.clientId === libraryMapContextMenu.value.tourId,
      ) ?? null)
    : null,
)

const selectedLibraryMapContextTourIsActivity = computed(() => {
  const tour = selectedLibraryMapContextTour.value
  return !!tour && (!!tour.type || !!tour.startDate)
})

const libraryMapContextItems = computed<ActionMenuItem[]>(() => {
  const tour = selectedLibraryMapContextTour.value
  if (!tour) return []

  return buildTourPrimaryActionItems({
    tour,
    canEdit: hasPermission('edit_track'),
    isActivity: selectedLibraryMapContextTourIsActivity.value,
    isDownloading: libraryContextMenuDownloading.value,
    onTogglePinned: () => {
      void toggleLibraryMapContextTourPinned()
    },
    onEditRoute: () => {
      void editLibraryMapContextTourRoute()
    },
    onDownload: () => {
      void downloadLibraryTourGpx()
    },
    onAddToCollection: manageLibraryTourCollections,
  })
})
const libraryMapContextMenuStyle = computed(() =>
  positionMapContextMenu(
    libraryMapContextMenu.value.x,
    libraryMapContextMenu.value.y,
    libraryMapContextItems.value.length,
  ),
)

const handleLibraryContextMenu = (detail: {
  tourId: string
  trackClientId: string
  trackIndex: number
  clientX: number
  clientY: number
}) => {
  closeMapEntityContextMenus()
  libraryMapContextMenu.value = {
    visible: true,
    tourId: detail.tourId,
    trackClientId: detail.trackClientId,
    trackIndex: detail.trackIndex,
    x: detail.clientX,
    y: detail.clientY,
  }
}

const downloadLibraryTourGpx = async () => {
  const tourId = libraryMapContextMenu.value.tourId
  if (!tourId) return
  const tour = libraryStore.tours.find((t) => t.clientId === tourId)
  closeLibraryMapContextMenu()
  libraryContextMenuDownloading.value = true
  try {
    const blob = await apiService.downloadGpx(tourId)
    await downloadFile(blob, `${(tour?.name ?? 'tour').trim()}.gpx`, {
      mimeType: 'application/gpx+xml',
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download library tour GPX', { error })
    showError(`Failed to download: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    libraryContextMenuDownloading.value = false
  }
}

const manageLibraryTourCollections = () => {
  const { tourId, x, y } = libraryMapContextMenu.value
  if (!tourId) return
  closeLibraryMapContextMenu()
  toolbarRef.value?.openAddToCollectionByTourId(tourId, { x, y })
}

const toggleLibraryMapContextTourPinned = async () => {
  const tour = selectedLibraryMapContextTour.value
  if (!tour) return
  closeLibraryMapContextMenu()
  await toggleTourPinned(tour)
}

const editLibraryMapContextTourRoute = async () => {
  const { tourId, trackClientId } = libraryMapContextMenu.value
  if (!tourId || !trackClientId) return
  closeLibraryMapContextMenu()
  await enterTrackEditModeForTrack(tourId, trackClientId, 'library')
}
const selectedTourProfileFocusedBreakdownSegment = computed(() => {
  const focus = selectedTourProfileFocusedBreakdown.value
  if (!focus) return null
  return resolveFocusedBreakdownLeg(selectedTourProfileAnalyses.value[focus.metricId], focus)
})
const editingTrackExists = computed(() => {
  if (!uiStore.isTrackEditMode || !uiStore.editingTrackTourId || !uiStore.editingTrackClientId) {
    return true
  }

  const tour = getTrackTourById(uiStore.editingTrackTourId)
  return !!tour?.tracks.some((track) => track.clientId === uiStore.editingTrackClientId)
})

const resetSelectedTourProfileBreakdownState = () => {
  selectedTourProfileHoveredSection.value = null
  selectedTourProfileFocusedBreakdown.value = null
  selectedTourProfileInteractionResetKey.value += 1
}

const resetSelectedTourProfileLegendInteraction = () => {
  selectedTourProfileHoveredSection.value = null
  selectedTourProfileInteractionResetKey.value += 1
}

const clearSelectedTourProfileMetricOverlayState = () => {
  selectedTourProfileCursorDistanceM.value = null
  selectedTourProfileVisibleRangeM.value = null
  resetSelectedTourProfileBreakdownState()
}

const resetTrackProfileBreakdownState = () => {
  trackProfileHoveredSection.value = null
  trackProfileFocusedBreakdown.value = null
  trackProfileInteractionResetKey.value += 1
}

const resetTrackProfileLegendInteraction = () => {
  trackProfileHoveredSection.value = null
  trackProfileInteractionResetKey.value += 1
}

const handleRouteModeProfileMetricChange = (metric: TrackProfileMetricId) => {
  routeModeSelectedMetric.value = metric
  resetTrackProfileBreakdownState()
}

const editReferenceSegments = computed(() =>
  buildTrackEditReferenceDiffSegments(
    editReferenceCoordinates.value,
    editingTrackCoordinates.value,
  ),
)

// Hover popup data for the red-dashed reference line: the original (pre-edit) track's
// profile in the currently selected metric, so hovering it shows the same segment info
// as the edited track.
const editReferenceProfileAnalysis = computed(() =>
  uiStore.isTrackEditMode && editReferenceTrackSegments.value.length > 0
    ? buildTrackProfileAnalyses(editReferenceTrackSegments.value)[routeModeOverlayMetric.value]
    : null,
)
const editReferenceHoverTitle = computed(() => `${editingTrackHoverTitle.value} · Original`)

watch(editingTrackExists, (exists) => {
  if (!uiStore.isTrackEditMode || exists) return
  void closeTrackEdit()
})

watch(
  () => selectedTourProfileTour.value?.clientId ?? null,
  () => {
    selectedTourProfileTrackClientId.value = null
    selectedTourProfileCursorDistanceM.value = null
    selectedTourProfileVisibleRangeM.value = null
    selectedRouteSegmentRangeM.value = null
    isSelectedRouteSegmentActive.value = false
    isSelectedTourProfileOpen.value = false
    resetSelectedTourProfileBreakdownState()
  },
)

watch(
  () => [uiStore.isTrackEditMode, uiStore.editingTrackClientId] as const,
  ([isTrackEditMode]) => {
    if (!isTrackEditMode) {
      trackProfileCursorDistanceM.value = null
      trackProfileVisibleRangeM.value = null
      resetTrackProfileBreakdownState()
    }
  },
  { immediate: true },
)

watch(
  () => selectedTourProfileTrack.value?.clientId ?? null,
  () => {
    selectedTourProfileVisibleRangeM.value = null
    resetSelectedTourProfileBreakdownState()
  },
)

watch(
  () => uiStore.editingTrackClientId ?? null,
  () => {
    trackProfileVisibleRangeM.value = null
    resetTrackProfileBreakdownState()
  },
)

const isSparseTrackToolboxOpen = computed(() => {
  const setup = sparseTrackSetup.value
  return (
    !!setup &&
    uiStore.isTrackEditMode &&
    setup.tourId === uiStore.editingTrackTourId &&
    setup.trackClientId === uiStore.editingTrackClientId
  )
})

function getTrackTourById(tourId: string | null | undefined): Tour | null {
  if (!tourId) return null
  const draftTour = activeDraftTour.value
  if (draftTour?.clientId === tourId) {
    return draftTour
  }

  if (
    resolveRoutingScopeForTour(tourId) === 'library' &&
    libraryStore.detailTour?.clientId === tourId
  ) {
    return libraryStore.detailTour
  }

  return workspaceStore.getTourById(tourId) ?? null
}

const focusRoutingTour = (tourId: string | null) => {
  if (!tourId) return
  if (_isLibraryMode()) {
    libraryStore.exitSelectionMode()
    return
  }
  workspaceStore.setSelectedTourId(null)
  workspaceStore.setHoveredTourId(null)
  uiStore.hideTourDetailPanel()
  hideCollectionRoutingMapContent()
}

const getKnownRoutingTour = (
  tourId: string | null,
  preferredScope?: RoutingScope | null,
): Tour | null => {
  if (!tourId) return null
  if (resolveRoutingScopeForTour(tourId, preferredScope) === 'library') {
    const detail = libraryStore.detailTour
    return detail?.clientId === tourId ? detail : null
  }
  return workspaceStore.getTourById(tourId) ?? null
}

const ensureRoutingTourLoaded = async (
  tourId: string,
  scope: RoutingScope,
): Promise<Tour | null> => {
  if (scope === 'library') {
    if (libraryStore.detailTour?.clientId !== tourId) {
      const listTour = libraryStore.tours.find((candidate) => candidate.clientId === tourId)
      if (listTour) {
        libraryStore.selectTourForDetail(listTour, { focusMap: false })
      }
    }

    await libraryStore.loadFullTourDetail(tourId)
    return libraryStore.detailTour?.clientId === tourId ? libraryStore.detailTour : null
  }

  await workspaceStore.ensureTourDetailLoaded?.(tourId)
  return workspaceStore.getTourById(tourId) ?? null
}

const persistTourRoutingProfile = async (
  tour: Tour,
  routingProfile: string,
  scope: RoutingScope | null,
) => {
  try {
    if (scope === 'library') {
      await libraryStore.updateTour(tour.clientId, { routingProfile })
      return
    }

    await workspaceStore.updateTour(tour.clientId, { routingProfile })
  } catch (error) {
    logService.error(LOG_CATEGORIES.MAP, 'Failed to persist tour routing profile', {
      tourId: tour.clientId,
      routingProfile,
      error,
    })
    showError('Failed to save tour routing profile')
  }
}

const getActiveRoutingTour = (): Tour | null => {
  if (uiStore.isTrackEditMode) {
    return getKnownRoutingTour(uiStore.editingTrackTourId, getActiveRoutingScope())
  }

  return getKnownRoutingTour(resolveTargetTourId(), getActiveRoutingScope())
}

const handleRoutingProfileChange = (routingProfile: string) => {
  const routingScope = getActiveRoutingScope()
  const tour = getActiveRoutingTour()
  // While a drawing / edit session is open, a profile change belongs to the
  // in-memory route draft only for the *tour record*: the routing-profile
  // watcher in useTrackRoutingSession reroutes the draft and stamps the
  // profile onto the draft track, which is persisted on Save. Persisting the
  // tour here would sync the tour mid-session — a regression; a profile change
  // must never sync mid-session. The user's global "last used profile" setting
  // is unrelated to that tour sync, so an explicit dropdown choice should
  // always update it, session or not.
  const isRoutingSessionActive = uiStore.isTrackDrawingActive || uiStore.isTrackEditMode

  uiStore.setRoutingProfile(routingProfile)

  if (tour && !isRoutingSessionActive) {
    void persistTourRoutingProfile(tour, uiStore.routingProfile, routingScope)
  }
}

const handleStartTrackDrawing = async (
  resolvedTourId?: string | null,
  scope?: RoutingScope | null,
) => {
  const routingScope = resolveRoutingScopeForTour(resolvedTourId ?? null, scope)
  const targetTourId = resolvedTourId ?? resolveTargetTourId(routingScope)
  if (!routingScope || !targetTourId) {
    showError('No tour available to add track')
    return
  }
  const targetTour = getKnownRoutingTour(targetTourId, routingScope)
  // Guard entry into drawing mode up front — creator-only content edit. A tour
  // created by someone else must never let a collaborator start adding a track
  // to it, not fail later when the track tries to save.
  if (
    targetTour &&
    !(await requireItemEditable(targetTour.canEdit, 'track', targetTour.createdByName))
  )
    return
  const targetTourRoutingProfile = targetTour?.routingProfile
  if (isKnownRoutingProfileId(targetTourRoutingProfile)) {
    uiStore.setRoutingProfile(targetTourRoutingProfile, { persist: false })
  }

  activeRoutingScope.value = routingScope
  activeRoutingTourId.value = targetTourId
  isMobileToolbarVisible.value = false
  clearSelectedTourProfileMetricOverlayState()
  focusRoutingTour(targetTourId)
  trackDrawing.beginDrawing()
}

const confirmAdditionalTrackWarning = async (
  tourId: string | null,
  scope?: RoutingScope | null,
) => {
  if (!tourId) return true
  if (settingsService.getSetting<boolean>(UI_SETTING_KEYS.mapAdditionalTrackWarningAcknowledged)) {
    return true
  }

  const tour = getKnownRoutingTour(tourId, scope)
  if (!tour || tour.tracks.length === 0) return true

  const confirmed = await showConfirm({
    title: 'Add another track to this tour?',
    message:
      'Most users should create a new tour instead of adding a second track here. Multiple tracks in one tour are mainly kept for GPX compatibility. Do you want to add another track anyway?',
    confirmText: 'Add track anyway',
    cancelText: 'Cancel',
    variant: 'warning',
  })

  if (confirmed) {
    settingsService.saveSetting(UI_SETTING_KEYS.mapAdditionalTrackWarningAcknowledged, true)
  }

  return confirmed
}

const enterTrackEditModeForTrack = async (
  tourId: string,
  trackClientId: string,
  scope?: RoutingScope | null,
) => {
  if (uiStore.isTrackEditMode) return
  const routingScope = resolveRoutingScopeForTour(tourId, scope)
  if (!routingScope) return
  preparingTrackEdit.value = { tourId, trackClientId }
  try {
    if (!(await ensureRoutingTourLoaded(tourId, routingScope))) return
  } catch {
    if (
      preparingTrackEdit.value?.tourId === tourId &&
      preparingTrackEdit.value?.trackClientId === trackClientId
    ) {
      preparingTrackEdit.value = null
    }
    return
  }

  try {
    const tour = getKnownRoutingTour(tourId, routingScope)
    if (!tour) return
    // Guard entry into routing mode up front — creator-only content edit. A
    // non-creator collaborator must never be let into the drawing session at
    // all, not fail later when the edits try to save.
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return
    const trackIndex = tour.tracks.findIndex((candidate) => candidate.clientId === trackClientId)
    if (trackIndex < 0) return
    const track = tour.tracks[trackIndex]
    activeRoutingScope.value = routingScope
    focusRoutingTour(tourId)
    resetEditState()
    clearSelectedTourProfileMetricOverlayState()
    if (
      isSmallScreen.value &&
      uiStore.activePanel === 'tourDetail' &&
      ((routingScope === 'library' && libraryStore.detailTour?.clientId === tourId) ||
        (routingScope === 'workspace' && workspaceStore.selectedTour?.clientId === tourId))
    ) {
      hiddenTourDetailPanelForTrackEditTourId.value = isMobileTourDetailVisible.value
        ? tourId
        : null
      uiStore.hideTourDetailPanel()
    } else {
      hiddenTourDetailPanelForTrackEditTourId.value = null
    }
    const trackRoutePoints = getTrackRoutePoints(tour.routePoints, trackClientId)
    const hasSparseTrackRoutePoints = trackRoutePoints.length <= 2
    const hasExplicitRoutingProfile =
      isKnownRoutingProfileId(track.routingProfile) || isKnownRoutingProfileId(tour.routingProfile)
    if (!hasSparseTrackRoutePoints || hasExplicitRoutingProfile) {
      uiStore.setRoutingProfile(resolveTrackRoutingProfileId(track, tour.routingProfile), {
        persist: false,
      })
    }
    uiStore.enterTrackEditMode(tourId, trackClientId)
    isMobileToolbarVisible.value = false

    captureEditReferenceTrack(track)
    initEditModeLastClickedPoint(track)
    await nextTick()
    if (
      !uiStore.isTrackEditMode ||
      uiStore.editingTrackTourId !== tourId ||
      uiStore.editingTrackClientId !== trackClientId
    ) {
      return
    }
    maybeOpenSparseTrackSetup()
  } finally {
    if (
      preparingTrackEdit.value?.tourId === tourId &&
      preparingTrackEdit.value?.trackClientId === trackClientId
    ) {
      preparingTrackEdit.value = null
    }
  }
}

const selectedWaypointLayerRef = ref<InstanceType<typeof WaypointMarkerLayer> | null>(null)
const smartSearchPin: Ref<NonNullable<SmartSearchLocationEventDetail['pin']> | null> = ref(null)

// Photo slideshow state
const isSlideshowOpen = ref(false)
const currentPhotoId = ref('')

// Deep link handling
const pendingDeepLinkTourId = ref<string | null>(null)

const tryFocusDeepLinkedTour = () => {
  const tourId = pendingDeepLinkTourId.value
  if (!tourId) return
  // Require map loaded and tour present in store
  const tour = workspaceStore.getTourById(tourId)
  if (!mapLoaded.value || !tour) return

  workspaceStore.selectTour(tourId)
  showTourDetailPanel()
  if (uiStore.mapNavigationIntent === null) {
    uiStore.beginMapNavigation('deeplink')
  }
  if (shouldAutoMoveRouteItem()) {
    zoomToTour(tourId)
  }
  finalizeRouteItemMovement()
  // Signal toolbars to scroll photos to tour start
  nextTick(() => {
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent('focus-tour', { detail: { tourId } }))
    })
  })

  pendingDeepLinkTourId.value = null
}

const getWorkspaceContentBounds = (): maplibregl.LngLatBounds | null => {
  const tourBounds = workspaceStore.calculateMapBounds()
  const photoBounds = photoStore.calculateMapBounds()
  let combinedBounds: maplibregl.LngLatBounds | null = null

  if (tourBounds && !tourBounds.isEmpty()) {
    combinedBounds = tourBounds
  }

  if (photoBounds && !photoBounds.isEmpty()) {
    if (combinedBounds) {
      photoBounds.toArray().forEach((coord) => {
        combinedBounds?.extend(coord as maplibregl.LngLatLike)
      })
    } else {
      combinedBounds = photoBounds
    }
  }

  return combinedBounds && !combinedBounds.isEmpty() ? combinedBounds : null
}

const isFollowLocationCameraActive = () => mapRef.value?.isFollowLocationActive?.() === true

const expandBoundsForCurrentLocationMargin = (bounds: maplibregl.LngLatBounds) => {
  const sw = bounds.getSouthWest()
  const ne = bounds.getNorthEast()
  const lngMargin = Math.max(
    Math.abs(ne.lng - sw.lng) * COLLECTION_BOUNDS_LOCATION_MARGIN_RATIO,
    COLLECTION_BOUNDS_LOCATION_MIN_MARGIN_DEGREES,
  )
  const latMargin = Math.max(
    Math.abs(ne.lat - sw.lat) * COLLECTION_BOUNDS_LOCATION_MARGIN_RATIO,
    COLLECTION_BOUNDS_LOCATION_MIN_MARGIN_DEGREES,
  )

  return new maplibregl.LngLatBounds(
    [sw.lng - lngMargin, Math.max(-90, sw.lat - latMargin)],
    [ne.lng + lngMargin, Math.min(90, ne.lat + latMargin)],
  )
}

const isCurrentLocationViewportOutsideCollection = (
  viewport: MapViewportSnapshot,
  source: string,
) => {
  const location = currentLocation.value
  if (!location) return false
  if (Date.now() - location.timestamp > CURRENT_LOCATION_VIEWPORT_MAX_AGE_MS) return false

  const contentBounds = getWorkspaceContentBounds()
  if (!contentBounds) return false

  const locationLngLat = new maplibregl.LngLat(location.lon, location.lat)
  const expandedContentBounds = expandBoundsForCurrentLocationMargin(contentBounds)
  if (expandedContentBounds.contains(locationLngLat)) return false

  const viewportCenter = new maplibregl.LngLat(viewport.center[0], viewport.center[1])
  const distanceToCurrentLocationM = viewportCenter.distanceTo(locationLngLat)
  const maxMatchDistanceM = Math.max(
    CURRENT_LOCATION_VIEWPORT_MATCH_MIN_DISTANCE_M,
    (location.accuracyM ?? 0) * CURRENT_LOCATION_VIEWPORT_MATCH_ACCURACY_MULTIPLIER,
  )
  const isCurrentLocationViewport = distanceToCurrentLocationM <= maxMatchDistanceM

  if (isCurrentLocationViewport) {
    logService.info(LOG_CATEGORIES.MAP, 'Detected current-location viewport outside collection', {
      source,
      workspaceId: workspaceStore.clientId,
      route: route.fullPath,
      viewportCenter: viewport.center,
      currentLocation: {
        lat: location.lat,
        lon: location.lon,
        accuracyM: location.accuracyM,
        source: location.source,
        timestamp: location.timestamp,
      },
      distanceToCurrentLocationM,
      maxMatchDistanceM,
      contentBounds: contentBounds.toArray(),
      expandedContentBounds: expandedContentBounds.toArray(),
    })
  }

  return isCurrentLocationViewport
}

const initialWorkspaceBounds = computed<LngLatBoundsLike | null>(() => {
  // Only the actual collections overview uses its shared saved bounds. Pinned also has no
  // route workspace ID, but it is a workspace-backed content surface with its own camera.
  // The overview bounds let that surface start without a fallback auto-zoom flying in.
  if (route.name === 'workspaces' && !route.params.workspaceId) {
    const saved = settingsService.getSetting<{ sw: [number, number]; ne: [number, number] }>(
      UI_SETTING_KEYS.mapCollectionsOverviewBounds,
    )
    if (saved) return [saved.sw, saved.ne]
    return null
  }

  if (getSavedViewportForActiveWorkspace('initial-bounds', false)) {
    return null
  }

  if (!shouldAutoMoveWorkspaceContent()) return null
  if (uiStore.isInWorkspacesOverview) return null
  const bounds = getWorkspaceContentBounds()
  if (!bounds) return null
  const [sw, ne] = bounds.toArray()
  return [sw as LngLatLike, ne as LngLatLike]
})

const getRouteWorkspaceId = () => {
  const workspaceId = route.params.workspaceId
  return typeof workspaceId === 'string' ? workspaceId : null
}

const getActiveWorkspaceViewportKey = () => workspaceStore.clientId || getRouteWorkspaceId()

const getSavedViewportForActiveWorkspace = (source: string, shouldLog = true) => {
  if (!isTauri()) return undefined

  const workspaceId = getActiveWorkspaceViewportKey()
  if (!workspaceId) {
    if (shouldLog) {
      logService.info(LOG_CATEGORIES.MAP, 'No active collection for saved viewport lookup', {
        source,
        route: route.fullPath,
      })
    }
    return undefined
  }

  const viewport = uiStatePersistenceService.getMapViewport(workspaceId)
  if (shouldLog) {
    logService.info(LOG_CATEGORIES.MAP, 'Saved collection viewport lookup', {
      source,
      workspaceId,
      route: route.fullPath,
      hasSavedViewport: Boolean(viewport),
      center: viewport?.center,
      zoom: viewport?.zoom,
      pitch: viewport?.pitch,
      bearing: viewport?.bearing,
    })
  }
  return viewport
}

const restoreSavedViewportIfAvailable = (
  source: string,
  options: { restoreFollowScale?: boolean } = {},
) => {
  const savedViewport = getSavedViewportForActiveWorkspace(source)
  if (!savedViewport) return false

  if (isFollowLocationCameraActive()) {
    uiStore.disableAutoViewportAdaptation()
    if (options.restoreFollowScale) {
      logService.info(LOG_CATEGORIES.MAP, 'Restoring saved collection scale for follow mode', {
        source,
        workspaceId: getActiveWorkspaceViewportKey(),
        zoom: savedViewport.zoom,
        pitch: savedViewport.pitch,
        bearing: savedViewport.bearing,
      })
      mapRef.value?.restoreViewportWithoutCenter(savedViewport)
    }
    return true
  }

  if (isCurrentLocationViewportOutsideCollection(savedViewport, source)) {
    logService.info(
      LOG_CATEGORIES.MAP,
      'Skipping saved current-location viewport outside collection',
      {
        source,
        workspaceId: getActiveWorkspaceViewportKey(),
        route: route.fullPath,
        center: savedViewport.center,
        zoom: savedViewport.zoom,
        pitch: savedViewport.pitch,
        bearing: savedViewport.bearing,
      },
    )
    return false
  }

  // A saved camera is user intent. Disable automatic refits that would otherwise
  // run when toolbars/refs finish mounting and overwrite the restored view.
  uiStore.disableAutoViewportAdaptation()
  restoredSavedViewportWorkspaceId.value = getActiveWorkspaceViewportKey()
  logService.info(LOG_CATEGORIES.MAP, 'Restoring saved collection viewport', {
    source,
    workspaceId: getActiveWorkspaceViewportKey(),
    center: savedViewport.center,
    zoom: savedViewport.zoom,
    pitch: savedViewport.pitch,
    bearing: savedViewport.bearing,
  })
  mapRef.value?.restoreViewport(savedViewport)
  return true
}

const shouldSkipAutoContentFitAfterSavedRestore = (source: string) => {
  const workspaceId = getActiveWorkspaceViewportKey()
  const shouldSkip = Boolean(
    isTauri() &&
      workspaceId &&
      restoredSavedViewportWorkspaceId.value === workspaceId &&
      !uiStore.isInWorkspacesOverview,
  )

  if (shouldSkip) {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping content fit after saved viewport restore', {
      source,
      workspaceId,
      route: route.fullPath,
      restoredSavedViewportWorkspaceId: restoredSavedViewportWorkspaceId.value,
    })
  }

  return shouldSkip
}

const persistCurrentViewportSnapshot = (source: string) => {
  if (!isTauri()) return
  const workspaceId = workspaceStore.clientId
  if (!workspaceId || uiStore.isInWorkspacesOverview) {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping lifecycle viewport snapshot persistence', {
      source,
      route: route.fullPath,
      workspaceId,
      isInWorkspacesOverview: uiStore.isInWorkspacesOverview,
    })
    return
  }

  const viewport = mapRef.value?.getViewportSnapshot?.()
  if (!viewport) {
    logService.info(LOG_CATEGORIES.MAP, 'No current map viewport snapshot available', {
      source,
      route: route.fullPath,
      workspaceId,
    })
    return
  }

  if (isCurrentLocationViewportOutsideCollection(viewport, source)) {
    logService.info(
      LOG_CATEGORIES.MAP,
      'Skipping lifecycle current-location viewport persistence',
      {
        source,
        route: route.fullPath,
        workspaceId,
        center: viewport.center,
        zoom: viewport.zoom,
        pitch: viewport.pitch,
        bearing: viewport.bearing,
      },
    )
    return
  }

  logService.info(LOG_CATEGORIES.MAP, 'Persisting lifecycle collection viewport', {
    source,
    workspaceId,
    route: route.fullPath,
    center: viewport.center,
    zoom: viewport.zoom,
    pitch: viewport.pitch,
    bearing: viewport.bearing,
  })
  uiStatePersistenceService.updateMapViewport(workspaceId, viewport)
  uiStatePersistenceService.flush()
}

// Determine when to show the New Workspace CTA on desktop toolbar:
// Handle creating a new workspace centrally to keep navigation/sync consistent
const createNewWorkspace = async () => {
  // Creating a collection is a write; block it up front while offline (view-only) instead of
  // letting the flow proceed and throw later at the WORKSPACE_CREATED enqueue.
  if (!(await requireOnlineModificationAccess('create collection'))) return
  if (!(await syncService.confirmSafeToLeave('New Collection', { destructive: true }))) {
    return
  }
  await syncService.resetAllStores()

  // Navigate to the new workspace's URL immediately using the freshly generated clientId
  const newWorkspaceId = useWorkspaceStore().clientId
  uiStore.setWorkspacesOverview(false)
  uiStore.expandMobileBottomPanel()
  router.push({ name: 'workspace-detail', params: { workspaceId: newWorkspaceId } })
}

// Event handlers for custom map events
const handleZoomToSearchWorkspaces = (event: CustomEvent) => {
  const raw = event.detail as unknown
  const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object'
  const force =
    isObj(raw) && 'force' in raw ? Boolean((raw as Record<string, unknown>).force) : false
  const animate =
    isObj(raw) && 'animate' in raw ? Boolean((raw as Record<string, unknown>).animate) : false
  const updateOverviewBounds =
    !isObj(raw) || !('updateOverviewBounds' in raw)
      ? true
      : Boolean((raw as Record<string, unknown>).updateOverviewBounds)
  const payload = isObj(raw) && 'bounds' in raw ? (raw as Record<string, unknown>).bounds : raw

  if (!payload) return

  const mapBounds = new maplibregl.LngLatBounds()
  const sw = (payload as { sw: [number, number] }).sw
  const ne = (payload as { ne: [number, number] }).ne
  mapBounds.extend(sw)
  mapBounds.extend(ne)
  const isExplicitFit = uiStore.mapNavigationIntent === 'explicit'

  if (updateOverviewBounds) {
    // Cache so mapLoaded / lastSearchWorkspacesBounds watchers can zoom later if the map component
    // isn't ready yet, and persist so the overview can start at the right location on next load.
    explicitlyFitOverviewBounds = null
    lastSearchWorkspacesBounds.value = mapBounds
    if (uiStore.isInWorkspacesOverview) {
      settingsService.saveSetting(UI_SETTING_KEYS.mapCollectionsOverviewBounds, { sw, ne })
    }
  }

  if (
    uiStore.isInWorkspacesOverview &&
    !shouldAutoMoveOverviewContent() &&
    uiStore.mapNavigationIntent !== 'explicit'
  ) {
    finalizeOverviewContentMovement()
    return
  }

  if (!mapRef.value) return

  // Respect auto-adaptation unless an explicit user action forces the zoom
  if (!uiStore.autoViewportAdaptationEnabled && !force) return

  const padding = calculateViewportPadding()
  const isValidPadding = Object.values(padding).every(
    (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
  )
  if (updateOverviewBounds) {
    explicitlyFitOverviewBounds = isExplicitFit ? mapBounds : null
  }
  mapRef.value.zoomToBounds(mapBounds, {
    padding: isValidPadding ? padding : 50,
    maxZoom: 14,
    animate: isExplicitFit || animate ? true : undefined,
  })

  if (isExplicitFit) {
    uiStore.consumeMapNavigationIntent('explicit')
  }
}

const handleZoomToContent = () => {
  restoredSavedViewportWorkspaceId.value = null
  const isExplicitFit = uiStore.mapNavigationIntent === 'explicit'
  zoomToContent(isExplicitFit ? { animate: true } : undefined, {
    force: true,
    source: 'zoom-to-content-event',
  })
  if (isExplicitFit) {
    uiStore.consumeMapNavigationIntent('explicit')
  }
}

const handleTourAdded = (event: Event) => {
  const detail = (event as CustomEvent<{ tourId?: string }>).detail
  const tourId = detail?.tourId
  if (!tourId) return
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return
  if (tour.tracks.length > 0 || tour.waypoints.length > 0) {
    return
  }

  workspaceStore.selectTour(tourId)
  uiStore.showTourDetailPanel()
}

// Set up event listeners for custom map events with automatic cleanup
useEventListener(window, 'zoom-to-search-workspaces', handleZoomToSearchWorkspaces as EventListener)
useEventListener(window, 'zoom-to-content', handleZoomToContent as EventListener)
useEventListener(window, 'tour-added', handleTourAdded as EventListener)
useEventListener(window, ROUTE_MODE_LEAVE_REQUEST_EVENT, handleRouteModeLeaveRequest)
useEventListener(document, 'visibilitychange', () => {
  if (!isTauri()) return

  if (document.visibilityState === 'hidden') {
    persistCurrentViewportSnapshot('visibility-hidden')
    return
  }

  if (document.visibilityState === 'visible') {
    logService.info(LOG_CATEGORIES.MAP, 'Preserving current viewport after visibility resume', {
      route: route.fullPath,
      workspaceId: workspaceStore.clientId,
      routeWorkspaceId: getRouteWorkspaceId(),
      reason: 'passive location refresh does not move the map',
    })
  }
})
useEventListener(window, 'expand-tour-profile', () => {
  // Profile box starts collapsed on desktop; user can expand it manually.
  if (isSmallScreen.value) trackProfileCollapsed.value = false
})

// Disable auto viewport adaptation once the user interacts with the map (pan/zoom)
useEventListener(window, 'user-map-interaction', () => {
  isUserMapInteractionActive = false
  mapInteractionEpoch += 1
  uiStore.disableAutoViewportAdaptation()
  resetSelectedTourProfileLegendInteraction()
  resetTrackProfileLegendInteraction()
})

// Open Add Waypoint modal
useEventListener(window, 'open-add-waypoint-modal', async () => {
  if (!(await requirePermission('edit_waypoint'))) return
  addWaypointModalScope.value = 'tour-waypoint'
  isAddWaypointModalOpen.value = true
})

useEventListener(window, 'open-add-poi-modal', async () => {
  if (!(await requirePermission('edit_waypoint'))) return
  addWaypointModalScope.value = 'workspace-poi'
  isAddWaypointModalOpen.value = true
})

useEventListener(window, 'start-track-drawing', async (event: Event) => {
  if (!(await requirePermission('edit_track'))) return
  const detail = (
    event as CustomEvent<{
      tourId?: string
      scope?: RoutingScope
      discardTourOnCancel?: boolean
    }>
  ).detail
  const routingScope = resolveRoutingScopeForTour(detail?.tourId ?? null, detail?.scope)
  const targetTourId = detail?.tourId ?? resolveTargetTourId(routingScope)
  if (!(await confirmAdditionalTrackWarning(targetTourId, routingScope))) return
  discardRoutingTourOnCancel.value = detail?.discardTourOnCancel === true
  await handleStartTrackDrawing(targetTourId, routingScope)
})

useEventListener(window, 'routecamp-e2e-track-edit-insert', (event: Event) => {
  const detail = (event as CustomEvent<{ insertIndex?: number; lat?: number; lon?: number }>).detail
  if (
    typeof detail?.insertIndex !== 'number' ||
    typeof detail.lat !== 'number' ||
    typeof detail.lon !== 'number'
  ) {
    return
  }

  void handleTrackEditInsertDrop(detail.insertIndex, detail.lat, detail.lon)
})

useEventListener(window, 'routecamp-e2e-enter-track-edit', (event: Event) => {
  const detail = (event as CustomEvent<{ tourId?: string; trackClientId?: string }>).detail
  if (!detail?.tourId || !detail.trackClientId) {
    return
  }

  enterTrackEditModeForTrack(detail.tourId, detail.trackClientId)
})

useEventListener(window, 'header-action-create-workspace', () => {
  void createNewWorkspace()
})

useEventListener(window, 'header-action-import-files', () => {
  emit('trigger-gpx-upload')
})

useEventListener(window, 'header-action-new-workspace', async () => {
  // New collection + immediate route drawing are both writes — blocked while offline (view-only).
  if (!(await requireOnlineModificationAccess('create collection'))) return
  if (!(await syncService.confirmSafeToLeave('New Collection', { destructive: true }))) return
  await syncService.resetAllStores()

  const ws = useWorkspaceStore()
  const newWorkspaceId = ws.clientId
  uiStore.setWorkspacesOverview(false)
  await router.push({ name: 'workspace-detail', params: { workspaceId: newWorkspaceId } })

  const tourId = ws.addLocalDraftTour()

  if (tourId) {
    await nextTick()
    window.dispatchEvent(
      new CustomEvent('start-track-drawing', {
        detail: { tourId, discardTourOnCancel: true },
      }),
    )
  }
})

useEventListener(window, 'routecamp-e2e-track-drawing-click', (event: Event) => {
  const detail = (event as CustomEvent<{ lat?: number; lon?: number }>).detail
  if (typeof detail?.lat !== 'number' || typeof detail.lon !== 'number') {
    return
  }

  void trackDrawing.handleDrawingClick({
    lngLat: {
      lat: detail.lat,
      lng: detail.lon,
    },
  } as never)
})

useEventListener(window, 'routecamp-e2e-route-point-dragend', (event: Event) => {
  const detail = (event as CustomEvent<{ routePointIndex?: number; lat?: number; lon?: number }>)
    .detail
  if (
    typeof detail?.routePointIndex !== 'number' ||
    typeof detail.lat !== 'number' ||
    typeof detail.lon !== 'number' ||
    !uiStore.editingTrackTourId
  ) {
    return
  }

  void handleRoutePointDragend(
    uiStore.editingTrackTourId,
    detail.routePointIndex,
    detail.lat,
    detail.lon,
  )
})

// Start waypoint placement (from manual or search)
const executeWaypointFlyTo = (
  target: { lat: number; lon: number; zoom?: number },
  zoomHint?: number,
) => {
  if (!mapRef.value) return
  const zoom = target.zoom || zoomHint || 16
  requestAnimationFrame(() => {
    mapRef.value!.flyTo([target.lon, target.lat], { zoom })
    requestAnimationFrame(() => {
      isWaypointPlacementActive.value = true
    })
  })
}

const flyToMapLocation = (
  target: { lat: number; lon: number; zoom?: number },
  zoomHint?: number,
) => {
  if (!mapLoaded.value || !mapRef.value) {
    pendingMapFlyTo.value = target
    return
  }

  requestAnimationFrame(() => {
    mapRef.value!.flyTo([target.lon, target.lat], { zoom: target.zoom || zoomHint || 16 })
  })
}

const closeAddWaypointModal = () => {
  isAddWaypointModalOpen.value = false
  addWaypointModalScope.value = 'tour-waypoint'
}

const stageWaypointPlacement = (detail: Record<string, unknown>) => {
  const flyTo = detail.flyTo as { lat: number; lon: number; zoom?: number } | undefined
  waypointPlacementScope.value =
    detail.scope === 'workspace-poi' ? 'workspace-poi' : 'tour-waypoint'
  pendingWaypointName.value = typeof detail.name === 'string' ? detail.name : null
  pendingWaypointType.value = typeof detail.type === 'string' ? detail.type : null
  pendingWaypointDescription.value =
    typeof detail.description === 'string' ? detail.description : null
  closeAddWaypointModal()
  if (isSmallScreen.value) {
    uiStore.collapseMobileBottomPanel()
  }
  if (flyTo) {
    if (!mapLoaded.value || !mapRef.value) {
      pendingWaypointFlyTo.value = flyTo
    } else {
      executeWaypointFlyTo(flyTo, typeof detail.zoom === 'number' ? detail.zoom : undefined)
    }
  } else {
    isWaypointPlacementActive.value = true
  }
}

useEventListener(window, 'start-waypoint-placement', (async (e: Event) => {
  if (!(await requirePermission('edit_waypoint'))) return
  stageWaypointPlacement(((e as CustomEvent).detail || {}) as Record<string, unknown>)
}) as EventListener)

useEventListener(window, 'smart-search-open-location', (async (event: Event) => {
  const detail = ((event as CustomEvent<SmartSearchLocationEventDetail>).detail ??
    null) as SmartSearchLocationEventDetail | null
  if (!detail?.flyTo) return

  if (uiStore.isTrackDrawingActive) {
    smartSearchPin.value = null
    flyToMapLocation(detail.flyTo)
    await addDrawingRoutePoint(detail.flyTo.lat, detail.flyTo.lon)
    return
  }

  pendingWaypointName.value = null
  pendingWaypointType.value = null
  pendingWaypointDescription.value = null
  waypointPlacementScope.value = 'tour-waypoint'
  isWaypointPlacementActive.value = false
  // Smart Search place results only move the map for now; avoid dropping a marker.
  smartSearchPin.value = null
  flyToMapLocation(detail.flyTo)
}) as EventListener)

const removeSmartSearchPin = () => {
  smartSearchPin.value = null
}

useEventListener(document, 'keydown', (e: KeyboardEvent) => {
  const key = typeof e.key === 'string' ? e.key.toLowerCase() : ''
  const target = e.target instanceof HTMLElement ? e.target : null
  const isTyping =
    target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable

  if (key === 'm' && !isTyping && !e.repeat) {
    e.preventDefault()
    uiStore.setMapElementsHiddenByKey(true)
    return
  }

  if (key === 'k' && !isTyping && !e.repeat && !e.ctrlKey && !e.metaKey) {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('open-static-site', { detail: 'keyboard-shortcuts' }))
  }
})

useEventListener(document, 'keyup', (e: KeyboardEvent) => {
  const key = typeof e.key === 'string' ? e.key.toLowerCase() : ''
  if (key === 'm') {
    uiStore.setMapElementsHiddenByKey(false)
  }
})

const onMapLoaded = (map: maplibregl.Map) => {
  mapInstance.value = map
  mapLoaded.value = true

  logService.info(LOG_CATEGORIES.MAP, 'Workspace map loaded', {
    route: route.fullPath,
    workspaceId: workspaceStore.clientId,
    routeWorkspaceId: getRouteWorkspaceId(),
    isTauri: isTauri(),
    isInWorkspacesOverview: uiStore.isInWorkspacesOverview,
    autoViewportAdaptationEnabled: uiStore.autoViewportAdaptationEnabled,
  })

  // Restore the saved camera before any initial fit-to-content logic can run.
  // Wait for DOM updates to complete before applying camera changes.
  nextTick(() => {
    if (!shouldAutoMoveWorkspaceContent()) {
      return
    }

    if (restoreSavedViewportIfAvailable('map-loaded')) {
      return
    }

    const skipAnimation = initialWorkspaceBounds.value !== null
    logService.info(LOG_CATEGORIES.MAP, 'Fitting collection content on map load', {
      route: route.fullPath,
      workspaceId: workspaceStore.clientId,
      routeWorkspaceId: getRouteWorkspaceId(),
      skipAnimation,
      hasInitialBounds: initialWorkspaceBounds.value !== null,
    })
    zoomToContent(skipAnimation ? { duration: 0, linear: true } : undefined, {
      source: 'map-loaded',
    })
    finalizeWorkspaceContentMovement()
  })

  // If there is a pending deep link tour to focus, try now
  nextTick(() => tryFocusDeepLinkedTour())

  // Execute any pending waypoint flyTo (e.g., nominatim result clicked before map loaded)
  if (pendingWaypointFlyTo.value) {
    const target = pendingWaypointFlyTo.value
    pendingWaypointFlyTo.value = null
    if (target) {
      executeWaypointFlyTo(target)
    }
  }

  if (pendingMapFlyTo.value) {
    const target = pendingMapFlyTo.value
    pendingMapFlyTo.value = null
    if (target) {
      flyToMapLocation(target)
    }
  }
}

// Persist the current map viewport per collection (Tauri only) so it can be
// restored when the collection is reopened. Debounced to avoid hammering
// localStorage on every frame of a pan/zoom gesture.
const persistMapViewport = useDebounceFn((viewport: MapViewportSnapshot) => {
  const workspaceId = workspaceStore.clientId
  if (!workspaceId || uiStore.isInWorkspacesOverview) return
  if (isCurrentLocationViewportOutsideCollection(viewport, 'map-move')) {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping current-location viewport persistence', {
      workspaceId,
      route: route.fullPath,
      center: viewport.center,
      zoom: viewport.zoom,
      pitch: viewport.pitch,
      bearing: viewport.bearing,
    })
    return
  }

  logService.info(LOG_CATEGORIES.MAP, 'Persisting collection viewport', {
    workspaceId,
    route: route.fullPath,
    center: viewport.center,
    zoom: viewport.zoom,
    pitch: viewport.pitch,
    bearing: viewport.bearing,
  })
  uiStatePersistenceService.updateMapViewport(workspaceId, viewport)
}, 500)

const onMapMove = (payload: MapViewportSnapshot & { userInitiated?: boolean }) => {
  if (isOfflineMapAreaSelector.value) return

  if (payload.userInitiated && navigationStore.isActive) {
    navigationStore.setFollowMap(false)
  }

  if (!isTauri()) return
  if (!workspaceStore.clientId || uiStore.isInWorkspacesOverview) {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping viewport persistence outside collection detail', {
      route: route.fullPath,
      workspaceId: workspaceStore.clientId,
      isInWorkspacesOverview: uiStore.isInWorkspacesOverview,
      userInitiated: payload.userInitiated,
    })
    return
  }

  if (!payload.userInitiated) {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping non-user viewport persistence', {
      route: route.fullPath,
      workspaceId: workspaceStore.clientId,
      center: payload.center,
      zoom: payload.zoom,
      pitch: payload.pitch,
      bearing: payload.bearing,
    })
    return
  }

  persistMapViewport(payload)
}

const onMapPanStart = () => {
  if (isOfflineMapAreaSelector.value) return
  if (uiStore.isSmartSearchOpen) {
    uiStore.closeSmartSearch()
  }
}

const onMapInteractionStart = () => {
  if (isOfflineMapAreaSelector.value) return
  isUserMapInteractionActive = true
  mapInteractionEpoch += 1
  uiStore.disableAutoViewportAdaptation()
}

const onCurrentLocation = (location: CurrentLocationUpdate) => {
  currentLocation.value = location
}

const buildNavigationAnalysis = (tour: Tour, track: Track): TrackProfileAnalysis => {
  const color = TrackColorService.getTourColor(tour.clientId, track.color)
  return buildTrackProfileAnalyses(track.segments ?? [], color).gradient
}

const getNavigableTracks = (tour: Tour): Track[] =>
  tour.tracks.filter((track) => buildNavigationAnalysis(tour, track).points.length > 1)

const selectedNavigationTour = computed(() => {
  const tourId = workspaceStore.selectedTourId
  if (!tourId) return null

  const tour = workspaceStore.getTourById(tourId)
  if (!tour || getNavigableTracks(tour).length === 0) return null

  return tour
})
const selectedRouteActionTourId = computed(
  () => selectedNavigationTour.value?.clientId ?? selectedTourProfileTour.value?.clientId ?? null,
)

const centerNavigationMap = (options: { immediate?: boolean } = {}) => {
  const location = navigationStore.currentLocation
  const map = mapInstance.value
  if (!location || !map || !mapLoaded.value) return

  map.easeTo({
    center: [location.lon, location.lat],
    duration: options.immediate ? 0 : 500,
    essential: true,
    zoom: Math.max(map.getZoom(), 16),
  })
}

const startNavigationForTrack = async (tour: Tour, track: Track) => {
  if (
    (uiStore.isTrackDrawingActive || uiStore.isTrackEditMode) &&
    !(await requestRouteModeLeave())
  ) {
    return
  }

  const analysis = buildNavigationAnalysis(tour, track)
  if (analysis.points.length < 2) {
    showError('This route does not have enough geometry for navigation.')
    return
  }

  navigationTrackPickerTour.value = null
  uiStore.collapseMobileBottomPanel()
  isMobileToolbarVisible.value = false
  trackProfileCollapsed.value = true

  await navigationStore.start({
    tourId: tour.clientId,
    trackClientId: track.clientId,
    name: track.name?.trim() || getEffectiveTourName(tour) || 'Navigation',
    analysis,
  })

  centerNavigationMap({ immediate: true })
}

const handleNavigateTour = async (tourId: string) => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return

  const navigableTracks = getNavigableTracks(tour)
  if (navigableTracks.length === 0) {
    showError('This route does not have enough geometry for navigation.')
    return
  }

  if (navigableTracks.length === 1) {
    await startNavigationForTrack(tour, navigableTracks[0])
    return
  }

  navigationTrackPickerTour.value = tour
}

const handleNavigationEnd = async () => {
  await navigationStore.end()
  isMobileToolbarVisible.value = true
}

const handleNavigationRecenter = () => {
  navigationStore.setFollowMap(true)
  centerNavigationMap({ immediate: true })
}

const handleOpenSelectedRouteProfile = () => {
  // On mobile the profile now lives inside the unified Route sheet's Profile tab.
  if (isSmallScreen.value) {
    if (!hasMobileTourDetailToggle.value) return
    routeSheetTab.value = 'profile'
    showTourDetailPanel({ revealOnMobile: true })
    return
  }
  if (selectedTourProfileAnalysis.value.points.length <= 1) return
  isSelectedTourProfileOpen.value = true
  selectedTourProfileOpenKey.value += 1
  trackProfileCollapsed.value = true
}

const handleCloseSelectedRouteProfile = () => {
  if (isSelectedRouteSegmentActive.value) {
    clearSelectedRouteSegmentSelection()
  }

  isSelectedTourProfileOpen.value = false
}

const handleOpenSelectedRouteDetails = () => {
  if (!hasMobileTourDetailToggle.value) return
  routeSheetTab.value = 'details'
  showTourDetailPanel({ revealOnMobile: true })
}

// Inline rename from the Route sheet header (mirrors TourDetailPanel.saveTourName).
const handleRouteSheetRename = async (name: string) => {
  const trimmed = name.trim()
  if (!trimmed) return
  if (!(await requirePermission('edit_track'))) return
  if (uiStore.isAllActivitiesView) {
    const tour = libraryStore.detailTour
    if (!tour || trimmed === tour.name) return
    await libraryStore.updateTour(tour.clientId, { name: trimmed })
  } else {
    const tour = workspaceStore.selectedTour
    if (!tour || trimmed === getEffectiveTourName(tour)) return
    await workspaceStore.updateTourCollectionName(tour.clientId, trimmed)
  }
}

const handleRouteSheetRenameRequest = () => {
  const tour = uiStore.isAllActivitiesView ? libraryStore.detailTour : workspaceStore.selectedTour
  if (!tour) return
  openMobileRenameSheet({
    title: `Rename ${isActivityTour(tour) ? 'activity' : 'route'}`,
    currentName: getEffectiveTourName(tour),
    submit: handleRouteSheetRename,
  })
}

const handleRouteSheetNameReset = async () => {
  if (!(await requirePermission('edit_track'))) return
  if (uiStore.isAllActivitiesView) return

  const tour = workspaceStore.selectedTour
  if (!tour) return
  await workspaceStore.updateTourCollectionName(tour.clientId, null)
}

// The Route sheet owns its own share modal (the toolbar's instance is inside a hidden
// subtree while the sheet is open, so its modal would be invisible).
const isRouteShareModalOpen = ref(false)
const handleShareCollectionFromRouteSheet = () => {
  isRouteShareModalOpen.value = true
}
const canShareWorkspaceCollection = computed(
  () =>
    !uiStore.isAllActivitiesView && workspaceStore.currentWorkspace?._createEventPushed === true,
)

// Mobile tab bar destinations. Library/Collections open their list drawer (collapsed,
// Map-first); Pinned opens the system collection route.
const handleMobileCollectionsTab = () => {
  selectedMobileNavTab.value = 'collections'
  uiStore.closeSmartSearch()
  isCanvasSheetOpen.value = false
  if (isCollectionContextRoute.value) {
    uiStore.setMobileMapView(false)
    const openMobileCampTab = mobileToolbarRef.value?.openMobileCampTab
    if (typeof openMobileCampTab === 'function') {
      void openMobileCampTab()
    }
    void router.push({ name: 'workspaces' })
    return
  }
  if (hasLastCollectionContext()) {
    const lastCollectionTarget = getLastCollectionRouteTarget()
    if (lastCollectionTarget.name === 'workspaces') {
      const openMobileCampTab = mobileToolbarRef.value?.openMobileCampTab
      if (typeof openMobileCampTab === 'function') {
        void openMobileCampTab()
      }
      void router.push(lastCollectionTarget)
      return
    }
    uiStore.setMobileMapView(false)
    uiStore.beginMapNavigation('map')
    void router.push(lastCollectionTarget)
    return
  }
  const openMobileCampTab = mobileToolbarRef.value?.openMobileCampTab
  if (typeof openMobileCampTab === 'function') {
    void openMobileCampTab()
  }
  void router.push({ name: 'workspaces' })
}
const handleMobileLibraryTab = () => {
  selectedMobileNavTab.value = 'library'
  uiStore.closeSmartSearch()
  isCanvasSheetOpen.value = false
  void mobileToolbarRef.value?.openMobileLibraryTab()
  void router.push({ name: 'library' })
}
const handleMobilePinnedTab = async () => {
  selectedMobileNavTab.value = 'pinned'
  uiStore.closeSmartSearch()
  uiStore.setMobileMapView(false)
  isCanvasSheetOpen.value = false
  mobileToolbarRef.value?.openMobilePinnedTab()
  if (route.name === 'pinned') {
    if (!authStore.isAuthenticated) return
    await syncService.loadPinnedWorkspace()
    zoomToContentWhenReady()
    return
  }
  void router.push({ name: 'pinned' })
}

const mobileTabBarActiveTab = computed<MobileNavTab>(() => {
  if (uiStore.isProfileSheetOpen) return null
  if (uiStore.isSmartSearchOpen) return 'search'
  if (isCanvasSheetOpen.value) return null
  if (selectedMobileNavTab.value) return selectedMobileNavTab.value
  if (route.name === 'pinned') return 'pinned'
  return mobileToolbarRef.value?.activeMobileTab ?? selectedMobileNavTab.value
})

watch(
  () => route.name,
  (routeName) => {
    if (routeName === 'pinned') {
      selectedMobileNavTab.value = 'pinned'
      return
    }
    if (routeName === 'library') {
      selectedMobileNavTab.value = 'library'
      return
    }
    if (routeName === 'workspaces') {
      selectedMobileNavTab.value = 'collections'
    }
  },
  { immediate: true },
)

watch(
  () => uiStore.isMobileMapView,
  (isMobileMapView) => {
    if (isMobileMapView) selectedMobileNavTab.value = null
  },
)

watch(
  () => navigationStore.currentLocation?.timestamp,
  () => {
    if (!navigationStore.isActive || !navigationStore.followMap) return
    centerNavigationMap()
  },
)

const openCreateTourModal = async () => {
  if (!(await requirePermission('edit_tour'))) return
  const tourId = await workspaceStore.addEmptyTour({ emitAddedEvent: false })
  workspaceStore.selectTour(tourId)
  uiStore.showTourDetailPanel()
}

const handleMakeWorkspaceOffline = async (workspaceId: string) => {
  if (!isTauri()) return

  try {
    const loaded = await syncService.loadRemoteWorkspace(workspaceId, false)
    if (!loaded) {
      return
    }

    isOfflineDialogOpen.value = true
  } catch (error) {
    showError(
      `Failed to prepare offline download: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const closeMapTrackContextMenu = (options: { preserveHighlight?: boolean } = {}) => {
  const tourId = mapTrackContextMenu.value.tourId
  if (!options.preserveHighlight && mapTrackContextMenu.value.visible && tourId) {
    if (workspaceStore.activelyHoveredTourId === tourId) {
      workspaceStore.setActivelyHoveredTourId(null)
    }
    if (workspaceStore.hoveredTourId === tourId) {
      workspaceStore.setHoveredTourId(workspaceStore.selectedTourId ?? null)
    }
  }
  mapTrackContextMenu.value.visible = false
}

const closeMapTrackOverlapMenu = () => {
  mapTrackOverlapMenu.value.visible = false
  mapTrackOverlapMenu.value.candidates = []
}

const closeMapPOIContextMenu = (options: { preserveHighlight?: boolean } = {}) => {
  const poiId = mapPOIContextMenu.value.poiId
  if (
    !options.preserveHighlight &&
    mapPOIContextMenu.value.visible &&
    poiId &&
    uiStore.hoveredWaypointId === `poi_${poiId}`
  ) {
    uiStore.setHoveredWaypoint(null)
  }
  mapPOIContextMenu.value.visible = false
}

const closeMapWorkspaceContextMenu = (options: { preserveHighlight?: boolean } = {}) => {
  const workspaceId = mapWorkspaceContextMenu.value.workspaceId
  if (
    !options.preserveHighlight &&
    mapWorkspaceContextMenu.value.visible &&
    workspaceId &&
    searchWorkspaceStore.hoveredWorkspaceId === workspaceId
  ) {
    searchWorkspaceStore.setHoveredWorkspaceId(null)
  }
  mapWorkspaceContextMenu.value.visible = false
}

const closeMapEntityContextMenus = () => {
  closeMapTrackContextMenu()
  closeMapTrackOverlapMenu()
  closeMapPOIContextMenu()
  closeMapWorkspaceContextMenu()
  closeLibraryMapContextMenu()
}

const getTrackContext = (tourId: string, trackClientId: string) => {
  const tour = workspaceStore.getTourById(tourId)
  if (!tour) return null
  const trackIndex = tour.tracks.findIndex((track) => track.clientId === trackClientId)
  if (trackIndex < 0) return null
  const track = tour.tracks[trackIndex]
  return { tour, track, trackIndex }
}

const selectedMapContextTrack = computed(() => {
  const { tourId, trackClientId } = mapTrackContextMenu.value
  if (!tourId || !trackClientId) return null
  return getTrackContext(tourId, trackClientId)
})
const selectedMapContextTour = computed(() => selectedMapContextTrack.value?.tour ?? null)
const mapTrackOverlapItems = computed<ActionMenuItem[]>(() =>
  mapTrackOverlapMenu.value.candidates.flatMap((candidate) => {
    const tour = workspaceStore.getTourById(candidate.tourId)
    if (!tour) return []
    const isActivity = isActivityTour(tour)
    return [
      {
        key: candidate.tourId,
        label: getEffectiveTourName(tour) || 'Untitled route',
        icon: isActivity ? ActivityIcon : RouteIcon,
        iconStyle: { color: resolveTourAccentColor(tour) },
        onSelect: () => {
          closeMapTrackOverlapMenu()
          void handleWorkspaceTrackClick(candidate.tourId, candidate.trackClientId)
        },
      },
    ]
  }),
)
const selectedMapContextPOI = computed(() =>
  mapPOIContextMenu.value.poiId
    ? (workspaceStore.pois.find(
        (candidate) => candidate.clientId === mapPOIContextMenu.value.poiId,
      ) ?? null)
    : null,
)
const selectedMapContextWorkspace = computed(() =>
  mapWorkspaceContextMenu.value.workspaceId
    ? (searchWorkspaceStore.selectedWorkspaces.find(
        (workspace) => workspace.clientId === mapWorkspaceContextMenu.value.workspaceId,
      ) ?? null)
    : null,
)
const selectedMapContextWorkspaceCatalogItem = computed<WorkspaceSearchItem | null>(() =>
  mapWorkspaceContextMenu.value.workspaceId
    ? (searchWorkspaceStore.allWorkspaces.find(
        (workspace) => workspace.clientId === mapWorkspaceContextMenu.value.workspaceId,
      ) ?? null)
    : null,
)
const canEditMapContextTour = computed(() => hasPermission('edit_track'))
const isActivityTour = (tour: Tour) => !!tour.type || tour.tracks.some((track) => !!track.startTime)
const selectedMapContextTourIsActivity = computed(() => {
  const tour = selectedMapContextTour.value
  return !!tour && isActivityTour(tour)
})
const selectedMapContextTourDownloading = computed(() => {
  const tour = selectedMapContextTour.value
  return !!tour && mapTourDownloadingIds.value.has(tour.clientId)
})
const mapTourContextItems = computed<ActionMenuItem[]>(() => {
  const tour = selectedMapContextTour.value
  if (!tour) return []

  const items = buildTourPrimaryActionItems({
    tour,
    canEdit: canEditMapContextTour.value,
    isActivity: selectedMapContextTourIsActivity.value,
    isDownloading: selectedMapContextTourDownloading.value,
    onTogglePinned: () => {
      void toggleMapContextTourPinned()
    },
    onEditRoute: () => {
      void editMapContextTourRoute()
    },
    onDownload: openMapTourDownloadModal,
    onAddToCollection: openMapContextTourAddToCollection,
  })

  const isVisible = tour.visible !== false
  items.splice(1, 0, {
    key: 'toggle-visibility',
    label: isVisible ? 'Hide' : 'Unhide',
    icon: isVisible ? EyeOffIcon : EyeIcon,
    onSelect: () => {
      closeMapTrackContextMenu()
      void workspaceStore.setTourVisibility(tour.clientId, !isVisible)
    },
  })

  if (selectedMapContextTourIsActivity.value && canEditMapContextTour.value) {
    items.splice(2, 0, {
      key: 'edit-as-route',
      label: 'Edit as route',
      icon: RouteIcon,
      onSelect: () => {
        void editMapContextActivityAsRoute()
      },
    })
  }

  return items
})
const mapTourContextMenuStyle = computed(() =>
  positionMapContextMenu(
    mapTrackContextMenu.value.x,
    mapTrackContextMenu.value.y,
    mapTourContextItems.value.length,
  ),
)
const mapTrackOverlapMenuStyle = computed(() =>
  positionMapContextMenu(
    mapTrackOverlapMenu.value.x,
    mapTrackOverlapMenu.value.y,
    mapTrackOverlapItems.value.length,
  ),
)
const mapPOIContextItems = computed<ActionMenuItem[]>(() => {
  const poi = selectedMapContextPOI.value
  if (!poi) return []

  return [
    {
      key: 'toggle-pinned',
      label: poi.isPinned ? 'Unpin' : 'Pin',
      icon: poi.isPinned ? PinOffIcon : PinIcon,
      iconClass: poi.isPinned ? 'text-amber-600' : 'text-gray-400',
      onSelect: () => {
        void toggleMapContextPOIPinned()
      },
    },
    {
      key: 'toggle-visibility',
      label: uiStore.isPOIHidden(poi.clientId) ? 'Unhide' : 'Hide',
      icon: uiStore.isPOIHidden(poi.clientId) ? EyeIcon : EyeOffIcon,
      onSelect: () => {
        closeMapPOIContextMenu()
        uiStore.toggleSinglePOIVisibility(poi.clientId)
      },
    },
    {
      key: 'download',
      label: 'Download GPX',
      icon: Download,
      onSelect: () => {
        void downloadMapContextPOI()
      },
    },
    {
      key: 'copy',
      label: 'Add to collection',
      icon: SquarePlusIcon,
      onSelect: openMapContextPOIDetailForCopy,
    },
  ]
})
const mapPOIContextMenuStyle = computed(() =>
  positionMapContextMenu(
    mapPOIContextMenu.value.x,
    mapPOIContextMenu.value.y,
    mapPOIContextItems.value.length,
  ),
)
const isMapContextWorkspacePinned = computed(() => {
  const workspace = selectedMapContextWorkspaceCatalogItem.value
  if (!workspace) return false
  if (workspace.isPinned === true) return true
  if (workspace.isPinned === false) return false
  const hasOtherItems = (workspace.poiCount ?? 0) > 0 || (workspace.photoCount ?? 0) > 0
  return (
    workspace.tours.length > 0 &&
    !hasOtherItems &&
    workspace.tours.every((tour) => tour.isPinned === true)
  )
})
const mapWorkspaceContextItems = computed<ActionMenuItem[]>(() => {
  const workspace = selectedMapContextWorkspace.value
  if (!workspace) return []

  return [
    {
      key: 'pin',
      label: isMapContextWorkspacePinned.value ? 'Unpin' : 'Pin',
      icon: PinIcon,
      iconClass: isMapContextWorkspacePinned.value ? 'fill-amber-500 text-amber-500' : undefined,
      onSelect: () => {
        void toggleMapContextWorkspacePinned()
      },
    },
    {
      key: 'hide',
      label: 'Hide',
      icon: EyeOffIcon,
      onSelect: hideMapContextWorkspace,
    },
    {
      key: 'share',
      label: 'Share',
      icon: Share2Icon,
      onSelect: () => {
        void shareMapContextWorkspace()
      },
    },
  ]
})
const mapWorkspaceContextMenuStyle = computed(() =>
  positionMapContextMenu(
    mapWorkspaceContextMenu.value.x,
    mapWorkspaceContextMenu.value.y,
    mapWorkspaceContextItems.value.length,
  ),
)

const openMapTrackContextMenu = (detail: {
  tourId: string
  trackClientId: string
  trackIndex: number
  clientX: number
  clientY: number
}) => {
  if (uiStore.isTrackDrawingActive || uiStore.isTrackEditMode) return

  closeMapTrackOverlapMenu()
  closeMapPOIContextMenu()
  closeMapWorkspaceContextMenu()
  mapTrackContextMenu.value = {
    visible: true,
    tourId: detail.tourId,
    trackClientId: detail.trackClientId,
    trackIndex: detail.trackIndex,
    x: detail.clientX,
    y: detail.clientY,
  }
}

const openMapTrackOverlapMenu = (detail: {
  candidates: Array<{
    tourId: string
    trackClientId: string
    trackIndex: number
  }>
  clientX: number
  clientY: number
}) => {
  if (uiStore.isTrackDrawingActive || uiStore.isTrackEditMode || detail.candidates.length < 2) {
    return
  }

  closeMapTrackContextMenu()
  closeMapPOIContextMenu()
  closeMapWorkspaceContextMenu()
  closeLibraryMapContextMenu()
  mapTrackOverlapMenu.value = {
    visible: true,
    candidates: detail.candidates,
    x: detail.clientX,
    y: detail.clientY,
  }
}

const openMapPOIContextMenu = (detail: { poiId: string; clientX: number; clientY: number }) => {
  if (uiStore.isTrackDrawingActive || uiStore.isTrackEditMode) return

  closeMapTrackContextMenu()
  closeMapTrackOverlapMenu()
  closeMapWorkspaceContextMenu()
  mapPOIContextMenu.value = {
    visible: true,
    poiId: detail.poiId,
    x: detail.clientX,
    y: detail.clientY,
  }
}

const openMapWorkspaceContextMenu = (detail: {
  workspaceId: string
  clientX: number
  clientY: number
}) => {
  if (uiStore.isTrackDrawingActive || uiStore.isTrackEditMode) return

  closeMapTrackContextMenu()
  closeMapTrackOverlapMenu()
  closeMapPOIContextMenu()
  closeLibraryMapContextMenu()
  searchWorkspaceStore.setHoveredWorkspaceId(detail.workspaceId)
  mapWorkspaceContextMenu.value = {
    visible: true,
    workspaceId: detail.workspaceId,
    x: detail.clientX,
    y: detail.clientY,
  }
}

const updateMapContextWorkspacePinnedState = (isPinned: boolean) => {
  const workspace = selectedMapContextWorkspaceCatalogItem.value
  if (!workspace) return
  workspace.isPinned = isPinned
  workspace.tours.forEach((tour) => {
    tour.isPinned = isPinned
  })
}

const toggleMapContextWorkspacePinned = async () => {
  const workspace = selectedMapContextWorkspace.value
  if (!workspace) return
  closeMapWorkspaceContextMenu()
  if (!(await requireOnlineModificationAccess('pin collection'))) return

  try {
    const detail = await apiService.getWorkspaceDetail(workspace.clientId)
    if (isMapContextWorkspacePinned.value) {
      await unpinCollectionItems(detail)
      updateMapContextWorkspacePinnedState(false)
      showSuccess('Unpinned collection')
      return
    }
    await pinCollectionItems(detail)
    updateMapContextWorkspacePinnedState(true)
    showSuccess('Pinned collection')
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update map context collection pin state', {
      error,
      workspaceId: workspace.clientId,
    })
    showError(
      `Failed to update collection pin state: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const hideMapContextWorkspace = () => {
  const workspaceId = mapWorkspaceContextMenu.value.workspaceId
  if (!workspaceId) return
  closeMapWorkspaceContextMenu()
  searchWorkspaceStore.toggleWorkspaceVisibility(workspaceId)
}

const shareMapContextWorkspace = async () => {
  const workspace = selectedMapContextWorkspace.value
  if (!workspace) return
  closeMapWorkspaceContextMenu()

  try {
    const loaded = await syncService.loadRemoteWorkspace(workspace.clientId, false)
    if (!loaded) return

    uiStore.requestWorkspaceShareOpen()
    uiStore.beginMapNavigation('collection-route')
    await router.push({ name: 'workspace-detail', params: { workspaceId: workspace.clientId } })
    window.dispatchEvent(new CustomEvent('open-workspace-share-modal'))
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to open map context collection share modal', {
      error,
      workspaceId: workspace.clientId,
    })
    showError(
      `Failed to open share modal: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const toggleMapContextTourPinned = async () => {
  const tour = selectedMapContextTour.value
  if (!tour) return
  closeMapTrackContextMenu()
  await toggleTourPinned(tour)
}

const editMapContextTourRoute = async () => {
  const context = selectedMapContextTrack.value
  if (!context?.track.clientId) return
  closeMapTrackContextMenu()
  await enterTrackEditModeForTrack(context.tour.clientId, context.track.clientId)
}

const editMapContextActivityAsRoute = async () => {
  const context = selectedMapContextTrack.value
  const sourceTour = context?.tour
  if (!sourceTour) return
  closeMapTrackContextMenu()

  try {
    const hydratedTour =
      (await workspaceStore.ensureTourDetailLoaded?.(sourceTour.clientId)) ??
      workspaceStore.getTourById(sourceTour.clientId) ??
      sourceTour
    const newTourId = await workspaceStore.addEmptyTour({
      name: `${hydratedTour.name} (copy)`,
      type: undefined,
      sourceTourClientId: getRootSourceTourClientId(hydratedTour),
      sourceTour: getRootSourceTour(hydratedTour),
      emitAddedEvent: false,
    })
    const duplicatedTrackIds = new Map<string, string>()

    for (const track of hydratedTour.tracks) {
      const newTrackId = await workspaceStore.addTrack(newTourId, {
        name: track.name ?? '',
        segments: track.segments.map(({ clientId: _clientId, ...segment }) => ({
          ...segment,
          points: segment.points.map((point) => ({ ...point })),
        })),
        color: track.color,
        routingProfile: undefined,
      })
      if (newTrackId) {
        duplicatedTrackIds.set(track.clientId, newTrackId)
      }
    }

    for (const routePoint of hydratedTour.routePoints) {
      const newTrackClientId = duplicatedTrackIds.get(routePoint.trackClientId)
      if (!newTrackClientId) continue

      await workspaceStore.addRoutePoint(newTourId, {
        lat: routePoint.lat,
        lon: routePoint.lon,
        ele: routePoint.ele,
        name: routePoint.name,
        description: routePoint.description,
        order: routePoint.order,
        trackClientId: newTrackClientId,
      })
    }

    workspaceStore.setSelectedTourId(newTourId)
    const newTrackId = duplicatedTrackIds.get(context.track.clientId)
    const fallbackTrackId = duplicatedTrackIds.get(hydratedTour.tracks[0]?.clientId ?? '')
    if (newTrackId || fallbackTrackId) {
      await enterTrackEditModeForTrack(newTourId, newTrackId ?? fallbackTrackId!)
    }
  } catch (error) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to edit map context activity as route', {
      tourId: sourceTour.clientId,
      error,
    })
    showError('Failed to edit as route')
  }
}

const openMapTourDownloadModal = () => {
  const tour = selectedMapContextTour.value
  if (!tour || mapTourDownloadingIds.value.has(tour.clientId)) return
  mapTourDownloadTarget.value = tour
  closeMapTrackContextMenu()
}

const closeMapTourDownloadModal = () => {
  mapTourDownloadTarget.value = null
}

const handleDownloadMapTourGpx = async (options: { includeRoutePoints: boolean }) => {
  const tour = mapTourDownloadTarget.value
  if (!tour) return

  mapTourDownloadingIds.value.add(tour.clientId)
  try {
    const [blob] = await Promise.all([
      apiService.downloadGpx(tour.clientId, { includeRoutePoints: options.includeRoutePoints }),
      new Promise((resolve) => setTimeout(resolve, 500)),
    ])
    await downloadFile(blob, `${getWorkspaceTourDownloadName(tour)}.gpx`, {
      mimeType: 'application/gpx+xml',
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download map tour GPX', { error })
    showError(`Failed to download: ${error instanceof Error ? error.message : String(error)}`)
  } finally {
    mapTourDownloadingIds.value.delete(tour.clientId)
    closeMapTourDownloadModal()
  }
}

const openMapContextTourAddToCollection = () => {
  const { tourId, x, y } = mapTrackContextMenu.value
  if (!tourId) return
  closeMapTrackContextMenu({ preserveHighlight: true })
  toolbarRef.value?.openAddToCollectionByTourId(tourId, { x, y })
}

const escapeGpxText = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const buildMapPOIGpx = (poi: WorkspacePOI) => {
  if (!Number.isFinite(poi.lat) || !Number.isFinite(poi.lon)) return ''
  const parts = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="route.camp" xmlns="http://www.topografix.com/GPX/1/1">',
    `  <wpt lat="${poi.lat}" lon="${poi.lon}">`,
    `    <name>${escapeGpxText(poi.name?.trim() || 'POI')}</name>`,
  ]
  if (typeof poi.ele === 'number') parts.push(`    <ele>${poi.ele}</ele>`)
  if (poi.description?.trim())
    parts.push(`    <desc>${escapeGpxText(poi.description.trim())}</desc>`)
  if (poi.time) parts.push(`    <time>${new Date(poi.time).toISOString()}</time>`)
  if (poi.type?.trim()) parts.push(`    <sym>${escapeGpxText(poi.type.trim())}</sym>`)
  parts.push('  </wpt>', '</gpx>')
  return parts.join('\n')
}

const downloadMapContextPOI = async () => {
  const poi = selectedMapContextPOI.value
  if (!poi) return
  closeMapPOIContextMenu()

  const gpx = buildMapPOIGpx(poi)
  if (!gpx) return
  const fileName = `${(poi.name?.trim() || 'poi').replace(/[\\/:*?"<>|]+/g, '_')}.gpx`
  try {
    await downloadFile(new Blob([gpx], { type: 'application/gpx+xml' }), fileName, {
      mimeType: 'application/gpx+xml',
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download map POI GPX', { error })
    showError(`Failed to download POI: ${error instanceof Error ? error.message : String(error)}`)
  }
}

const toggleMapContextPOIPinned = async () => {
  const poi = selectedMapContextPOI.value
  if (!poi) return
  closeMapPOIContextMenu()
  await togglePOIPinned(poi)
}

const openMapContextPOIDetailForCopy = () => {
  const poi = selectedMapContextPOI.value
  if (!poi) return
  closeMapPOIContextMenu({ preserveHighlight: true })
  openAddToCollection({
    points: [poiToAddToCollectionPoint(poi)],
    sourceWorkspaceId: poi.workspaceClientId,
  })
}

const closeTourModalAndCollapseMobileToolbar = () => {
  selectedWaypointDetail.value = null
  uiStore.hideTourDetailPanel()
  uiStore.collapseMobileBottomPanel()
}

const handleWaypointDelete = async (tourId: string, waypointIndex: number) => {
  if (!(await requirePermission('delete_waypoint'))) return
  let tour: Tour | null = null
  if (uiStore.isAllActivitiesView) {
    tour = libraryStore.detailTour?.clientId === tourId ? libraryStore.detailTour : null
  } else {
    tour = workspaceStore.tours.find((candidate) => candidate.clientId === tourId) ?? null
  }
  if (!tour || waypointIndex < 0 || waypointIndex >= tour.waypoints.length) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return

  const waypoint = tour.waypoints[waypointIndex]
  const waypointName = waypoint.name || `Waypoint ${waypointIndex + 1}`

  const confirmed = await showConfirm({
    title: 'Delete Waypoint',
    message: `Are you sure you want to delete "${waypointName}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    if (uiStore.isAllActivitiesView) {
      await libraryStore.deleteWaypoint(tourId, waypointIndex)
    } else {
      await workspaceStore.deleteWaypoint(tourId, waypointIndex)
    }
    selectedWaypointDetail.value = null
  }
}

const handleWaypointEdit = (tourId: string, waypointIndex: number) => {
  void openSelectedTourWaypointDetail(tourId, waypointIndex, 'map')
}

const handlePOIEdit = (poiId: string) => {
  openPOIDetailPanel(poiId)
}

const handlePOIDelete = async (poiId: string) => {
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === poiId)
  if (!poi) return
  if (!(await requirePermission('delete_waypoint'))) return
  // Check creator-only content edit *before* the "are you sure" prompt, so a non-creator sees
  // only the explainer instead of a confirm they can't actually act on.
  if (!(await requireItemEditable(poi.canEdit, 'waypoint', poi.createdByName))) return

  const confirmed = await showConfirm({
    title: 'Delete POI',
    message: `Are you sure you want to delete "${poi.name || 'POI'}"? This action cannot be undone.`,
    confirmText: 'Delete',
    cancelText: 'Cancel',
    variant: 'danger',
  })

  if (confirmed) {
    await workspaceStore.deleteWorkspacePOI(poiId)
  }

  closePOIDetailPanel()
}

const handleViewPhoto = (photoId: string) => {
  currentPhotoId.value = photoId
  isSlideshowOpen.value = true
}

const closeSlideshow = () => {
  isSlideshowOpen.value = false
  currentPhotoId.value = ''
}

const zoomToContent = (
  fitOverrides?: Partial<FitBoundsOptions>,
  options: { force?: boolean; source?: string } = {},
) => {
  const source = options.source ?? 'unknown'
  if (!options.force && shouldSkipAutoContentFitAfterSavedRestore(source)) {
    return
  }

  if (!options.force && isFollowLocationCameraActive()) {
    logService.info(LOG_CATEGORIES.MAP, 'Skipping automatic content fit during follow mode', {
      source,
      workspaceId: workspaceStore.clientId,
      route: route.fullPath,
    })
    return
  }

  if (!mapRef.value) return

  const combinedBounds = getWorkspaceContentBounds()
  if (!combinedBounds) return

  logService.info(LOG_CATEGORIES.MAP, 'Fitting collection content', {
    source,
    force: Boolean(options.force),
    workspaceId: workspaceStore.clientId,
    route: route.fullPath,
    autoViewportAdaptationEnabled: uiStore.autoViewportAdaptationEnabled,
  })

  const fitOptions = { ...(fitOverrides ?? {}) } as FitBoundsOptions

  if (fitOptions.padding === undefined) {
    const viewportPadding = calculateViewportPadding()
    const isValidPadding = Object.values(viewportPadding).every(
      (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
    )

    if (isValidPadding) {
      fitOptions.padding = viewportPadding
    } else {
      logService.warn(
        LOG_CATEGORIES.UI,
        'Invalid viewport padding detected, using default padding',
        { padding: viewportPadding },
      )
      fitOptions.padding = 50
    }
  }

  mapRef.value.zoomToBounds(combinedBounds, fitOptions)
}

// Helper: obtain bounds for collections overview, prefer cached, else compute from store
const getWorkspacesOverviewBounds = (): maplibregl.LngLatBounds | null => {
  if (lastSearchWorkspacesBounds.value) return lastSearchWorkspacesBounds.value
  const boundsLike = searchWorkspaceStore.getVisibleWorkspacesBounds?.()
  if (!boundsLike) return null
  const b = new maplibregl.LngLatBounds()
  b.extend(boundsLike.sw as [number, number])
  b.extend(boundsLike.ne as [number, number])
  // cache for later
  lastSearchWorkspacesBounds.value = b
  return b
}

const calculateLibraryTourBounds = (tourId: string): maplibregl.LngLatBounds | null => {
  const tour = libraryStore.tours.find((candidate) => candidate.clientId === tourId)
  if (!tour) return null

  const bounds = new maplibregl.LngLatBounds()
  let hasPoints = false

  tour.tracks.forEach((track) => {
    if (!track.pointsLineReduced500m) return
    parseLineString(track.pointsLineReduced500m).forEach(([lon, lat]) => {
      bounds.extend([lon, lat])
      hasPoints = true
    })
  })

  return hasPoints ? bounds : null
}

const zoomToTour = (tourId: string) => {
  if (!mapRef.value || !tours?.value) return
  const tour = tours.value.find((t: Tour) => t.clientId === tourId)
  const bounds = tour
    ? workspaceStore.calculateTourBounds(tour)
    : calculateLibraryTourBounds(tourId)
  if (bounds && !bounds.isEmpty()) {
    const padding = calculateViewportPadding()
    const isValidPadding = Object.values(padding).every(
      (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
    )

    mapRef.value.zoomToBounds(bounds, {
      padding: isValidPadding ? padding : 60,
      maxZoom: 15,
      duration: 500,
      animate: true,
    })

    // Treat explicit zoom actions as user intent: disable further auto-adaptation
    uiStore.disableAutoViewportAdaptation()
  }
}

// Watch for workspace ID changes to zoom to new content
watch(
  () => workspaceStore.clientId,
  (newWorkspaceId, oldWorkspaceId) => {
    if (oldWorkspaceId && newWorkspaceId !== oldWorkspaceId && uiStore.isTrackEditMode) {
      void closeTrackEdit()
    }

    // Don't zoom on initial load or if map isn't ready
    if (!oldWorkspaceId || !mapInstance.value) return

    // Only zoom when switching to a new workspace, not when clearing back to overview
    if (!newWorkspaceId) return

    // WorkspacesView owns route-entry movement through zoomToContentWhenReady(), which waits for
    // the final layout and handles saved viewports. Starting another fit here would be interrupted
    // by that authoritative fit after its settle timeout.
    if (getRouteWorkspaceId() === newWorkspaceId) return

    if (!shouldAutoMoveWorkspaceContent()) {
      if (hasPendingItemRoute()) {
        nextTick(() => {
          tryFocusDeepLinkedTour()
        })
      }
      return
    }

    // Deep-linked item routes handle their own map movement.
    if (hasPendingItemRoute()) {
      nextTick(() => {
        // If a deep link tour is pending and belongs to this workspace, try to focus
        tryFocusDeepLinkedTour()
      })
      return
    }

    // Zoom to fit all tracks/content for the new workspace
    nextTick(() => {
      // Re-enable auto viewport adaptation on navigation change
      uiStore.enableAutoViewportAdaptation()
      zoomToContent(undefined, { source: 'workspace-id-change' })
      finalizeWorkspaceContentMovement()
      // If a deep link tour is pending and belongs to this workspace, try to focus
      tryFocusDeepLinkedTour()
    })
  },
)

// Watch for mobile toolbar state changes to adjust viewport
watch(
  () => isMobileBottomPanelExpanded.value,
  (expanded) => {
    // Only adjust viewport on small screens where mobile toolbar is visible
    if (!isSmallScreen.value || !mapInstance.value) return

    // Wait for DOM updates to complete before recalculating viewport
    nextTick(() => {
      if (!uiStore.autoViewportAdaptationEnabled) return

      // In collections overview, refit on both expand and collapse so the visible
      // collections stay inside the uncovered map viewport.
      if (uiStore.isInWorkspacesOverview && lastSearchWorkspacesBounds.value) {
        const padding = calculateViewportPadding()
        const isValidPadding = Object.values(padding).every(
          (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
        )
        mapRef.value!.zoomToBounds(lastSearchWorkspacesBounds.value, {
          padding: isValidPadding ? padding : 50,
          maxZoom: 14,
        })
        return
      }

      // Preserve existing collection-detail behavior: only refit when collapsing.
      if (expanded) return

      zoomToContent(undefined, { source: 'mobile-toolbar-collapse' })
    })
  },
)

// Watch for desktop sidebar visibility changes to adjust viewport
watch(
  () => isSidebarVisible.value,
  () => {
    // Only adjust viewport on large screens where desktop sidebar is visible
    if (isSmallScreen.value || !mapInstance.value) return

    // Wait for DOM updates to complete before recalculating viewport
    nextTick(() => {
      if (!uiStore.autoViewportAdaptationEnabled) return
      if (uiStore.isInWorkspacesOverview && lastSearchWorkspacesBounds.value) {
        const padding = calculateViewportPadding()
        const isValidPadding = Object.values(padding).every(
          (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
        )
        mapRef.value!.zoomToBounds(lastSearchWorkspacesBounds.value, {
          padding: isValidPadding ? padding : 50,
          maxZoom: 14,
        })
      } else {
        zoomToContent(undefined, { source: 'desktop-sidebar-change' })
      }
    })
  },
)

// Watch for UI refs to become available and re-zoom if needed on initial load
// This ensures proper viewport padding is applied even when refs are not initially available
watch(
  [() => localHeaderRef.value, () => toolbarRef.value, () => mobileToolbarRef.value],
  (
    [newHeaderRef, newToolbarRef, newMobileToolbarRef],
    [oldHeaderRef, oldToolbarRef, oldMobileToolbarRef],
  ) => {
    // Only trigger re-zoom on initial ref availability (when old ref was null/undefined)
    const headerBecameAvailable = !oldHeaderRef && newHeaderRef
    const toolbarBecameAvailable = !oldToolbarRef && newToolbarRef && !isSmallScreen.value
    const mobileToolbarBecameAvailable =
      !oldMobileToolbarRef && newMobileToolbarRef && isSmallScreen.value

    // Only re-zoom if map is loaded and we have content to zoom to
    if (!mapInstance.value || !mapLoaded.value) return

    const hasContent = () => {
      const tourBounds = workspaceStore.calculateMapBounds()
      const photoBounds = photoStore.calculateMapBounds()
      return (tourBounds && !tourBounds.isEmpty()) || (photoBounds && !photoBounds.isEmpty())
    }

    // Re-zoom when key UI elements become available
    if (headerBecameAvailable || toolbarBecameAvailable || mobileToolbarBecameAvailable) {
      // Priority 1: If we have tours/photos, zoom to them
      if (hasContent() && uiStore.autoViewportAdaptationEnabled) {
        nextTick(() => {
          zoomToContent(undefined, { source: 'ui-refs-ready' })
        })
        return
      }
      // Priority 2: If we're in collections overview and we have cached search bounds, zoom to those
      if (uiStore.autoViewportAdaptationEnabled && uiStore.isInWorkspacesOverview) {
        const overviewBounds = getWorkspacesOverviewBounds()
        if (!overviewBounds) return
        nextTick(() => {
          const padding = calculateViewportPadding()
          const isValidPadding = Object.values(padding).every(
            (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
          )
          mapRef.value!.zoomToBounds(overviewBounds, {
            padding: isValidPadding ? padding : 50,
            maxZoom: 14,
          })
        })
      }
    }
  },
  { flush: 'post' },
)

// When switching into collections overview after navigation, re-apply cached bounds with padding
watch(
  () => uiStore.isInWorkspacesOverview,
  (isOverview) => {
    if (isOverview) {
      photoLayerRef.value?.forceRemoveMarkers?.()
      selectedWaypointLayerRef.value?.forceRemoveMarkers?.()

      // Always disable waypoint placement when entering collection search mode
      isWaypointPlacementActive.value = false
      pendingWaypointName.value = null
      pendingWaypointType.value = null
      pendingWaypointDescription.value = null
    }

    if (!isOverview || !mapInstance.value || !mapLoaded.value) return
    if (!shouldAutoMoveOverviewContent()) {
      finalizeOverviewContentMovement()
      return
    }
    // Only zoom if the user hasn't manually moved the map since their last interaction
    if (!uiStore.autoViewportAdaptationEnabled) return
    const overviewBounds = getWorkspacesOverviewBounds()
    if (!overviewBounds) return
    nextTick(() => {
      const padding = calculateViewportPadding()
      const isValidPadding = Object.values(padding).every(
        (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
      )
      mapRef.value?.zoomToBounds(overviewBounds, {
        padding: isValidPadding ? padding : 50,
        maxZoom: 14,
      })
    })
  },
  { immediate: true },
)

// Also react when search collections bounds become available for the first time
// (initial page load case where bounds arrive after UI is mounted)
watch(
  () => lastSearchWorkspacesBounds.value,
  (bounds) => {
    if (bounds && bounds === explicitlyFitOverviewBounds) {
      explicitlyFitOverviewBounds = null
      return
    }
    explicitlyFitOverviewBounds = null
    if (!bounds || !mapInstance.value || !mapLoaded.value) return
    if (!uiStore.isInWorkspacesOverview) return
    if (!shouldAutoMoveOverviewContent()) {
      finalizeOverviewContentMovement()
      return
    }
    if (!uiStore.autoViewportAdaptationEnabled) return
    nextTick(() => {
      const padding = calculateViewportPadding()
      const isValidPadding = Object.values(padding).every(
        (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
      )
      mapRef.value?.zoomToBounds(bounds, {
        padding: isValidPadding ? padding : 50,
        maxZoom: 14,
      })
    })
  },
)

// If the map finishes loading after we received bounds, apply them with padding
watch(
  () => mapLoaded.value,
  (loaded) => {
    if (!loaded) return
    if (!uiStore.isInWorkspacesOverview) return
    if (!shouldAutoMoveOverviewContent()) {
      finalizeOverviewContentMovement()
      return
    }
    // Do not check autoViewportAdaptationEnabled here: this watcher fires exactly once
    // (initial page load), before the user has interacted with the map. On Edge/Safari,
    // MapLibre can spuriously emit gesture events during initialization, falsely disabling
    // auto-adaptation before the first overview zoom has had a chance to run.
    const bounds = lastSearchWorkspacesBounds.value
    if (!bounds) return
    nextTick(() => {
      const padding = calculateViewportPadding()
      const isValidPadding = Object.values(padding).every(
        (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
      )
      mapRef.value?.zoomToBounds(bounds, {
        padding: isValidPadding ? padding : 50,
        maxZoom: 14,
      })
    })
  },
)

const zoomToWaypoint = (tourId: string, waypointIndex: number) => {
  const map = mapInstance.value
  if (!map) return
  const tour = uiStore.isAllActivitiesView
    ? libraryStore.detailTour?.clientId === tourId
      ? libraryStore.detailTour
      : null
    : (tours.value.find((candidate: Tour) => candidate.clientId === tourId) ?? null)
  if (!tour || !tour.waypoints || waypointIndex >= tour.waypoints.length) return
  const waypoint = tour.waypoints[waypointIndex]
  if (waypoint && waypoint.lat && waypoint.lon) {
    // Centre in the visible map band above the open Route sheet (not the full map), same as the
    // A→B segment fit — reserve the sheet's height as bottom padding so the waypoint isn't hidden.
    map.flyTo({
      center: [waypoint.lon as number, waypoint.lat as number],
      zoom: 16,
      padding: getInitialRouteSegmentViewportPadding(),
      duration: 450,
      essential: true,
    })
    uiStore.disableAutoViewportAdaptation()
  }
}

const zoomToPOI = (poiId: string) => {
  if (!mapRef.value) return
  const poi = workspaceStore.pois.find((candidate) => candidate.clientId === poiId)
  if (!poi || typeof poi.lat !== 'number' || typeof poi.lon !== 'number') return
  const padding = calculateViewportPadding()
  const isValidPadding = Object.values(padding).every(
    (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
  )
  mapRef.value.flyTo([poi.lon, poi.lat], {
    zoom: 15,
    padding: isValidPadding ? padding : undefined,
    duration: isSmallScreen.value ? 450 : undefined,
    essential: true,
  })
  uiStore.disableAutoViewportAdaptation()
}

watch(
  () => [selectedPOIId.value, workspaceStore.pois.length] as const,
  ([poiId]) => {
    if (!poiId) return
    if (!workspaceStore.pois.some((candidate) => candidate.clientId === poiId)) {
      if (retainedUnpinnedPOIDetail.value?.clientId === poiId) return
      closePOIDetailPanel()
    }
  },
)

const zoomToRoutePoint = (tourId: string, routePointIndex: number) => {
  if (!mapRef.value || !tours?.value) return
  const tour = tours.value.find((t: Tour) => t.clientId === tourId)
  if (!tour || !tour.routePoints || routePointIndex >= tour.routePoints.length) return
  const rp = tour.routePoints[routePointIndex]
  if (rp && rp.lat && rp.lon) {
    mapRef.value.flyTo([rp.lon as number, rp.lat as number], { zoom: 16 })
  }
}

const zoomToPhoto = (photoId: string) => {
  if (!mapRef.value) return
  const photo = photoStore.photos.find((p: Photo) => p.clientId === photoId)
  if (!photo || !photo.hasCoordinates || photo.lat === undefined || photo.lon === undefined) return
  mapRef.value.flyTo([photo.lon as number, photo.lat as number], { zoom: 16 })

  // Disable auto viewport adaptation when user explicitly zooms to photo location
  uiStore.disableAutoViewportAdaptation()
}

// Map "Viewport" calculation with UI element dimensions
// Reactive computed property that responds to mobile toolbar state changes
const viewportPadding = computed(() => {
  const OFFSET = 50

  const padding = {
    top: OFFSET,
    right: OFFSET,
    bottom: OFFSET,
    left: OFFSET,
  }

  // Calculate header height dynamically. The mobile header is visually removed, so mobile
  // map fitting should not reserve top header space.
  if (isSmallScreen.value) {
    padding.top = OFFSET
  } else if (localHeaderRef.value?.$el) {
    const headerHeight = localHeaderRef.value.$el.offsetHeight
    if (typeof headerHeight === 'number' && !isNaN(headerHeight) && headerHeight > 0) {
      padding.top = headerHeight + OFFSET
    } else {
      padding.top = 48 + OFFSET // Fallback
    }
  } else {
    // Fallback to known height if ref not available
    padding.top = 48 + OFFSET // MapHeader h-12 = 48px
  }

  // While the unified Route sheet is open it (not the hidden list toolbar) occupies the
  // bottom of the screen, sitting on the 64px tab bar. Pad the fit to the visible map area
  // above the sheet so a selected route centres in that space, not behind the sheet.
  if (isSmallScreen.value && isMobileRouteSheetOpen.value) {
    const TAB_BAR_HEIGHT = 64
    const sheetHeight = routeSheetHeight.value ?? Math.floor(window.innerHeight * 0.52)
    padding.bottom = sheetHeight + TAB_BAR_HEIGHT + 20
  }
  // Calculate mobile toolbar height dynamically - reactive to panel state
  else if (isSmallScreen.value && mobileToolbarRef.value?.$el) {
    const toolbarElement = mobileToolbarRef.value.$el as HTMLElement | undefined
    const measuredToolbarHeight = toolbarElement?.offsetHeight ?? 0
    const fallbackToolbarHeight = isMobileBottomPanelExpanded.value
      ? Math.floor(window.innerHeight * 0.65)
      : selectedRouteActionTourId.value
        ? 188
        : 124
    const toolbarHeight = measuredToolbarHeight > 0 ? measuredToolbarHeight : fallbackToolbarHeight
    if (typeof toolbarHeight === 'number' && !isNaN(toolbarHeight) && toolbarHeight > 0) {
      // Use a tight margin above the toolbar edge — avoid the full OFFSET because
      // the visible map area can be very small when the panel is expanded.
      padding.bottom = toolbarHeight + 16
    } else {
      // Fallback based on mobile panel state when DOM not available
      const fallbackHeight = isMobileBottomPanelExpanded.value
        ? Math.floor(window.innerHeight * 0.6) // 65% when expanded
        : 68 // Header height when collapsed
      padding.bottom = fallbackHeight + 16
    }
  }

  // Calculate desktop toolbar width dynamically
  if (!isSmallScreen.value) {
    if (toolbarRef.value?.$el) {
      const toolbarWidth = toolbarRef.value.$el?.offsetWidth
      if (typeof toolbarWidth === 'number' && !isNaN(toolbarWidth) && toolbarWidth > 0) {
        padding.left = toolbarWidth + OFFSET
      } else {
        // Enhanced fallback for desktop toolbar when ref not available
        padding.left = 320 + OFFSET // Typical toolbar width for "My Collections" view
      }
    } else {
      // Enhanced fallback for desktop toolbar when ref not available
      padding.left = 320 + OFFSET // Typical toolbar width for "My Collections" view
    }
  }

  // Take map controls into account on desktop only — on mobile the toolbar
  // already covers the controls area, so adding extra padding wastes visible space.
  if (!isSmallScreen.value) {
    padding.bottom = padding.bottom + 50
  }

  return padding
})

const calculateViewportPadding = () => {
  return viewportPadding.value
}

// Cache explicit track-zoom bounds so repeated toolbar/list zooms stay cheap.
const trackBoundsCache = ref<Record<string, maplibregl.LngLatBounds>>({})

watch(
  () => [workspaceStore.tours, libraryStore.detailTour] as const,
  () => {
    trackBoundsCache.value = {}
  },
  { deep: true },
)

const zoomToTrack = (tourId: string, trackIndex: number) => {
  if (!mapRef.value) return
  const tour = getTrackTourById(tourId)
  if (!tour) return

  const track = tour.tracks[trackIndex]
  if (!track) return

  const cacheKey = `${tourId}-${trackIndex}`
  if (!trackBoundsCache.value[cacheKey]) {
    const bounds = new maplibregl.LngLatBounds()

    const lineString = getBestTrackLineString(track)
    if (lineString) {
      const coordinates = parseLineString(lineString)
      if (coordinates.length > 0) {
        bounds.extend([coordinates[0][0], coordinates[0][1]])
        bounds.extend([
          coordinates[coordinates.length - 1][0],
          coordinates[coordinates.length - 1][1],
        ])

        if (coordinates.length > 100) {
          const sampleRate = Math.max(Math.floor(coordinates.length / 50), 1)
          for (let i = sampleRate; i < coordinates.length - 1; i += sampleRate) {
            bounds.extend([coordinates[i][0], coordinates[i][1]])
          }
        } else {
          for (let i = 1; i < coordinates.length - 1; i++) {
            bounds.extend([coordinates[i][0], coordinates[i][1]])
          }
        }
      }
    } else if (track.segments && track.segments.length > 0) {
      const allPoints = track.segments.flatMap((segment: Segment) => segment.points)
      const pointCount = allPoints.length

      if (pointCount > 0) {
        bounds.extend([allPoints[0].lon, allPoints[0].lat])
        bounds.extend([allPoints[pointCount - 1].lon, allPoints[pointCount - 1].lat])
      }

      if (pointCount > 100) {
        const sampleRate = Math.max(Math.floor(pointCount / 50), 1)
        for (let i = sampleRate; i < pointCount - 1; i += sampleRate) {
          bounds.extend([allPoints[i].lon, allPoints[i].lat])
        }
      } else {
        for (let i = 1; i < pointCount - 1; i++) {
          bounds.extend([allPoints[i].lon, allPoints[i].lat])
        }
      }
    }

    trackBoundsCache.value[cacheKey] = bounds
  }

  const bounds = trackBoundsCache.value[cacheKey]
  if (!bounds.isEmpty()) {
    const padding = calculateViewportPadding()
    const isValidPadding = Object.values(padding).every(
      (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
    )

    mapRef.value.zoomToBounds(bounds, {
      padding: isValidPadding ? padding : 40,
      maxZoom: 15,
      duration: 500,
      animate: true,
    })

    uiStore.disableAutoViewportAdaptation()
  }
}

const registerSelectedWaypointLayerRef = (el: Element | ComponentPublicInstance | null) => {
  if (el && '$props' in el) {
    selectedWaypointLayerRef.value = el as InstanceType<typeof WaypointMarkerLayer>
  } else {
    selectedWaypointLayerRef.value = null
  }
}

const resolveTargetTourId = (scope?: RoutingScope | null): string | null => {
  const routingScope = resolveRoutingScopeForTour(null, scope)
  if (routingScope === 'library') {
    return libraryStore.detailTour?.clientId ?? null
  }

  if (routingScope === 'workspace' && activeRoutingTourId.value) {
    const activeTour = workspaceStore.getTourById(activeRoutingTourId.value)
    if (activeTour) return activeTour.clientId
  }

  if (workspaceStore.selectedTour) return workspaceStore.selectedTour.clientId
  const active = workspaceStore.tours.find((t) => t.active)
  if (active) return active.clientId
  if (workspaceStore.tours.length) return workspaceStore.tours[0].clientId
  return null
}

const routePointLayerTours = computed(() => {
  if (uiStore.isTrackEditMode) {
    const tour = getTrackTourById(uiStore.editingTrackTourId)
    return tour ? [tour] : []
  }

  if (uiStore.isTrackDrawingActive) {
    const targetTour = getTrackTourById(resolveTargetTourId())
    return targetTour ? [targetTour] : []
  }

  return []
})

const cancelWaypointPlacement = () => {
  isWaypointPlacementActive.value = false
  waypointPlacementScope.value = 'tour-waypoint'
  pendingWaypointName.value = null
  pendingWaypointType.value = null
  pendingWaypointDescription.value = null
}

const confirmWaypointPlacement = async () => {
  try {
    if (!(await requirePermission('edit_waypoint'))) {
      showError('You do not have permission to add waypoints')
      cancelWaypointPlacement()
      return
    }
    if (!mapInstance.value) return
    const center = mapInstance.value.getCenter()
    const lat = center.lat
    const lon = center.lng

    if (waypointPlacementScope.value === 'workspace-poi') {
      await workspaceStore.addWorkspacePOI({
        lat,
        lon,
        name: pendingWaypointName.value || undefined,
        description: pendingWaypointDescription.value || undefined,
        type: pendingWaypointType.value || undefined,
      })
      return
    }

    const tourId = resolveTargetTourId()
    if (!tourId) {
      showError('No tour available to add waypoint')
      cancelWaypointPlacement()
      return
    }
    await workspaceStore.addWaypoint(tourId, {
      lat,
      lon,
      name: pendingWaypointName.value || undefined,
      description: pendingWaypointDescription.value || undefined,
      type: pendingWaypointType.value || undefined,
      time: Date.now(),
    })
  } catch (e) {
    showError(`Failed to add waypoint: ${(e as Error).message}`)
  } finally {
    cancelWaypointPlacement()
  }
}

const waypointPlacementConfirmLabel = computed(() =>
  waypointPlacementScope.value === 'workspace-poi' ? 'Add POI Here' : 'Add Waypoint Here',
)

const resizeMap = () => {
  mapRef.value?.resizeMap()
}

const waitForNextAnimationFrame = () =>
  new Promise<void>((resolve) => {
    requestAnimationFrame(() => resolve())
  })

const waitForMapToSettleBeforeViewportFit = async () => {
  await nextTick()
  await waitForNextAnimationFrame()

  resizeMap()

  const map = mapInstance.value
  if (!map || !mapLoaded.value) {
    return
  }

  if (map.isMoving?.()) {
    await new Promise<void>((resolve) => {
      let settled = false
      let timeoutId: number | null = null

      const finish = () => {
        if (settled) return
        settled = true
        map.off('moveend', finish)
        if (timeoutId !== null) {
          window.clearTimeout(timeoutId)
        }
        resolve()
      }

      map.once('moveend', finish)
      timeoutId = window.setTimeout(finish, 1000)
      if (!map.isMoving()) finish()
    })
  }

  await new Promise<void>((resolve) => {
    let settled = false
    let timeoutId: number | null = null

    const finish = () => {
      if (settled) return
      settled = true
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId)
      }
      resolve()
    }

    map.once('idle', finish)
    timeoutId = window.setTimeout(finish, 250)
  })

  await waitForNextAnimationFrame()
}

const applyPendingSmartSearchTrackSelection = async () => {
  const selection = uiStore.pendingSmartSearchTrackSelection
  if (!selection || selection.workspaceId !== workspaceStore.clientId) return

  try {
    await workspaceStore.ensureTourDetailLoaded(selection.tourId)
  } catch (error) {
    uiStore.clearSmartSearchTrackSelection()
    logService.warn(LOG_CATEGORIES.UI, 'Failed to load smart-search track target', {
      error,
      selection,
    })
    return
  }

  const tour = workspaceStore.getTourById(selection.tourId)
  const trackIndex =
    tour?.tracks.findIndex((track) => track.clientId === selection.trackClientId) ?? -1

  if (!tour || trackIndex < 0) {
    uiStore.clearSmartSearchTrackSelection()
    return
  }

  workspaceStore.selectTour(selection.tourId)
  showTourDetailPanel()
  handleSelectedTourTrackZoom(selection.tourId, trackIndex)
  uiStore.clearSmartSearchTrackSelection()
}

watch(
  () =>
    [
      uiStore.pendingSmartSearchTrackSelection?.workspaceId ?? null,
      workspaceStore.clientId,
      workspaceStore.tours.length,
    ] as const,
  () => {
    void applyPendingSmartSearchTrackSelection()
  },
  { immediate: true },
)

const selectTour = (tourId: string) => {
  if (selectedPOIId.value) {
    closePOIDetailPanel()
  }
  workspaceStore.selectTour(tourId)
  showTourDetailPanel()
}

const clearSelectedRouteSegmentSelection = () => {
  isSelectedRouteSegmentActive.value = false
  selectedRouteSegmentRangeM.value = null
  selectedTourProfileCursorDistanceM.value = null
}

const activateSelectedRouteSegment = (initialRange: TrackProfileVisibleRange | null = null) => {
  if (selectedTourProfileAnalysis.value.points.length <= 1) {
    isSelectedRouteSegmentActive.value = false
    return false
  }

  isSelectedRouteSegmentActive.value = true
  selectedRouteSegmentRangeM.value = initialRange
  selectedTourProfileCursorDistanceM.value = null
  return true
}

const handleWorkspaceTrackClick = async (tourId: string, trackClientId: string) => {
  ignoreNextRouteSelectionMapClick = true
  window.setTimeout(() => {
    ignoreNextRouteSelectionMapClick = false
  }, 0)

  workspaceStore.selectTour(tourId)
  showTourDetailPanel({ initialMode: lastRouteSheetMode.value })

  try {
    await workspaceStore.ensureTourDetailLoaded(tourId)
  } catch (error) {
    logService.warn(LOG_CATEGORIES.MAP, 'Failed to load route details for segment selection', {
      tourId,
      trackClientId,
      error,
    })
    showError('Failed to load route details for profile selection.')
    return
  }

  if (workspaceStore.selectedTourId !== tourId) return

  const tour = workspaceStore.getTourById(tourId)
  const selectedTrack =
    tour?.tracks.find((track) => track.clientId === trackClientId) ?? tour?.tracks[0] ?? null
  selectedTourProfileTrackClientId.value = selectedTrack?.clientId ?? trackClientId
  resetSelectedTourProfileBreakdownState()

  if (!activateSelectedRouteSegment()) {
    showError('This route does not have enough profile geometry for A/B selection.')
    return
  }

  await nextTick()
  const initialRange = resolveVisibleTrackProfileRange(selectedTourProfileAnalysis.value)
  if (initialRange) {
    selectedRouteSegmentRangeM.value = initialRange
  }
}

const handleListTourZoom = (tourId: string) => {
  uiStore.runIntentDrivenMapMovement('tour-list', () => {
    zoomToTour(tourId)
  })
}

const handleListTourSelect = (tourId: string) => {
  selectTour(tourId)
}

const handleListPOIZoom = (poiId: string) => {
  uiStore.runIntentDrivenMapMovement('poi-list', () => {
    zoomToPOI(poiId)
  })
}

const handleOpenPOIDetailFromList = (poiId: string) => {
  openPOIDetailPanel(poiId, 'list')
}

const handleListWaypointZoom = (tourId: string, waypointIndex: number) => {
  uiStore.runIntentDrivenMapMovement('waypoint-list', () => {
    zoomToWaypoint(tourId, waypointIndex)
  })
}

const handleListTrackZoom = (tourId: string, trackIndex: number) => {
  uiStore.runIntentDrivenMapMovement('track-list', () => {
    handleSelectedTourTrackZoom(tourId, trackIndex)
  })
}

const handleOpenWaypointDetailFromList = async (
  tourId: string,
  waypointIndex: number,
  source?: 'list' | 'map',
) => {
  await openSelectedTourWaypointDetail(tourId, waypointIndex, source ?? null)
}

const handleSelectedTourTrackZoom = (tourId: string, trackIndex: number) => {
  const tour = getTrackTourById(tourId)
  selectedTourProfileTrackClientId.value = tour?.tracks[trackIndex]?.clientId ?? null
  clearSelectedRouteSegmentSelection()
  resetSelectedTourProfileBreakdownState()
  zoomToTrack(tourId, trackIndex)
}

const handleSelectedTourProfileVisibleRangeUpdate = (
  visibleRangeM: TrackProfileVisibleRange | null,
) => {
  if (isSelectedRouteSegmentActive.value) {
    selectedRouteSegmentRangeM.value = visibleRangeM
    return
  }

  selectedTourProfileVisibleRangeM.value = visibleRangeM
}

const handleSelectedTourProfileRangeSelect = (range: TrackProfileVisibleRange) => {
  activateSelectedRouteSegment(range)
}

const cycleBreakdownFocus = (
  analysis: TrackProfileAnalysis,
  categoryId: string,
  currentFocus: { categoryId: string; segmentIndex: number } | null,
) => {
  const mergedSegments = mergeTrackProfileLegs(
    analysis.legs.filter((leg) => leg.categoryId === categoryId),
  )
  if (mergedSegments.length === 0) return null

  return {
    categoryId,
    segmentIndex:
      currentFocus?.categoryId === categoryId
        ? (currentFocus.segmentIndex + 1) % mergedSegments.length
        : 0,
  }
}

const zoomToProfileLeg = (segment: TrackProfileMergedSegment | null) => {
  if (!mapRef.value || !segment) return

  const bounds = new maplibregl.LngLatBounds()
  for (const [lon, lat] of segment.coordinates) {
    bounds.extend([lon, lat])
  }
  if (bounds.isEmpty()) return

  const padding = calculateViewportPadding()
  const isValidPadding = Object.values(padding).every(
    (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
  )

  mapRef.value.zoomToBounds(bounds, {
    padding: isValidPadding ? padding : 40,
    maxZoom: 16,
    duration: 500,
    animate: true,
  })
  uiStore.disableAutoViewportAdaptation()
}

const zoomToProfileRange = (
  analysis: TrackProfileAnalysis,
  visibleRangeM: TrackProfileVisibleRange | null,
) => {
  if (!mapRef.value) return

  const { activeLegs } = partitionTrackProfileLegsByRange(analysis.legs, visibleRangeM)
  if (activeLegs.length === 0) return

  const bounds = new maplibregl.LngLatBounds()
  for (const leg of activeLegs) {
    for (const [lon, lat] of leg.coordinates) {
      bounds.extend([lon, lat])
    }
  }
  if (bounds.isEmpty()) return

  const padding = calculateViewportPadding()
  const isValidPadding = Object.values(padding).every(
    (value) => typeof value === 'number' && !isNaN(value) && isFinite(value),
  )

  mapRef.value.zoomToBounds(bounds, {
    padding: isValidPadding ? padding : 40,
    maxZoom: visibleRangeM ? 16 : 15,
    duration: 500,
    animate: true,
  })
  uiStore.disableAutoViewportAdaptation()
}

// Cycle through (and zoom to) the sections of a clicked breakdown category. The "previous
// focus" only carries over when the same metric+category is re-clicked.
const cycleSectionFocus = (
  analyses: ReturnType<typeof buildTrackProfileAnalyses>,
  section: ProfileSection,
  previous: ProfileSectionFocus | null,
): { focus: ProfileSectionFocus | null; segment: TrackProfileMergedSegment | null } => {
  const analysis = analyses[section.metricId]
  const carryOver =
    previous && previous.metricId === section.metricId
      ? { categoryId: previous.categoryId, segmentIndex: previous.segmentIndex }
      : null
  const nextFocus = cycleBreakdownFocus(analysis, section.categoryId, carryOver)
  return {
    focus: nextFocus ? { metricId: section.metricId, ...nextFocus } : null,
    segment: resolveFocusedBreakdownLeg(analysis, nextFocus),
  }
}

const handleSelectedTourSectionHover = (section: ProfileSection | null) => {
  selectedTourProfileHoveredSection.value = section
}

const handleSelectedTourSectionCycle = (section: ProfileSection) => {
  const { focus, segment } = cycleSectionFocus(
    selectedTourProfileAnalyses.value,
    section,
    selectedTourProfileFocusedBreakdown.value,
  )
  selectedTourProfileFocusedBreakdown.value = focus
  zoomToProfileLeg(segment)
}

const handleRouteModeSectionHover = (section: ProfileSection | null) => {
  trackProfileHoveredSection.value = section
}

const handleRouteModeSectionCycle = (section: ProfileSection) => {
  const { focus, segment } = cycleSectionFocus(
    routeModeActiveAnalyses.value,
    section,
    trackProfileFocusedBreakdown.value,
  )
  trackProfileFocusedBreakdown.value = focus
  zoomToProfileLeg(segment)
}

watch(trackProfileVisibleRangeM, (visibleRangeM, previousRangeM) => {
  if (!uiStore.isTrackEditMode) return
  if (visibleRangeM === null && previousRangeM === null) return
  zoomToProfileRange(editingTrackProfileAnalysis.value, visibleRangeM)
})

// Handler for selecting a workspace from search
const selectWorkspace = (workspaceId: string) => {
  rememberLastCollectionContext(workspaceId)
  uiStore.beginMapNavigation('collection-route')
  router.push({ name: 'workspace-detail', params: { workspaceId } })
}

const stableCollapsedSidebarLabel = ref('Collections')

const resolvedCollapsedSidebarLabel = computed(() => {
  if (uiStore.isAllActivitiesView) return 'My Activities & Routes'
  if (routeWorkspaceId.value === null) return 'Collections'

  if (
    workspaceStore.clientId === routeWorkspaceId.value &&
    workspaceStore._isLoading !== true &&
    workspaceStore.name.trim().length > 0
  ) {
    return workspaceStore.name.trim()
  }

  return null
})

const showCollapsedSidebarRail = computed(
  () => !isSidebarVisible.value && !isSmallScreen.value && !uiStore.isLandingPageVisible,
)

const showCollapsedSidebarBackButton = computed(
  () => showCollapsedSidebarRail.value && routeWorkspaceId.value !== null,
)

const collapsedSidebarHasWorkspaceRole = computed(
  () =>
    workspaceStore.isOwner ||
    workspaceStore.isCollaborator ||
    workspaceStore.isViewer ||
    workspaceStore.canEdit === true,
)

const collapsedSidebarIsBookmarkOnly = computed(
  () => workspaceStore.isBookmarked === true && !collapsedSidebarHasWorkspaceRole.value,
)

watch(
  resolvedCollapsedSidebarLabel,
  (label) => {
    if (!label) return
    stableCollapsedSidebarLabel.value = label
  },
  { immediate: true },
)

const collapsedSidebarLabel = computed(() => stableCollapsedSidebarLabel.value)
const canShareDesktopWorkspace = computed(() => {
  const storeWithSaveFlag = workspaceStore as typeof workspaceStore & {
    _createEventPushed?: boolean
    currentWorkspace?: { _createEventPushed?: boolean } | null
  }

  return (
    routeWorkspaceId.value !== null &&
    (storeWithSaveFlag.currentWorkspace?._createEventPushed === true ||
      storeWithSaveFlag._createEventPushed === true) &&
    !isSelectedRouteSegmentActive.value
  )
})
const showDesktopShareButton = computed(
  () => canShareDesktopWorkspace.value && !isSidebarVisible.value,
)
const isOverviewFilterFloatExpanded = ref(false)
const showCollapsedOverviewFilters = computed(
  () => showCollapsedSidebarRail.value && isCollectionsOverviewContext.value,
)
const hasActiveOverviewSharingFilter = computed(
  () => uiStore.workspaceOverviewSharingFilter !== 'all',
)
const collapsedOverviewVisibleFilters = computed(() =>
  hasActiveOverviewSharingFilter.value ? (['sharing'] as Array<'sharing'>) : [],
)
const overviewFloatingVisibleFilters = computed(() =>
  isOverviewFilterFloatExpanded.value
    ? (['sharing'] as Array<'sharing'>)
    : collapsedOverviewVisibleFilters.value,
)

const handleExpandSidebar = () => {
  uiStore.setSuppressNextAutoZoom(true)
  uiStore.showSidebar()
}

const toggleOverviewFilterFloat = () => {
  isOverviewFilterFloatExpanded.value = !isOverviewFilterFloatExpanded.value
}

const getMapChromeContainerWidth = () => {
  void mapChromeLayoutEpoch.value
  void uiStore.toolbarLayoutEpoch

  const container = mapChromeContainerRef.value
  if (!container) return 0

  return container.clientWidth || container.getBoundingClientRect().width || 0
}

const clampDesktopFloatingLeft = (left: number, controlWidth = FLOATING_MAP_CHROME_SIZE) => {
  const containerWidth = getMapChromeContainerWidth()
  if (containerWidth <= 0) return left
  return Math.min(left, containerWidth - controlWidth - FLOATING_MAP_CHROME_EDGE_MARGIN)
}

const getCollapsedRailRight = () => {
  const measuredCollapsedRailWidth =
    collapsedRailMeasureRef.value?.getBoundingClientRect().width ?? null
  return measuredCollapsedRailWidth
    ? DESKTOP_SIDEBAR_LEFT_INSET + measuredCollapsedRailWidth
    : FALLBACK_COLLAPSED_RAIL_RIGHT
}

const getExpandedSidebarRight = () =>
  DESKTOP_SIDEBAR_LEFT_INSET + DESKTOP_SIDEBAR_PANEL_MARGIN + DESKTOP_SIDEBAR_WIDTH

const getPredictedDesktopShareButtonLeft = (sidebarVisible: boolean, createLeft: number) => {
  const EXPANDED_SHARE_LEFT = getExpandedSidebarRight() - 100
  return sidebarVisible
    ? clampDesktopFloatingLeft(EXPANDED_SHARE_LEFT)
    : clampDesktopFloatingLeft(createLeft + FLOATING_MAP_CHROME_SIZE + FLOATING_MAP_CHROME_GAP)
}

const getPredictedDesktopCreateMenuPosition = (sidebarVisible: boolean) => {
  const EXPANDED_BUTTON_DROP = FLOATING_MAP_CHROME_SIZE / 2
  return {
    left: sidebarVisible
      ? clampDesktopFloatingLeft(getExpandedSidebarRight() + FLOATING_MAP_CHROME_GAP)
      : clampDesktopFloatingLeft(getCollapsedRailRight() + FLOATING_MAP_CHROME_GAP),
    top: sidebarVisible
      ? `calc(${desktopPanelTop.value} + ${DESKTOP_SIDEBAR_PANEL_MARGIN + EXPANDED_BUTTON_DROP}px)`
      : `calc(${desktopPanelTop.value} + ${(48 - FLOATING_MAP_CHROME_SIZE) / 2}px)`,
  }
}

const syncDesktopCreateMenuPosition = () => {
  if (isSidebarVisible.value) {
    desktopCreateMenuPosition.value = getPredictedDesktopCreateMenuPosition(true)
    return
  }

  const containerRect = mapChromeContainerRef.value?.getBoundingClientRect() ?? null
  // Use the hidden measurement rail instead of the visible rail during collapse.
  // The visible rail is itself animating, which introduces a second settling step.
  const anchorRect = collapsedRailMeasureRef.value?.getBoundingClientRect() ?? null

  if (containerRect && anchorRect) {
    desktopCreateMenuPosition.value = {
      left: Math.round(clampDesktopFloatingLeft(anchorRect.right - containerRect.left + 12)),
      top: `${Math.round(anchorRect.top - containerRect.top + (anchorRect.height - FLOATING_MAP_CHROME_SIZE) / 2)}px`,
    }
    return
  }

  if (desktopCreateMenuPosition.value.left !== 0 || desktopCreateMenuPosition.value.top !== '0px')
    return

  desktopCreateMenuPosition.value = getPredictedDesktopCreateMenuPosition(isSidebarVisible.value)
}

watch(
  () => isSidebarVisible.value,
  (sidebarVisible) => {
    desktopCreateMenuPosition.value = getPredictedDesktopCreateMenuPosition(sidebarVisible)
    if (showDesktopShareButton.value) {
      desktopShareButtonPosition.value = getPredictedDesktopShareButtonPosition(sidebarVisible)
    }
  },
  { flush: 'sync' },
)

watch(
  () => desktopPanelTop.value,
  () => {
    desktopCreateMenuPosition.value = getPredictedDesktopCreateMenuPosition(isSidebarVisible.value)
    if (showDesktopShareButton.value) {
      desktopShareButtonPosition.value = getPredictedDesktopShareButtonPosition(
        isSidebarVisible.value,
      )
    }
  },
  { flush: 'sync' },
)

watch(
  () => collapsedSidebarLabel.value,
  () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        syncDesktopCreateMenuPosition()
      })
    })
  },
  { flush: 'post' },
)

watch(
  () => showCollapsedSidebarBackButton.value,
  () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        syncDesktopCreateMenuPosition()
      })
    })
  },
  { flush: 'post' },
)

useEventListener(window, 'resize', () => {
  mapChromeLayoutEpoch.value += 1
  nextTick(() => {
    requestAnimationFrame(() => {
      syncDesktopCreateMenuPosition()
    })
  })
})

watch(
  [
    () => mapChromeContainerRef.value,
    () => collapsedRailShellRef.value,
    () => collapsedRailMeasureRef.value,
    () => uiStore.toolbarLayoutEpoch,
  ],
  () => {
    nextTick(() => {
      requestAnimationFrame(() => {
        syncDesktopCreateMenuPosition()
      })
    })
  },
  { immediate: true, flush: 'post' },
)

const desktopCreateMenuStyle = computed(() => {
  return {
    top: desktopCreateMenuPosition.value.top,
    left: `${desktopCreateMenuPosition.value.left}px`,
  }
})

const getPredictedDesktopShareButtonPosition = (sidebarVisible: boolean) => {
  const createLeft =
    desktopCreateMenuPosition.value.left ||
    getPredictedDesktopCreateMenuPosition(sidebarVisible).left
  return {
    left: getPredictedDesktopShareButtonLeft(sidebarVisible, createLeft),
    top: sidebarVisible
      ? `calc(${desktopPanelTop.value} + 168px)`
      : desktopCreateMenuPosition.value.top,
  }
}

const getDesktopSmartSearchLayout = (sidebarVisible: boolean) => {
  void mapChromeLayoutEpoch.value
  void uiStore.toolbarLayoutEpoch

  const containerWidth = getMapChromeContainerWidth()
  if (containerWidth <= 0) {
    return {
      closedLeft: null as number | null,
      openLeft: null as number | null,
      expandedWidth: null as string | null,
    }
  }

  const createLeft =
    desktopCreateMenuPosition.value.left ||
    getPredictedDesktopCreateMenuPosition(sidebarVisible).left
  const shareLeft = showDesktopShareButton.value
    ? desktopShareButtonPosition.value.left ||
      getPredictedDesktopShareButtonPosition(sidebarVisible).left
    : null

  const protectedLeftEdge = Math.max(
    sidebarVisible ? getExpandedSidebarRight() : getCollapsedRailRight(),
    createLeft + FLOATING_MAP_CHROME_SIZE,
    shareLeft !== null ? shareLeft + FLOATING_MAP_CHROME_SIZE : 0,
  )
  const minimumSearchLeft = protectedLeftEdge + FLOATING_MAP_CHROME_GAP
  const centeredButtonLeft = Math.round(containerWidth / 2 - FLOATING_MAP_CHROME_SIZE / 2)
  const maxAvailableWidth = Math.max(
    FLOATING_MAP_CHROME_SIZE,
    Math.floor(containerWidth - FLOATING_MAP_CHROME_EDGE_MARGIN - minimumSearchLeft),
  )
  const expandedWidthPx = Math.min(DESKTOP_SMART_SEARCH_MAX_WIDTH, maxAvailableWidth)
  const centeredExpandedLeft = Math.round(containerWidth / 2 - expandedWidthPx / 2)
  const canCenterExpanded = centeredExpandedLeft >= minimumSearchLeft

  return {
    closedLeft: canCenterExpanded ? centeredButtonLeft : minimumSearchLeft,
    openLeft: canCenterExpanded ? centeredExpandedLeft : minimumSearchLeft,
    expandedWidth: `${expandedWidthPx}px`,
  }
}

const desktopSmartSearchFloatStyle = computed(() => {
  if (isSmallScreen.value) {
    return { top: desktopPanelVisibleTop.value }
  }

  const layout = getDesktopSmartSearchLayout(isSidebarVisible.value)
  if (layout.closedLeft === null || layout.openLeft === null) {
    return {
      top: desktopPanelVisibleTop.value,
      left: '50%',
      transform: 'translateX(-50%)',
    }
  }

  return {
    top: desktopPanelVisibleTop.value,
    left: `${uiStore.isSmartSearchOpen ? layout.openLeft : layout.closedLeft}px`,
  }
})

const desktopSmartSearchFloatClass = computed(() =>
  isSmallScreen.value
    ? 'absolute left-1/2 z-40 -translate-x-1/2'
    : 'absolute z-40 transition-[left] duration-300 ease-out',
)

const desktopSmartSearchExpandedWidth = computed(() => {
  if (isSmallScreen.value) return null
  return getDesktopSmartSearchLayout(isSidebarVisible.value).expandedWidth
})

const getOverviewFilterFloatWidthEstimate = () => {
  const pillCount = overviewFloatingVisibleFilters.value.length
  return (
    FLOATING_MAP_CHROME_SIZE +
    pillCount * OVERVIEW_FILTER_FLOAT_PILL_ESTIMATED_WIDTH +
    (pillCount > 0 ? pillCount * FLOATING_MAP_CHROME_GAP : 0)
  )
}

const desktopOverviewFilterFloatStyle = computed(() => {
  const layout = getDesktopSmartSearchLayout(isSidebarVisible.value)
  const closedLeft = layout.closedLeft ?? 0
  const openLeft = layout.openLeft ?? closedLeft
  const expandedWidthPx = layout.expandedWidth
    ? Number.parseInt(layout.expandedWidth, 10)
    : FLOATING_MAP_CHROME_SIZE
  const currentSearchRight = uiStore.isSmartSearchOpen
    ? openLeft + expandedWidthPx
    : closedLeft + FLOATING_MAP_CHROME_SIZE
  const closedAnchorRight = closedLeft + FLOATING_MAP_CHROME_SIZE
  const left = clampDesktopFloatingLeft(
    Math.max(currentSearchRight, closedAnchorRight) + FLOATING_MAP_CHROME_GAP,
    getOverviewFilterFloatWidthEstimate(),
  )

  return {
    top: desktopPanelVisibleTop.value,
    left: `${left}px`,
  }
})

watch(
  [() => desktopCreateMenuPosition.value.left, () => desktopCreateMenuPosition.value.top],
  () => {
    if (!showDesktopShareButton.value || isSidebarVisible.value) return
    desktopShareButtonPosition.value = getPredictedDesktopShareButtonPosition(false)
  },
  { flush: 'sync' },
)

watch(
  () => showDesktopShareButton.value,
  (visible) => {
    if (!visible) return
    desktopShareButtonPosition.value = getPredictedDesktopShareButtonPosition(
      isSidebarVisible.value,
    )
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => showCollapsedOverviewFilters.value,
  (visible) => {
    if (!visible) {
      isOverviewFilterFloatExpanded.value = false
    }
  },
  { immediate: true },
)

useEventListener(document, 'click', (event: Event) => {
  if (!isOverviewFilterFloatExpanded.value) return

  const target = event.target as HTMLElement | null
  if (!target?.closest('[data-overview-filter-float]')) {
    isOverviewFilterFloatExpanded.value = false
  }
})

const desktopShareButtonStyle = computed(() => ({
  top: desktopShareButtonPosition.value.top,
  left: `${desktopShareButtonPosition.value.left}px`,
}))

const desktopShareButtonClass = computed(() =>
  isSidebarVisible.value
    ? 'border border-white/35 bg-black/20 text-white hover:bg-black/35'
    : 'border border-black/[0.08] bg-white/92 text-gray-600 shadow-md backdrop-blur-sm hover:bg-white hover:text-gray-900',
)

const handleCollapsedShare = () => {
  window.dispatchEvent(new CustomEvent('open-workspace-share-modal'))
}

const handleShowAllWorkspaces = () => {
  // Navigate only — do not auto-zoom to collections bounds when the user
  // explicitly chooses to return to the overview from the floating button.
  // Disabling auto-adaptation blocks all zoom guards (isInWorkspacesOverview
  // watcher, lastSearchWorkspacesBounds watcher). It will be re-enabled
  // automatically when the user next enters a workspace.
  uiStore.disableAutoViewportAdaptation()
  uiStore.setWorkspacesOverview(true)
  uiStore.beginMapNavigation('back')
  uiStore.hideTourDetailPanel()
  router.push({ name: 'workspaces' })
}

// Handle desktop sidebar transition completion
const handleDesktopSidebarTransitionEnd = () => {
  uiStore.markToolbarLayoutStable()
}

// Zoom to workspace content, waiting for the map to finish loading first if needed.
// This handles the local-dev race condition where the API responds before MapLibre
// finishes loading its style/sprites from CDN.
const zoomToContentWhenReady = (options: { initialMapLoad?: boolean } = {}) => {
  const requestWorkspaceId = getActiveWorkspaceViewportKey()
  const requestRoutePath = route.path
  const interactionEpochAtRequest = mapInteractionEpoch
  const shouldApplyInitialCamera = options.initialMapLoad === true && mapInteractionEpoch === 0
  const initialCameraWasSuperseded = options.initialMapLoad === true && !shouldApplyInitialCamera
  const isCurrentRequest = () =>
    getActiveWorkspaceViewportKey() === requestWorkspaceId && route.path === requestRoutePath
  const hasUserInteractedDuringRequest = () => mapInteractionEpoch !== interactionEpochAtRequest

  const run = () => {
    if (!isCurrentRequest()) return
    if (initialCameraWasSuperseded) {
      finalizeWorkspaceContentMovement()
      return
    }
    if (isUserMapInteractionActive) {
      finalizeWorkspaceContentMovement()
      return
    }
    if (!shouldAutoMoveWorkspaceContent()) {
      finalizeWorkspaceContentMovement()
      if (hasPendingItemRoute()) {
        tryFocusDeepLinkedTour()
      }
      return
    }

    // On Tauri, if we remembered this collection's viewport, restore it instead
    // of auto-fitting so resume/cold launch returns to the exact saved camera.
    if (isTauri()) {
      const savedViewport = getSavedViewportForActiveWorkspace('zoom-to-content-when-ready')
      if (savedViewport) {
        void waitForMapToSettleBeforeViewportFit().then(() => {
          if (!isCurrentRequest()) return
          if (isUserMapInteractionActive || hasUserInteractedDuringRequest()) {
            finalizeWorkspaceContentMovement()
            return
          }
          if (
            restoreSavedViewportIfAvailable('zoom-to-content-when-ready:settled', {
              restoreFollowScale: shouldApplyInitialCamera && hadPersistedFollowOnMount,
            })
          ) {
            return
          }
          if (!uiStore.autoViewportAdaptationEnabled && !shouldApplyInitialCamera) {
            finalizeWorkspaceContentMovement()
            return
          }

          logService.info(
            LOG_CATEGORIES.MAP,
            'Saved viewport rejected; fitting collection content',
            {
              source: 'zoom-to-content-when-ready:settled',
              route: route.fullPath,
              workspaceId: workspaceStore.clientId,
            },
          )
          zoomToContent(undefined, { source: 'zoom-to-content-when-ready:saved-rejected' })
          finalizeWorkspaceContentMovement()
        })
        return
      }
    }

    void waitForMapToSettleBeforeViewportFit().then(() => {
      if (!isCurrentRequest()) return
      if (isUserMapInteractionActive || hasUserInteractedDuringRequest()) {
        finalizeWorkspaceContentMovement()
        return
      }
      if (!uiStore.autoViewportAdaptationEnabled && !shouldApplyInitialCamera) {
        finalizeWorkspaceContentMovement()
        return
      }
      if (!shouldAutoMoveWorkspaceContent()) {
        finalizeWorkspaceContentMovement()
        return
      }
      logService.info(LOG_CATEGORIES.MAP, 'No saved viewport; fitting collection content', {
        source: 'zoom-to-content-when-ready',
        route: route.fullPath,
        workspaceId: workspaceStore.clientId,
        routeWorkspaceId: getRouteWorkspaceId(),
        isTauri: isTauri(),
      })
      zoomToContent(undefined, { source: 'zoom-to-content-when-ready' })
      finalizeWorkspaceContentMovement()
    })
  }

  if (mapLoaded.value) {
    run()
    return
  }
  const unwatch = watch(mapLoaded, (loaded) => {
    if (!loaded) return
    unwatch()
    run()
  })
}

defineExpose({
  zoomToContent,
  zoomToContentWhenReady,
  zoomToTour,
  zoomToPhoto,
  zoomToPOI,
  zoomToWaypoint,
  zoomToTrack,
  resizeMap,
})

// React to route tourId param for deep-linked focus
watch(
  () => route.params.tourId,
  (newTourId) => {
    if (typeof newTourId === 'string' && newTourId.length > 0) {
      if (uiStore.mapNavigationIntent === null) {
        uiStore.beginMapNavigation('deeplink')
      }
      pendingDeepLinkTourId.value = newTourId
      // Ensure we're in collection detail context
      uiStore.setWorkspacesOverview(false)
      // Try immediately if possible, else will be attempted on map/workspace load
      tryFocusDeepLinkedTour()
    } else {
      pendingDeepLinkTourId.value = null
      finalizeRouteItemMovement()
    }
  },
  { immediate: true },
)

watch(
  () => shouldTemporarilySuppressSearchWorkspaceVisibility.value,
  (shouldSuppress, wasSuppressing) => {
    if (shouldSuppress) {
      if (!wasSuppressing && !searchWorkspaceStore.isVisibilityTemporarilySuppressed) {
        searchWorkspaceStore.hideAllWorkspacesForNavigation()
      } else {
        searchWorkspaceStore.hideAllWorkspacesTemporary()
      }
      return
    }

    if (wasSuppressing || searchWorkspaceStore.isVisibilityTemporarilySuppressed) {
      searchWorkspaceStore.restoreWorkspaceVisibility()
    }
  },
  { immediate: true },
)

watch(
  () => uiStore.isInWorkspacesOverview,
  (isInOverview, wasInOverview) => {
    if (!isInOverview || wasInOverview) return

    if (uiStore.isTrackDrawingActive) {
      void handleCloseTrackDrawing()
    }
    if (uiStore.isTrackEditMode) {
      void closeTrackEdit()
    }
  },
)

onUnmounted(() => {
  searchWorkspaceStore.restoreWorkspaceVisibility()
  restoreCollectionRoutingMapContent()
})

const bootstrapCollectionsOverview = async () => {
  try {
    await searchWorkspaceStore.init()
    const collections = await searchWorkspaceStore.ensureWorkspacesLoaded(true)
    searchWorkspaceStore.setAllWorkspaces(collections)
    searchWorkspaceStore.activateAllWorkspaces()
  } catch (error) {
    logService.warn(LOG_CATEGORIES.UI, 'Failed to bootstrap collections overview', {
      error,
    })
  }
}

watch(
  () => [isCollectionsOverviewContext.value, authStore.isAuthenticated] as const,
  ([isInOverview, isAuthenticated]) => {
    uiStore.setWorkspacesOverview(isInOverview)

    if (!isInOverview || !isAuthenticated) return

    void bootstrapCollectionsOverview()
  },
  { immediate: true },
)

// Cleanup of the drawing/edit sessions is handled by `useTrackRoutingHost`.
</script>

<template>
  <!-- MapHeader - positioned outside the relative container to avoid conflicts -->
  <MapHeader v-if="props.renderHeader && !isOfflineMapAreaSelector" ref="localHeaderRef" />

  <div
    ref="mapChromeContainerRef"
    class="relative w-full h-full"
    :class="{ 'offline-map-selection-active': isOfflineMapAreaSelector }"
  >
    <div
      v-if="authStore.isAuthenticated && !isSmallScreen && !uiStore.isLandingPageVisible"
      class="pointer-events-none invisible absolute left-4 z-[-1]"
      :style="{ top: desktopPanelVisibleTop }"
      aria-hidden="true"
    >
      <div
        ref="collapsedRailMeasureRef"
        :class="
          showCollapsedSidebarBackButton
            ? 'collapsed-rail-shell collapsed-rail-shell-with-back'
            : 'collapsed-rail-shell'
        "
      >
        <div
          :class="
            showCollapsedSidebarBackButton
              ? 'collapsed-rail-back-slot collapsed-rail-back-slot-open'
              : 'collapsed-rail-back-slot'
          "
        >
          <button class="flex h-8 w-8 items-center justify-center rounded-md text-gray-700">
            <ChevronLeftIcon class="h-5 w-5" />
          </button>
        </div>
        <div class="flex min-w-0 items-center gap-2">
          <span class="max-w-[min(52vw,24rem)] truncate text-lg font-semibold tracking-tight">
            {{ collapsedSidebarLabel }}
          </span>
          <UsersIcon
            v-if="
              routeWorkspaceId !== null &&
              collapsedSidebarHasWorkspaceRole &&
              !workspaceStore.isOwner
            "
            class="h-3.5 w-3.5 shrink-0 text-gray-900"
          />
          <GlobeIcon
            v-if="
              routeWorkspaceId !== null &&
              !collapsedSidebarIsBookmarkOnly &&
              workspaceStore.isPublic
            "
            class="h-3.5 w-3.5 shrink-0 text-gray-900"
          />
          <BookmarkIcon
            v-if="routeWorkspaceId !== null && collapsedSidebarIsBookmarkOnly"
            class="h-3.5 w-3.5 shrink-0 text-gray-900"
          />
        </div>
        <button class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500">
          <PanelTopOpenIcon class="h-5 w-5" />
        </button>
      </div>
    </div>

    <!-- Desktop Sidebar (WorkspaceToolbar) - Hidden on mobile -->
    <Transition name="desktop-sidebar-panel">
      <WorkspaceToolbar
        v-if="!isSmallScreen"
        v-show="isSidebarVisible && !uiStore.isLandingPageVisible"
        ref="toolbarRef"
        class="absolute left-4 z-15"
        :style="{ top: desktopPanelTop }"
        :selected-poi-id="selectedPOIId"
        @zoom-tour="handleListTourZoom"
        @zoom-photo="zoomToPhoto"
        @zoom-poi="handleListPOIZoom"
        @open-poi-detail="handleOpenPOIDetailFromList"
        @zoom-waypoint="handleListWaypointZoom"
        @view-photo="handleViewPhoto"
        @select-tour="handleListTourSelect"
        @select-workspace="selectWorkspace"
        @make-offline="handleMakeWorkspaceOffline"
        @create-tour="openCreateTourModal"
        @enter-track-edit="enterTrackEditModeForTrack"
        @create-new-workspace="createNewWorkspace"
        @trigger-gpx-upload="() => emit('trigger-gpx-upload')"
        @trigger-photo-upload="() => emit('trigger-photo-upload')"
        @transitionend="handleDesktopSidebarTransitionEnd"
      />
    </Transition>

    <Transition name="desktop-sidebar-rail-inline">
      <div
        v-if="showCollapsedSidebarRail"
        class="absolute left-4 z-16"
        :style="{ top: desktopPanelVisibleTop }"
        @transitionend="handleDesktopSidebarTransitionEnd"
      >
        <div
          ref="collapsedRailShellRef"
          :class="
            showCollapsedSidebarBackButton
              ? 'collapsed-rail-shell collapsed-rail-shell-with-back'
              : 'collapsed-rail-shell'
          "
        >
          <div
            :class="
              showCollapsedSidebarBackButton
                ? 'collapsed-rail-back-slot collapsed-rail-back-slot-open'
                : 'collapsed-rail-back-slot'
            "
          >
            <button
              class="flex h-8 w-8 items-center justify-center rounded-md text-gray-700 transition-colors hover:cursor-pointer hover:bg-black/[0.04] hover:text-gray-900"
              :tabindex="showCollapsedSidebarBackButton ? 0 : -1"
              :aria-hidden="!showCollapsedSidebarBackButton"
              aria-label="Back to collections"
              title="Back to collections"
              @click="showCollapsedSidebarBackButton && handleShowAllWorkspaces()"
            >
              <ChevronLeftIcon class="h-5 w-5" />
            </button>
          </div>
          <div class="flex min-w-0 items-center gap-2">
            <span class="max-w-[min(52vw,24rem)] truncate text-lg font-semibold tracking-tight">
              {{ collapsedSidebarLabel }}
            </span>
            <UsersIcon
              v-if="
                routeWorkspaceId !== null &&
                collapsedSidebarHasWorkspaceRole &&
                !workspaceStore.isOwner
              "
              class="h-3.5 w-3.5 shrink-0 text-gray-900"
              title="Collection shared with members"
            />
            <GlobeIcon
              v-if="
                routeWorkspaceId !== null &&
                !collapsedSidebarIsBookmarkOnly &&
                workspaceStore.isPublic
              "
              class="h-3.5 w-3.5 shrink-0 text-gray-900"
              title="Public collection"
            />
            <BookmarkIcon
              v-if="routeWorkspaceId !== null && collapsedSidebarIsBookmarkOnly"
              class="h-3.5 w-3.5 shrink-0 text-gray-900"
              title="Saved collection"
            />
          </div>
          <button
            data-testid="workspace-sidebar-show"
            class="flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:cursor-pointer hover:bg-black/[0.04] hover:text-gray-800"
            aria-label="Show sidebar"
            title="Show sidebar"
            @click="handleExpandSidebar"
          >
            <PanelTopOpenIcon class="h-5 w-5" />
          </button>
        </div>
      </div>
    </Transition>

    <div
      v-if="
        showCreateMenuFab &&
        !isSmallScreen &&
        !uiStore.isLandingPageVisible &&
        !isSelectedRouteSegmentActive
      "
      data-testid="workspace-create-menu-float"
      class="create-menu-float absolute z-30"
      :style="desktopCreateMenuStyle"
    >
      <CreateMenu />
    </div>

    <!-- Desktop only: the collapsed floating search pill. On mobile the Search tab opens a
         bottom sheet (SearchSheet) instead, so the pill is not rendered there. -->
    <div
      v-if="!isSmallScreen && !uiStore.isLandingPageVisible"
      data-testid="workspace-smart-search-float"
      :class="desktopSmartSearchFloatClass"
      :style="desktopSmartSearchFloatStyle"
    >
      <MapSmartSearch :expanded-width="desktopSmartSearchExpandedWidth" />
    </div>

    <div
      v-if="showCollapsedOverviewFilters"
      data-testid="workspace-overview-filters-float"
      class="absolute z-30 transition-[left] duration-300 ease-out"
      :style="desktopOverviewFilterFloatStyle"
    >
      <div data-overview-filter-float class="relative flex h-10 items-center">
        <button
          data-testid="workspace-overview-filters-toggle"
          class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/[0.08] bg-white/92 text-gray-600 shadow-md backdrop-blur-sm transition-[background-color,border-color,color,box-shadow] duration-300 hover:cursor-pointer hover:bg-white hover:text-gray-900"
          :aria-expanded="isOverviewFilterFloatExpanded"
          aria-label="Edit collection filters"
          title="Edit collection filters"
          @click="toggleOverviewFilterFloat"
        >
          <FunnelIcon class="h-5 w-5" />
        </button>
        <Transition name="overview-filter-float">
          <div
            v-if="overviewFloatingVisibleFilters.length > 0"
            class="absolute left-[calc(100%+12px)] top-0"
          >
            <WorkspaceOverviewFilterControls variant="floating" />
          </div>
        </Transition>
      </div>
    </div>

    <div
      v-if="
        authStore.isAuthenticated &&
        !isSmallScreen &&
        !uiStore.isLandingPageVisible &&
        showDesktopShareButton
      "
      data-testid="workspace-share-float"
      class="map-chrome-action-float absolute z-30"
      :style="desktopShareButtonStyle"
    >
      <button
        :class="desktopShareButtonClass"
        class="inline-flex h-10 w-10 items-center justify-center rounded-full transition-[background-color,border-color,color,box-shadow] duration-300 hover:cursor-pointer"
        title="Share collection"
        aria-label="Share collection"
        @click="handleCollapsedShare"
      >
        <Share2Icon class="h-5 w-5" />
      </button>
    </div>

    <!-- Large screens: Tour detail view on the right (all-activities context) -->
    <div
      v-if="
        !isSmallScreen &&
        uiStore.isAllActivitiesView &&
        uiStore.activePanel === 'tourDetail' &&
        libraryStore.detailTour
      "
      v-show="!uiStore.isLandingPageVisible"
      class="absolute right-4 z-20 flex max-h-[65vh] w-[340px] flex-col overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-lg"
      :style="{ top: desktopPanelTop }"
    >
      <TourDetailPanel
        :tour="libraryStore.detailTour"
        :loading="libraryStore.isLoadingDetail"
        :allow-track-add="true"
        :allow-track-route-edit="true"
        :selected-waypoint-index="selectedLibraryWaypointIndex"
        :waypoint-open-source="selectedWaypointOpenSource"
        :is-library-context="true"
        @back="closeTourDetailPanel"
        @zoom-waypoint="handleListWaypointZoom"
        @zoom-track="handleListTrackZoom"
        @open-waypoint-detail="handleOpenWaypointDetailFromList"
        @close-waypoint-detail="closeSelectedTourWaypointDetail"
        @delete-waypoint="handleWaypointDelete"
        @enter-track-edit="
          (tourId, trackClientId) => enterTrackEditModeForTrack(tourId, trackClientId, 'library')
        "
        class="min-h-0 flex-1"
      />
    </div>

    <!-- Large screens: Tour detail view on the right -->
    <div
      v-if="
        !isSmallScreen &&
        !uiStore.isAllActivitiesView &&
        workspaceStore._selectedTourId &&
        selectedWorkspaceTourDetail &&
        uiStore.activePanel === 'tourDetail'
      "
      v-show="!uiStore.isLandingPageVisible"
      class="absolute right-4 z-20 flex max-h-[65vh] w-[340px] flex-col overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-lg"
      :style="{ top: desktopPanelTop }"
    >
      <TrackAndWaypointList
        :detached-tour="retainedUnpinnedTourDetail"
        :selected-waypoint-index="selectedWorkspaceWaypointIndex"
        :waypoint-open-source="selectedWaypointOpenSource"
        @back="closeTourDetailPanel"
        @zoom-tour="handleListTourZoom"
        @zoom-track="handleListTrackZoom"
        @zoom-waypoint="handleListWaypointZoom"
        @zoom-route-point="zoomToRoutePoint"
        @open-waypoint-detail="handleOpenWaypointDetailFromList"
        @close-waypoint-detail="closeSelectedTourWaypointDetail"
        @delete-waypoint="handleWaypointDelete"
        @enter-track-edit="enterTrackEditModeForTrack"
        @retain-unpinned-tour-detail="retainUnpinnedTourDetail"
        class="min-h-0 flex-1"
      />
    </div>

    <div
      v-if="
        !isSmallScreen &&
        !uiStore.isAllActivitiesView &&
        selectedPOI &&
        uiStore.activePanel === 'poiDetail'
      "
      v-show="!uiStore.isLandingPageVisible"
      class="absolute right-4 z-20 flex max-h-[65vh] w-[340px] flex-col overflow-hidden rounded-xl border border-black/[0.06] bg-white shadow-lg"
      :style="{ top: desktopPanelTop }"
    >
      <POIDetailPanel
        :poi="selectedPOI"
        @close="closePOIDetailPanel"
        @zoom-poi="zoomToPOI"
        @delete-poi="handlePOIDelete"
        @retain-unpinned-poi-detail="retainUnpinnedPOIDetail"
        @replace-poi-selection="replacePOIDetailSelection"
        class="min-h-0 flex-1"
      />
    </div>

    <!-- Small screens: unified Route sheet (all-activities / library context) -->
    <RouteDetailSheet
      v-if="
        isSmallScreen &&
        uiStore.isAllActivitiesView &&
        uiStore.activePanel === 'tourDetail' &&
        libraryStore.detailTour &&
        !isTrackDrawingActive &&
        !uiStore.isTrackEditMode &&
        !navigationStore.isActive &&
        isMobileTourDetailVisible
      "
      v-show="
        !uiStore.isLandingPageVisible &&
        !isPoiBrowseSheetOpen &&
        !isMobileDetailSheetOpen &&
        !isMobileMapDrawerOpen
      "
      :tour="libraryStore.detailTour"
      :analyses="selectedTourProfileAnalyses"
      :loading="libraryStore.isLoadingDetail"
      :is-library-context="true"
      :allow-track-route-edit="true"
      :selected-waypoint-index="null"
      :waypoint-open-source="selectedWaypointOpenSource"
      :active-tab="routeSheetTab"
      :initial-mode="nextRouteSheetInitialMode"
      :metric="routeSheetMetric"
      :cursor-distance-m="selectedTourProfileCursorDistanceM"
      :visible-range-m="selectedTourProfilePanelVisibleRangeM"
      :active-metric="selectedTourProfileActiveMetric"
      :highlighted-category-id="selectedTourProfileHighlightedCategoryId"
      :current-location="selectedTourProfileCurrentLocation"
      :interaction-reset-key="selectedTourProfileInteractionResetKey"
      :stats-tracks="selectedTourProfileTrack ? [selectedTourProfileTrack] : undefined"
      :segment-stats="selectedRouteSegmentStats"
      :segment-range="selectedRouteSegmentRange"
      :ascent-grade-percent="routeSheetAscentGrade"
      :descent-grade-percent="routeSheetDescentGrade"
      @update:active-tab="routeSheetTab = $event"
      @update:mode="lastRouteSheetMode = $event"
      @update:metric="routeSheetMetric = $event"
      @update:cursor-distance-m="selectedTourProfileCursorDistanceM = $event"
      @update:visible-range-m="handleSelectedTourProfileVisibleRangeUpdate"
      @select:range="handleSelectedTourProfileRangeSelect"
      @hover:section="handleSelectedTourSectionHover"
      @cycle:section="handleSelectedTourSectionCycle"
      @leave:profile="resetSelectedTourProfileBreakdownState"
      @clear-segment="clearSelectedRouteSegmentSelection"
      @zoom-waypoint="handleListWaypointZoom"
      @zoom-track="handleListTrackZoom"
      @open-waypoint-detail="handleOpenWaypointDetailFromList"
      @close-waypoint-detail="closeSelectedTourWaypointDetail"
      @delete-waypoint="handleWaypointDelete"
      @enter-track-edit="
        (tourId, trackClientId) => enterTrackEditModeForTrack(tourId, trackClientId, 'library')
      "
      @request-rename="handleRouteSheetRenameRequest"
      @reset-name="handleRouteSheetNameReset"
      @update:height="routeSheetHeight = $event"
    />

    <!-- Small screens: unified Route sheet (collection context) -->
    <RouteDetailSheet
      v-if="
        isSmallScreen &&
        !uiStore.isAllActivitiesView &&
        selectedWorkspaceTourDetail &&
        uiStore.activePanel === 'tourDetail' &&
        !isTrackDrawingActive &&
        !uiStore.isTrackEditMode &&
        !navigationStore.isActive &&
        isMobileTourDetailVisible
      "
      v-show="
        !uiStore.isLandingPageVisible &&
        !isPoiBrowseSheetOpen &&
        !isMobileDetailSheetOpen &&
        !isMobileMapDrawerOpen
      "
      :tour="selectedWorkspaceTourDetail"
      :analyses="selectedTourProfileAnalyses"
      :loading="isSelectedTourDetailLoading"
      :allow-track-route-edit="true"
      :selected-waypoint-index="null"
      :waypoint-open-source="selectedWaypointOpenSource"
      :active-tab="routeSheetTab"
      :initial-mode="nextRouteSheetInitialMode"
      :metric="routeSheetMetric"
      :cursor-distance-m="selectedTourProfileCursorDistanceM"
      :visible-range-m="selectedTourProfilePanelVisibleRangeM"
      :active-metric="selectedTourProfileActiveMetric"
      :highlighted-category-id="selectedTourProfileHighlightedCategoryId"
      :current-location="selectedTourProfileCurrentLocation"
      :interaction-reset-key="selectedTourProfileInteractionResetKey"
      :stats-tracks="selectedTourProfileTrack ? [selectedTourProfileTrack] : undefined"
      :segment-stats="selectedRouteSegmentStats"
      :segment-range="selectedRouteSegmentRange"
      :ascent-grade-percent="routeSheetAscentGrade"
      :descent-grade-percent="routeSheetDescentGrade"
      :can-share-collection="canShareWorkspaceCollection"
      @update:height="routeSheetHeight = $event"
      @update:active-tab="routeSheetTab = $event"
      @update:mode="lastRouteSheetMode = $event"
      @update:metric="routeSheetMetric = $event"
      @update:cursor-distance-m="selectedTourProfileCursorDistanceM = $event"
      @update:visible-range-m="handleSelectedTourProfileVisibleRangeUpdate"
      @select:range="handleSelectedTourProfileRangeSelect"
      @hover:section="handleSelectedTourSectionHover"
      @cycle:section="handleSelectedTourSectionCycle"
      @leave:profile="resetSelectedTourProfileBreakdownState"
      @clear-segment="clearSelectedRouteSegmentSelection"
      @zoom-tour="handleListTourZoom"
      @zoom-track="handleListTrackZoom"
      @zoom-waypoint="handleListWaypointZoom"
      @zoom-route-point="zoomToRoutePoint"
      @open-waypoint-detail="handleOpenWaypointDetailFromList"
      @close-waypoint-detail="closeSelectedTourWaypointDetail"
      @delete-waypoint="handleWaypointDelete"
      @enter-track-edit="enterTrackEditModeForTrack"
      @retain-unpinned-tour-detail="retainUnpinnedTourDetail"
      @request-rename="handleRouteSheetRenameRequest"
      @reset-name="handleRouteSheetNameReset"
      @share-collection="handleShareCollectionFromRouteSheet"
    />

    <!-- Small screens: collection POI detail as a draggable bottom sheet (map stays visible). -->
    <DetailSheet :open="isMobilePoiDetailSheetOpen" @close="closePOIDetailPanel">
      <POIDetailPanel
        v-if="selectedPOI"
        :poi="selectedPOI"
        @close="closePOIDetailPanel"
        @zoom-poi="zoomToPOI"
        @delete-poi="handlePOIDelete"
        @retain-unpinned-poi-detail="retainUnpinnedPOIDetail"
        @replace-poi-selection="replacePOIDetailSelection"
        class="min-h-0 flex-1"
      />
    </DetailSheet>

    <!-- Small screens: tour waypoint detail as its own bottom sheet, not nested in the Route sheet. -->
    <DetailSheet :open="isMobileWaypointDetailSheetOpen" @close="closeSelectedTourWaypointDetail">
      <POIDetailPanel
        v-if="mobileWaypointDetailWaypoint && mobileWaypointDetailTour"
        mode="tour-waypoint"
        :waypoint="mobileWaypointDetailWaypoint"
        :tour-id="mobileWaypointDetailTour.clientId"
        :tour-name="mobileWaypointDetailTour.name"
        :waypoint-index="mobileWaypointDetailIndex"
        :is-library-context="uiStore.isAllActivitiesView"
        :waypoint-visible="mobileWaypointDetailVisible"
        @close="closeSelectedTourWaypointDetail"
        @back="closeSelectedTourWaypointDetail"
        @zoom-waypoint="handleListWaypointZoom"
        @delete-waypoint="handleWaypointDelete"
        @toggle-waypoint-visibility="handleMobileWaypointVisibilityToggle"
        class="min-h-0 flex-1"
      />
    </DetailSheet>

    <!-- Layers toggle button moved into MapLibre controls -->

    <!-- Map layers: a non-modal draggable sheet on mobile (map stays visible while toggling layers),
         the docked floating panel on desktop. -->
    <MapToolSheet
      v-if="isSmallScreen"
      :open="isMobileMapDrawerOpen && uiStore.isLayersPanelVisible"
      title="Map layers"
      :icon="LayersIcon"
      testid="map-layers-sheet"
      @close="uiStore.hideLayersPanel"
    >
      <template #title-suffix>
        <MapZoomIndicator />
      </template>
      <LayersList class="px-3 pb-4 pt-1" />
    </MapToolSheet>
    <LayersPanel
      v-else
      v-show="!uiStore.isLandingPageVisible"
      :is-visible="isLayersPanelVisible ?? false"
      @toggle-visibility="uiStore.toggleLayersPanel"
    />

    <!-- Map content: the user-facing POI category browser. Available everywhere (Map, Library,
         Collections overview) — view-only outside a collection; inside a collection you can also add
         POIs to it. Mobile = non-modal draggable sheet (map stays visible), desktop = docked card. -->
    <MapToolSheet
      v-if="isSmallScreen"
      :open="isMobileMapDrawerOpen && uiStore.isMapContentPanelVisible"
      title="Map content"
      :icon="MapPinnedIcon"
      testid="map-content-sheet"
      @close="uiStore.hideMapContentPanel"
    >
      <PoiCategoryBrowser />
    </MapToolSheet>
    <MapContentPanel
      v-else
      v-show="!uiStore.isLandingPageVisible"
      :is-visible="uiStore.isMapContentPanelVisible"
      @toggle-visibility="uiStore.toggleMapContentPanel"
    />

    <!-- Mobile: tapping a browsable POI opens its detail card as a draggable bottom sheet. Inside a
         collection the card also offers "Add to" targets; outside one it's view-only. -->
    <PoiBrowseSheet v-if="isSmallScreen" />
    <MapLibreRoot
      ref="mapRef"
      :initial-bounds="initialWorkspaceBounds"
      :selection-mode="isOfflineMapAreaSelector"
      class="offline-selector-map-root absolute inset-0"
      :class="{
        'map-drawing-active': isTrackDrawingActive || uiStore.isTrackEditMode,
        'landing-active': uiStore.isLandingPageVisible,
        'route-segment-selection-active': isSelectedRouteSegmentActive,
      }"
      @map-loaded="onMapLoaded"
      @map-interaction-start="onMapInteractionStart"
      @map-pan-start="onMapPanStart"
      @map-move="onMapMove"
      @map-click="handleWorkspaceMapClick"
      @current-location="onCurrentLocation"
    >
      <template v-if="!isOfflineMapAreaSelector">
        <!-- All Activities panel: render all library tours on the map -->
        <LibraryTourLayer
          v-if="uiStore.isAllActivitiesView"
          :metric-colorized-track="workspaceMetricColorizedTrack"
          @contextmenu-tour="handleLibraryContextMenu"
        />

        <WorkspaceTourLayer
          v-if="!uiStore.isAllActivitiesView"
          :tours="renderedTours"
          :dimmed="isSelectedRouteSegmentActive || isTrackDrawingActive || uiStore.isTrackEditMode"
          :suppressed-track="suppressedWorkspaceTourTrack"
          :metric-colorized-track="workspaceMetricColorizedTrack"
          @track-click="handleWorkspaceTrackClick"
          @track-overlap-select="openMapTrackOverlapMenu"
          @track-context-menu="openMapTrackContextMenu"
        />

        <!-- DrawingTrackLayer is placed after tour layers so it renders on top -->
        <DrawingTrackLayer
          :coordinates="drawingTrackPoints"
          :color="activeDrawingOverlayColor"
          :width="_isLibraryMode() && uiStore.isTrackDrawingActive ? 5 : undefined"
          :show-line="!isRouteModeMetricOverlayVisible && !uiStore.areMapElementsForceHidden"
        />

        <!-- Live in-progress recording track: rendered in every map context, passive (never moves
           the camera) so navigation / follow-location keep working while recording. -->
        <RecordingTrackLayer
          v-if="recordingStore.liveSegments.length > 0 && !uiStore.areMapElementsForceHidden"
          :segments="recordingStore.liveSegments"
        />

        <WorkspaceTrackProfileMetricLayer
          v-if="isSelectedTourProfileLayerVisible && !uiStore.areMapElementsForceHidden"
          :analysis="selectedTourProfileOverlayAnalysis"
          :cursor-only="selectedTourProfileActiveMetric === null"
          :cursor-point="selectedTourProfileCursorPoint"
          :highlighted-category-id="selectedTourProfileHighlightedCategoryId"
          :focused-segment="selectedTourProfileFocusedBreakdownSegment"
          :visible-range-m="selectedTourProfileVisibleRangeM"
          :hover-title="selectedTourProfileHoverTitle"
          :show-metric-details="true"
        />

        <TrackSegmentSelectionLayer
          v-if="
            isSelectedRouteSegmentActive &&
            selectedTourProfileAnalysis.points.length > 1 &&
            !uiStore.areMapElementsForceHidden
          "
          :analysis="selectedTourProfileAnalysis"
          :range="selectedRouteSegmentRangeM"
          :accent-color="selectedTourAccentColor"
          @update:range="selectedRouteSegmentRangeM = $event"
        />

        <WorkspaceTrackProfileMetricLayer
          v-if="isDrawingMetricOverlayVisible && !uiStore.areMapElementsForceHidden"
          :analysis="drawingTrackProfileAnalysis"
          :cursor-point="routeModeCursorPoint"
          :highlighted-category-id="routeModeHighlightedCategoryId"
          :focused-segment="routeModeFocusedBreakdownSegment"
          :visible-range-m="trackProfileVisibleRangeM"
          :show-metric-details="true"
        />

        <WorkspaceTrackProfileMetricLayer
          v-if="isEditingMetricOverlayVisible && !uiStore.areMapElementsForceHidden"
          :analysis="editingTrackProfileAnalysis"
          :cursor-point="routeModeCursorPoint"
          :highlighted-category-id="routeModeHighlightedCategoryId"
          :focused-segment="routeModeFocusedBreakdownSegment"
          :visible-range-m="trackProfileVisibleRangeM"
          :hover-title="editingTrackHoverTitle"
          :show-metric-details="true"
          :hover-layer-ids="editingTrackLayerIds"
        />

        <TrackEditReferenceLayer
          v-if="
            uiStore.isTrackEditMode &&
            !uiStore.areMapElementsForceHidden &&
            editingTrackCoordinates.length > 0 &&
            editReferenceSegments.length > 0
          "
          :segments="editReferenceSegments"
          :before-layer-id="editReferenceBeforeLayerId"
          :analysis="editReferenceProfileAnalysis"
          :hover-title="editReferenceHoverTitle"
        />

        <PhotoMarkerLayer ref="photoLayerRef" @view-photo="handleViewPhoto" />

        <SearchCollectionLayer @workspace-context-menu="openMapWorkspaceContextMenu" />

        <SmartSearchPinLayer :pin="smartSearchPin" @remove="removeSmartSearchPin" />

        <template v-if="!uiStore.isAllActivitiesView">
          <WorkspaceWaypointDotLayer :tours="tours" @waypoint-edit="handleWaypointEdit" />
          <WaypointMarkerLayer
            v-if="workspaceStore.selectedTour"
            :tour="workspaceStore.selectedTour"
            :selected-waypoint-id="selectedWorkspaceWaypointId"
            :ref="(el: any) => registerSelectedWaypointLayerRef(el)"
            @waypoint-edit="handleWaypointEdit"
          />
        </template>

        <template v-else>
          <WaypointMarkerLayer
            v-for="tour in tours"
            :key="`waypoint-layer-${tour.clientId}`"
            :tour="tour"
            :selected-waypoint-id="
              selectedWaypointDetail?.tourId === tour.clientId ? selectedLibraryWaypointId : null
            "
            @waypoint-edit="handleWaypointEdit"
          />
        </template>

        <WorkspacePOIMarkerLayer
          :pois="workspacePOIs"
          :selected-poi-id="selectedPOIId"
          @poi-edit="handlePOIEdit"
          @poi-context-menu="openMapPOIContextMenu"
        />

        <!-- gpx.studio-style POI browsing: available everywhere (view-only outside a collection;
           add-to-collection/route enabled when a collection is open). -->
        <PoiBrowseLayer />

        <RoutePointLayer
          v-for="tour in routePointLayerTours"
          :key="`route-point-layer-${tour.clientId}`"
          :tour="tour"
          :active-track-client-id="uiStore.isTrackDrawingActive ? drawingTrackClientId : undefined"
          :pending-route-points="uiStore.isTrackDrawingActive ? drawingPendingRoutePoints : []"
          :preview-route-points="uiStore.isTrackEditMode ? sparseTrackPreviewRoutePoints : []"
          @route-point-dragend="handleRoutePointDragend"
          @route-point-deleted="markRoutePointChanged"
          @pending-route-point-dragend="handlePendingDragend"
          @pending-route-point-deleted="handlePendingDeleted"
        />
      </template>
    </MapLibreRoot>

    <OfflineMapAreaSelector v-if="isOfflineMapAreaSelector" />

    <!-- Waypoint placement crosshair & controls -->
    <div
      v-if="isWaypointPlacementActive"
      class="absolute inset-0 pointer-events-none select-none z-50 flex items-center justify-center"
    >
      <div class="relative flex flex-col items-center gap-2">
        <!-- Crosshair -->
        <div class="w-12 h-12 mb-1">
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="w-0.5 h-10 bg-blue-600 drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]"></div>
          </div>
          <div class="absolute inset-0 flex items-center justify-center">
            <div class="w-10 h-0.5 bg-blue-600 drop-shadow-[0_0_3px_rgba(0,0,0,0.9)]"></div>
          </div>
          <div class="absolute inset-0 flex items-center justify-center">
            <div
              class="w-4 h-4 rounded-full border-2 border-blue-600 outline outline-white drop-shadow-[0_0_4px_rgba(0,0,0,0.85)] bg-transparent"
            ></div>
          </div>
        </div>

        <div
          v-if="pendingWaypointName"
          class="pointer-events-auto text-[11px] max-w-[260px] text-center text-gray-700 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-gray-200 -mt-4 mb-2"
        >
          {{ pendingWaypointName }}
        </div>

        <div class="flex gap-2 pointer-events-auto mt-8">
          <button
            class="px-3 py-1.5 rounded-md bg-primary-600 text-white text-xs font-medium shadow hover:bg-primary-700 transition-colors hover:cursor-pointer"
            @click="confirmWaypointPlacement"
          >
            {{ waypointPlacementConfirmLabel }}
          </button>
          <button
            class="px-3 py-1.5 rounded-md bg-gray-200 text-gray-700 text-xs font-medium shadow hover:bg-gray-300 transition-colors hover:cursor-pointer"
            @click="cancelWaypointPlacement"
          >
            Cancel
          </button>
        </div>

        <div class="text-[10px] text-gray-600 font-semibold pointer-events-none">
          Pan / zoom map to adjust position
        </div>
      </div>
    </div>

    <TrackDrawingControlPanel
      v-if="isTrackDrawingActive && !isSmallScreen"
      :can-reverse="canReverseTrackDrawing"
      :can-undo="canUndoTrackDrawing"
      :can-redo="canRedoTrackDrawing"
      :can-save="canSaveTrackDrawing"
      :has-points="drawingTrackPoints.length > 0"
      :is-calculating-route="isCalculatingRoute"
      :segments="drawingTrackSegments"
      :profile-metric="routeModeSelectedMetric"
      :tour="activeDraftTour"
      :track-client-id="drawingTrackClientId"
      @reverse="reverseTrackDrawingRoute"
      @undo="undoLastSegment"
      @redo="redoLastSegment"
      @save="handleSaveTrackDrawing"
      @cancel="handleCloseTrackDrawing"
      @close="handleCloseTrackDrawing"
      @routing-profile-change="handleRoutingProfileChange"
      @profile-metric-change="handleRouteModeProfileMetricChange"
      @zoom-route-point="zoomToRoutePoint"
      @reorder-route-points="handleReorderRoutePoints"
    />

    <TrackEditControlPanel
      v-if="isTrackEditToolbarVisible && !isSmallScreen"
      :can-reverse="canReverseTrackEdit"
      :can-undo="canUndoTrackEdit"
      :can-redo="canRedoTrackEdit"
      :can-save="canSaveTrackEdit"
      :is-calculating-route="isCalculatingRoute"
      :is-busy="isTrackEditToolbarBusy"
      :status-text="trackEditToolbarStatusText"
      :profile-metric="routeModeSelectedMetric"
      :tour="activeDraftTour"
      :track-client-id="uiStore.editingTrackClientId"
      @reverse="reverseTrackEditRoute"
      @undo="undoEditModeSegment"
      @redo="redoTrackEdit"
      @save="saveTrackEdit"
      @cancel="closeTrackEdit"
      @close="closeTrackEdit"
      @routing-profile-change="handleRoutingProfileChange"
      @profile-metric-change="handleRouteModeProfileMetricChange"
      @zoom-route-point="zoomToRoutePoint"
      @reorder-route-points="handleReorderRoutePoints"
    />

    <!-- Mobile planning sheet: the Komoot-style bottom sheet (Route Points · Profile · Details) that
         replaces the floating control panels on small screens, with drag-reorder + always-reachable
         route points (the occlusion fix). -->
    <TrackPlanningSheet
      v-if="isMobilePlanningSheetActive"
      :mode="planningSheetMode"
      :tour="activeDraftTour"
      :track-client-id="planningSheetTrackClientId"
      :drawing-point-count="drawingTrackSegments.length"
      :analyses="routeModeActiveAnalyses"
      :can-reverse="planningCanReverse"
      :can-undo="planningCanUndo"
      :can-redo="planningCanRedo"
      :can-save="planningCanSave"
      :is-calculating-route="isCalculatingRoute"
      :is-busy="planningIsBusy"
      :status-text="planningStatusText"
      :profile-metric="routeModeSelectedMetric"
      :active-metric="routeModeActiveMetric"
      :highlighted-category-id="routeModeHighlightedCategoryId"
      :cursor-distance-m="trackProfileCursorDistanceM"
      :visible-range-m="trackProfileVisibleRangeM"
      :current-location="drawingTrackProfileCurrentLocation"
      :interaction-reset-key="trackProfileInteractionResetKey"
      @reverse="planningReverse"
      @undo="planningUndo"
      @redo="planningRedo"
      @save="planningSave"
      @routing-profile-change="handleRoutingProfileChange"
      @profile-metric-change="handleRouteModeProfileMetricChange"
      @zoom-route-point="zoomToRoutePoint"
      @reorder-route-points="handleReorderRoutePoints"
      @update:cursor-distance-m="trackProfileCursorDistanceM = $event"
      @update:visible-range-m="trackProfileVisibleRangeM = $event"
      @hover:section="handleRouteModeSectionHover"
      @cycle:section="handleRouteModeSectionCycle"
      @leave:profile="resetTrackProfileBreakdownState"
      @update:height="planningSheetHeight = $event"
    />

    <TrackProfileToolbox
      v-if="
        !isSmallScreen &&
        uiStore.isTrackDrawingActive &&
        drawingTrackProfileAnalyses.gradient.points.length > 1
      "
      :key="`drawing-profile-${drawingTrackClientId ?? 'none'}`"
      :analyses="drawingTrackProfileAnalyses"
      :active-metric="routeModeActiveMetric"
      :highlighted-category-id="routeModeHighlightedCategoryId"
      :is-collapsed="trackProfileCollapsed"
      :cursor-distance-m="trackProfileCursorDistanceM"
      :current-location="drawingTrackProfileCurrentLocation"
      :interaction-reset-key="trackProfileInteractionResetKey"
      :is-calculating-route="isCalculatingRoute"
      :left-edge-margin="profileBoxLeftMargin"
      @update:is-collapsed="trackProfileCollapsed = $event"
      @update:cursor-distance-m="trackProfileCursorDistanceM = $event"
      @update:visible-range-m="trackProfileVisibleRangeM = $event"
      @hover:section="handleRouteModeSectionHover"
      @cycle:section="handleRouteModeSectionCycle"
      @leave:profile="resetTrackProfileBreakdownState"
    />

    <TrackProfileToolbox
      v-if="
        !uiStore.isTrackEditMode &&
        !uiStore.isTrackDrawingActive &&
        uiStore.activePanel === 'tourDetail' &&
        ((selectedTourProfileAnalysis.hasElevation &&
          selectedTourProfileAnalysis.points.length > 1) ||
          isSelectedTourDetailLoading) &&
        (!isSmallScreen || navigationStore.isActive)
      "
      :key="`selected-profile-${selectedTourProfileTrack?.clientId ?? 'none'}-${selectedTourProfileOpenKey}`"
      :analyses="selectedTourProfileAnalyses"
      :loading="isSelectedTourDetailLoading"
      :active-metric="selectedTourProfileActiveMetric"
      :highlighted-category-id="selectedTourProfileHighlightedCategoryId"
      :is-collapsed="trackProfileCollapsed"
      :cursor-distance-m="selectedTourProfileCursorDistanceM"
      :visible-range-m="selectedTourProfilePanelVisibleRangeM"
      :summary="selectedRouteProfileSummary"
      :current-location="selectedTourProfileCurrentLocation"
      :interaction-reset-key="selectedTourProfileInteractionResetKey"
      :is-calculating-route="false"
      :panel-class="selectedProfilePanelClass"
      :panel-body-class="selectedProfileBodyClass"
      :mobile-sheet="isSelectedRouteSegmentActive"
      :left-edge-margin="profileBoxLeftMargin"
      :bottom-offset="selectedProfileBottomOffset"
      :show-reset-zoom-button="!isSelectedRouteSegmentActive"
      :show-restore-button="navigationStore.isActive || !isSmallScreen"
      :show-close-button="!isSelectedRouteSegmentActive"
      @update:is-collapsed="trackProfileCollapsed = $event"
      @update:cursor-distance-m="selectedTourProfileCursorDistanceM = $event"
      @update:visible-range-m="handleSelectedTourProfileVisibleRangeUpdate"
      @select:range="handleSelectedTourProfileRangeSelect"
      @hover:section="handleSelectedTourSectionHover"
      @cycle:section="handleSelectedTourSectionCycle"
      @leave:profile="resetSelectedTourProfileBreakdownState"
      @close="handleCloseSelectedRouteProfile"
    />

    <TrackProfileToolbox
      v-if="
        uiStore.isTrackEditMode &&
        !isSparseTrackSetupOpen &&
        editingTrackProfileAnalyses.gradient.points.length > 1
      "
      :key="`editing-profile-${uiStore.editingTrackClientId ?? 'none'}`"
      :analyses="editingTrackProfileAnalyses"
      :active-metric="routeModeActiveMetric"
      :highlighted-category-id="routeModeHighlightedCategoryId"
      :is-collapsed="trackProfileCollapsed"
      :cursor-distance-m="trackProfileCursorDistanceM"
      :current-location="editingTrackProfileCurrentLocation"
      :interaction-reset-key="trackProfileInteractionResetKey"
      :is-calculating-route="isCalculatingRoute"
      :left-edge-margin="profileBoxLeftMargin"
      @update:is-collapsed="trackProfileCollapsed = $event"
      @update:cursor-distance-m="trackProfileCursorDistanceM = $event"
      @update:visible-range-m="trackProfileVisibleRangeM = $event"
      @hover:section="handleRouteModeSectionHover"
      @cycle:section="handleRouteModeSectionCycle"
      @leave:profile="resetTrackProfileBreakdownState"
    />

    <!-- Library tour map context menu -->
    <div v-if="libraryMapContextMenu.visible && selectedLibraryMapContextTour">
      <div
        class="fixed inset-0 z-[109]"
        @click="closeMapEntityContextMenus"
        @contextmenu.prevent="closeMapEntityContextMenus"
      />
      <BaseMenu :style="libraryMapContextMenuStyle" scroll @click.stop>
        <ActionMenuList :items="libraryMapContextItems" density="compact" />
      </BaseMenu>
    </div>

    <div v-if="mapTrackContextMenu.visible && selectedMapContextTour">
      <div
        class="fixed inset-0 z-[109]"
        @click="closeMapEntityContextMenus"
        @contextmenu.prevent="closeMapEntityContextMenus"
      />
      <BaseMenu :style="mapTourContextMenuStyle" scroll @click.stop>
        <ActionMenuList :items="mapTourContextItems" density="compact" />
      </BaseMenu>
    </div>

    <div v-if="mapTrackOverlapMenu.visible && mapTrackOverlapItems.length > 0">
      <div
        class="fixed inset-0 z-[109]"
        @click="closeMapEntityContextMenus"
        @contextmenu.prevent="closeMapEntityContextMenus"
      />
      <BaseMenu :style="mapTrackOverlapMenuStyle" scroll @click.stop>
        <ActionMenuList :items="mapTrackOverlapItems" density="compact" />
      </BaseMenu>
    </div>

    <div v-if="mapPOIContextMenu.visible && selectedMapContextPOI">
      <div
        class="fixed inset-0 z-[109]"
        @click="closeMapEntityContextMenus"
        @contextmenu.prevent="closeMapEntityContextMenus"
      />
      <BaseMenu :style="mapPOIContextMenuStyle" scroll @click.stop>
        <ActionMenuList :items="mapPOIContextItems" density="compact" />
      </BaseMenu>
    </div>

    <div v-if="mapWorkspaceContextMenu.visible && selectedMapContextWorkspace">
      <div
        class="fixed inset-0 z-[109]"
        @click="closeMapEntityContextMenus"
        @contextmenu.prevent="closeMapEntityContextMenus"
      />
      <BaseMenu :style="mapWorkspaceContextMenuStyle" scroll @click.stop>
        <ActionMenuList :items="mapWorkspaceContextItems" density="compact" />
      </BaseMenu>
    </div>

    <GpxDownloadOptionsModal
      :is-open="!!mapTourDownloadTarget"
      title="Download Tour GPX"
      :is-downloading="isMapTourDownloading"
      @close="closeMapTourDownloadModal"
      @download="handleDownloadMapTourGpx"
    />

    <OfflineDownloadDialog
      :is-open="isOfflineDialogOpen"
      :style-preference="mapStore.mapStylePreference"
      @close="isOfflineDialogOpen = false"
    />

    <AddWaypointModal
      :is-open="isAddWaypointModalOpen"
      :placement-scope="addWaypointModalScope"
      @close="closeAddWaypointModal"
      @close-tour-modal-and-collapse-mobile-toolbar="closeTourModalAndCollapseMobileToolbar"
    />

    <!-- Share modal triggered from the mobile Route sheet's collection breadcrumb -->
    <ShareWorkspaceModal :is-open="isRouteShareModalOpen" @close="isRouteShareModalOpen = false" />

    <TrackHelperPointsToolbox
      :is-open="isSparseTrackToolboxOpen"
      :point-count="sparseTrackSetup?.pointCount ?? 1"
      :min-point-count="sparseTrackSetup?.minPointCount ?? 1"
      :max-point-count="sparseTrackSetup?.maxPointCount ?? 100"
      :is-applying="isCalculatingRoute"
      @close="closeTrackEdit"
      @update:point-count="updateSparseTrackPointCount"
      @confirm="confirmSparseTrackSetup"
      @routing-profile-change="handleRoutingProfileChange"
    />

    <PhotoSlideshow
      :is-open="isSlideshowOpen"
      :initial-photo-id="currentPhotoId"
      @close="closeSlideshow"
      @navigate-to-location="zoomToPhoto"
    />
  </div>

  <MobileNavigationHud
    v-if="isSmallScreen && navigationStore.isActive"
    @end="handleNavigationEnd"
    @recenter="handleNavigationRecenter"
    @layers="uiStore.toggleLayersPanel"
  />

  <Teleport to="body">
    <div
      v-if="navigationTrackPickerTour"
      class="fixed inset-0 z-[9999] flex items-end"
      data-testid="navigation-track-picker"
    >
      <div class="absolute inset-0 bg-black/40" @click="navigationTrackPickerTour = null"></div>
      <div
        class="relative flex max-h-[60vh] w-full flex-col rounded-t-2xl bg-white shadow-xl"
        :style="{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }"
      >
        <div class="flex justify-center pt-3 pb-1">
          <div class="h-1 w-10 rounded-full bg-gray-300"></div>
        </div>
        <div class="border-b border-gray-100 px-4 py-3">
          <p class="text-sm font-semibold text-gray-900">Choose track to navigate</p>
          <p class="truncate text-xs text-gray-500">{{ navigationTrackPickerTour.name }}</p>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto py-1">
          <button
            v-for="track in getNavigableTracks(navigationTrackPickerTour)"
            :key="track.clientId"
            type="button"
            class="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50"
            @click="startNavigationForTrack(navigationTrackPickerTour, track)"
          >
            <span class="min-w-0">
              <span class="block truncate text-sm font-medium text-gray-900">{{
                track.name || navigationTrackPickerTour.name || 'Track'
              }}</span>
              <span class="block text-xs text-gray-500">Start active navigation</span>
            </span>
            <span class="rounded-full bg-emerald-700 px-3 py-1 text-xs font-semibold text-white">
              Navigate
            </span>
          </button>
        </div>
        <button
          type="button"
          class="border-t border-gray-100 py-3 text-sm font-medium text-gray-500"
          @click="navigationTrackPickerTour = null"
        >
          Cancel
        </button>
      </div>
    </div>
  </Teleport>

  <!-- Mobile Components - Outside relative container to avoid positioning conflicts -->
  <!-- Transparent hit-test guards keep gestures in the narrow map slivers around floating
       mobile chrome from leaking through to MapLibre. Their geometry follows the rendered
       panel height and the tab bar's safe-area offset. -->
  <template v-if="isMobileBottomPanelChromeVisible">
    <div
      data-testid="mobile-panel-left-map-guard"
      class="fixed left-0 w-3 touch-none"
      :style="mobilePanelSideGuardStyle"
      aria-hidden="true"
      @pointerdown.stop.prevent
      @pointermove.stop.prevent
      @touchstart.stop.prevent
      @touchmove.stop.prevent
      @wheel.stop.prevent
      @contextmenu.stop.prevent
    ></div>
    <div
      data-testid="mobile-panel-right-map-guard"
      class="fixed right-0 w-3 touch-none"
      :style="mobilePanelSideGuardStyle"
      aria-hidden="true"
      @pointerdown.stop.prevent
      @pointermove.stop.prevent
      @touchstart.stop.prevent
      @touchmove.stop.prevent
      @wheel.stop.prevent
      @contextmenu.stop.prevent
    ></div>
  </template>
  <div
    v-if="isMobileTabBarChromeVisible"
    data-testid="mobile-tab-bar-bottom-map-guard"
    class="fixed inset-x-0 bottom-0 touch-none"
    :style="mobileTabBarBottomGuardStyle"
    aria-hidden="true"
    @pointerdown.stop.prevent
    @pointermove.stop.prevent
    @touchstart.stop.prevent
    @touchmove.stop.prevent
    @wheel.stop.prevent
    @contextmenu.stop.prevent
  ></div>

  <!-- Mobile Bottom Panel Toolbar. Kept mounted in map view (v-show, not v-if) so the tab
       bar can still read its exposed active-tab state; the drawer itself is hidden. -->
  <div v-if="isSmallScreen" v-show="isMobileBottomPanelChromeVisible">
    <WorkspaceMobileToolbar
      ref="mobileToolbarRef"
      :selected-poi-id="selectedPOIId"
      :selected-route-tour-id="selectedRouteActionTourId"
      :can-navigate-selected-route="!!selectedNavigationTour"
      :can-show-selected-route-profile="selectedTourProfileAnalysis.points.length > 1"
      :can-show-selected-route-details="hasMobileTourDetailToggle"
      :show-high-header-back="isMobileCollectionChromeVisible"
      :show-high-header-primary-action="showHighHeaderPrimaryAction"
      :high-header-back-aria-label="mobileBackAriaLabel"
      @zoom-tour="handleListTourZoom"
      @zoom-photo="zoomToPhoto"
      @zoom-poi="handleListPOIZoom"
      @open-poi-detail="handleOpenPOIDetailFromList"
      @zoom-waypoint="handleListWaypointZoom"
      @view-photo="handleViewPhoto"
      @select-tour="handleListTourSelect"
      @select-workspace="selectWorkspace"
      @make-offline="handleMakeWorkspaceOffline"
      @navigate-tour="handleNavigateTour"
      @open-route-profile="handleOpenSelectedRouteProfile"
      @open-route-details="handleOpenSelectedRouteDetails"
      @create-tour="openCreateTourModal"
      @enter-track-edit="enterTrackEditModeForTrack"
      @create-new-workspace="createNewWorkspace"
      @trigger-gpx-upload="() => emit('trigger-gpx-upload')"
      @trigger-photo-upload="() => emit('trigger-photo-upload')"
      @trigger-mixed-upload="() => emit('trigger-mixed-upload')"
      @high-header-back="goBackOneLevel"
    >
      <template #high-header-create>
        <BaseFab
          v-if="mobileSurfaceActionFab"
          size="md"
          :aria-label="mobileSurfaceActionFabLabel"
          :title="mobileSurfaceActionFabLabel"
          @click="handleMobileSurfaceActionFab"
        >
          <CreateCollectionIcon
            v-if="mobileSurfaceActionFab === 'new-collection'"
            class="h-[18px] w-[18px]"
          />
          <UploadIcon v-else class="h-[18px] w-[18px]" />
        </BaseFab>
        <CreateMenu v-else-if="showCreateMenuFab" open-direction="down" size="desktop" />
      </template>
    </WorkspaceMobileToolbar>
  </div>

  <!-- Persistent mobile bottom tab bar — floats above the bottom-sheet panel and the unified
       Route sheet (so it stays reachable while a route is selected). -->
  <MobileTabBar
    v-if="isSmallScreen"
    v-show="isMobileTabBarChromeVisible"
    :active-tab="mobileTabBarActiveTab"
    @collections="handleMobileCollectionsTab"
    @library="handleMobileLibraryTab"
    @pinned="handleMobilePinnedTab"
  />

  <!-- Canvas tab surface — a draggable placeholder sheet over the map -->
  <CanvasSheet
    v-if="
      isSmallScreen &&
      isCanvasSheetOpen &&
      !isMobileRouteSheetOpen &&
      !uiStore.isSmartSearchOpen &&
      !isTrackDrawingActive &&
      !uiStore.isTrackEditMode &&
      !navigationStore.isActive &&
      !uiStore.isImmersiveMapMode
    "
    v-show="!uiStore.isLandingPageVisible"
    @close="isCanvasSheetOpen = false"
  />

  <!-- Search tab surface (mobile, MapOut-style): pinned top input + draggable bottom results
       drawer, so the map stays visible between them. -->
  <MobileSearchSurface
    v-if="
      isSmallScreen &&
      uiStore.isSmartSearchOpen &&
      !uiStore.isLandingPageVisible &&
      !isTrackDrawingActive &&
      !uiStore.isTrackEditMode &&
      !navigationStore.isActive &&
      !uiStore.isImmersiveMapMode
    "
  />

  <!-- Floating primary FAB on mobile — sits bottom-left above the tab bar, over the map.
       Collection overview and Library use direct actions; Pinned/detail keep the create menu. -->
  <div
    v-if="isSmallScreen && mobileSurfaceActionFab"
    v-show="
      !isTrackDrawingActive &&
      !uiStore.isTrackEditMode &&
      isMobileToolbarVisible &&
      !isSelectedRouteSegmentActive &&
      !isMobileRouteSheetOpen &&
      !uiStore.isProfileSheetOpen &&
      !uiStore.isSmartSearchOpen &&
      !navigationStore.isActive &&
      !uiStore.isLandingPageVisible &&
      !uiStore.isImmersiveMapMode &&
      !isPoiBrowseSheetOpen &&
      !isMobileDetailSheetOpen &&
      !isMobileMapDrawerOpen
    "
    class="fixed left-3"
    :style="{ bottom: mobileCreateFabBottom, zIndex: mobileFabZIndex }"
  >
    <BaseFab
      size="lg"
      :aria-label="mobileSurfaceActionFabLabel"
      :title="mobileSurfaceActionFabLabel"
      @click="handleMobileSurfaceActionFab"
    >
      <CreateCollectionIcon
        v-if="mobileSurfaceActionFab === 'new-collection'"
        class="h-5.5 w-5.5"
      />
      <UploadIcon v-else class="h-5.5 w-5.5" />
    </BaseFab>
  </div>

  <div
    v-if="isSmallScreen && showCreateMenuFab"
    v-show="
      !isTrackDrawingActive &&
      !uiStore.isTrackEditMode &&
      isMobileToolbarVisible &&
      !isSelectedRouteSegmentActive &&
      !isMobileRouteSheetOpen &&
      !uiStore.isProfileSheetOpen &&
      !uiStore.isSmartSearchOpen &&
      !navigationStore.isActive &&
      !uiStore.isLandingPageVisible &&
      !uiStore.isImmersiveMapMode &&
      !isPoiBrowseSheetOpen &&
      !isMobileDetailSheetOpen &&
      !isMobileMapDrawerOpen
    "
    data-testid="workspace-create-menu-float-mobile"
    class="fixed left-3"
    :style="{ bottom: mobileCreateFabBottom, zIndex: mobileFabZIndex }"
  >
    <CreateMenu open-direction="up" size="mobile" />
  </div>

  <!-- Ambient brand wordmark, debossed into the map at top-center. Replaced by the collection/track
       title at detail levels; decorative + non-interactive otherwise. -->
  <MapBrandWordmark v-if="isSmallScreen && !mobileTopTitle" :style="mobileHighHandoffStyle" />

  <!-- Floating nav-bar title at top-center (mobile detail levels) — the collection name, or the
       track name when a track is open — between the back and sync buttons. -->
  <MapCollectionTitle
    v-if="isSmallScreen && mobileTopTitle"
    :title="mobileTopTitle"
    :title-byline="mobileTopTitleByline"
    :parent="mobileTopParent"
    :byline="mobileTopByline"
    :dot-color="mobileTopDotColor"
    :style="mobileHighHandoffStyle"
  />

  <!-- Floating top-left back button (mobile detail levels): goes back one level, replacing the
       in-sheet back chevrons. Same row as the right-side sync button. -->
  <MapBackButton
    v-if="isSmallScreen"
    v-show="isMobileCollectionChromeVisible"
    :aria-label="mobileBackAriaLabel"
    :style="mobileBackHandoffStyle"
    @back="goBackOneLevel"
  />

  <!-- Floating top-left close (X) for the mobile planner: cancels drawing/editing (with a confirm
       when there's unsaved work). Lives in the same top-left slot as the back button — which is
       hidden during planning — so the planner sheet no longer needs an in-sheet Cancel. -->
  <div
    v-if="isMobilePlanningSheetActive"
    class="fixed left-3"
    :style="{ top: safeAreaTopOffset(12), zIndex: 30 }"
    data-testid="planning-close-button"
  >
    <BaseFab size="lg" variant="neutral" aria-label="Close route planner" @click="planningCancel">
      <XIcon class="h-5.5 w-5.5" />
    </BaseFab>
  </div>

  <!-- Floating sync status button on mobile (top-right) — restores the desktop header's sync
       icon (MapHeader is hidden on mobile). Tap opens the sync dialog directly. Reflects status
       via icon shape + colour + spin + count badge. Built on BaseFab (neutral, lg) so it matches
       the top-left back button's round-white language exactly — same row, same shape.
       Stays visible in immersive/full-map mode. No blind spots (sync-feedback.md §4.4): while
       the top is occupied (search) or in track-draw/edit/navigation, calm states hide as before —
       but critical states (sync error, signed-out queue, offline backlog) keep a compact (md)
       presence so they are never invisible. -->
  <button
    v-if="isSmallScreen && (authStore.isAuthenticated || syncSignedOutWithQueue)"
    v-show="
      !uiStore.isLandingPageVisible &&
      ((!uiStore.isSmartSearchOpen &&
        !isTrackDrawingActive &&
        !uiStore.isTrackEditMode &&
        !navigationStore.isActive) ||
        syncCriticalPresence)
    "
    type="button"
    data-testid="mobile-sync-status-button"
    class="fixed right-3 flex items-center justify-center rounded-full transition-all duration-200 hover:cursor-pointer"
    :class="[
      // 48px to line up with the round back button on the same row; compact while
      // draw/edit/navigation own the screen (no-blind-spots presence, §4.4).
      isTrackDrawingActive || uiStore.isTrackEditMode || navigationStore.isActive
        ? 'h-10 w-10'
        : 'h-12 w-12',
      // Calm technology: frosted surface only when the state needs attention,
      // fully transparent otherwise.
      syncNeedsAttention
        ? 'border border-white/60 bg-white/70 shadow-[0_8px_30px_rgba(15,23,42,0.14)] backdrop-blur-xl backdrop-saturate-150'
        : 'border border-transparent bg-transparent',
    ]"
    :style="{ top: safeAreaTopOffset(12), zIndex: Z_INDEX.POPOVER }"
    :title="syncButtonTitle"
    :aria-label="syncButtonTitle"
    @click="openSyncStatus"
  >
    <component
      :is="syncIconComponent"
      :class="[
        'h-5 w-5 transition-[filter]',
        syncIconStateClass,
        // Without a background (calm state) add a subtle shadow so the icon stays legible on the map.
        syncNeedsAttention ? '' : 'drop-shadow-[0_1px_2px_rgba(15,23,42,0.45)]',
      ]"
    />
    <!-- Queued-item count badge: shape + number, never colour alone (§4.4) -->
    <span
      v-if="syncBadgeCount > 0"
      :class="[
        'pointer-events-none absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none shadow-sm',
        syncBadgeToneClass,
      ]"
      data-testid="mobile-sync-badge"
    >
      {{ syncBadgeCount > 99 ? '99+' : syncBadgeCount }}
    </span>
  </button>

  <AddToCollectionHost />
  <MobileRenameActionSheetHost />
</template>

<style scoped>
.offline-map-selection-active > :not(.offline-selector-map-root):not(.offline-map-area-selector) {
  display: none !important;
}

.offline-map-selection-active :deep(.map-content-wrapper),
.offline-map-selection-active :deep(.layers-wrapper),
.offline-map-selection-active :deep(.map-style-wrapper),
.offline-map-selection-active :deep(.hide-elements-wrapper),
.offline-map-selection-active :deep(.immersive-wrapper),
.offline-map-selection-active :deep(.fullscreen-wrapper),
.offline-map-selection-active :deep(.map-style-panel),
.offline-map-selection-active :deep([data-testid='map-style-sheet']) {
  display: none !important;
}

/* Enforce crosshair cursor when in drawing mode, overriding MapLibre's default grab cursor */
:deep(.map-drawing-active .maplibregl-canvas-container),
:deep(.map-drawing-active .maplibregl-canvas) {
  cursor: crosshair !important;
}

/* Hide MapLibre native controls when landing overlay is active */
:deep(.landing-active .maplibregl-control-container) {
  display: none;
}

:deep(.route-segment-selection-active .zoom-controls-wrapper),
:deep(.route-segment-selection-active .maplibregl-ctrl-fullscreen) {
  display: none !important;
}

.collapsed-rail-shell {
  display: inline-flex;
  height: 3rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem 0 1.25rem;
  border-radius: 0.75rem;
  border: 1px solid rgb(15 23 42 / 0.08);
  background: rgb(255 255 255 / 0.92);
  color: rgb(17 24 39);
  box-shadow:
    0 10px 25px -8px rgb(15 23 42 / 0.18),
    0 2px 6px -2px rgb(15 23 42 / 0.12);
  backdrop-filter: blur(10px);
  transition:
    gap 260ms cubic-bezier(0.22, 1, 0.36, 1),
    padding 260ms cubic-bezier(0.22, 1, 0.36, 1),
    border-radius 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.collapsed-rail-shell-with-back {
  gap: 0.625rem;
  padding-left: 0.625rem;
}

.collapsed-rail-back-slot {
  width: 0;
  opacity: 0;
  overflow: hidden;
  flex-shrink: 0;
  transition:
    width 260ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 180ms ease,
    margin-right 260ms cubic-bezier(0.22, 1, 0.36, 1);
}

.collapsed-rail-back-slot-open {
  width: 2rem;
  opacity: 1;
}

.create-menu-float {
  transition:
    left 320ms cubic-bezier(0.22, 1, 0.36, 1),
    top 320ms cubic-bezier(0.22, 1, 0.36, 1);
}

.map-chrome-action-float {
  transition:
    left 320ms cubic-bezier(0.22, 1, 0.36, 1),
    top 320ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity 220ms ease,
    transform 220ms ease;
}

.overview-filter-float-enter-active,
.overview-filter-float-leave-active {
  transition:
    opacity 240ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 280ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 220ms ease;
  transform-origin: left center;
}

.overview-filter-float-enter-from,
.overview-filter-float-leave-to {
  opacity: 0;
  filter: blur(4px);
  transform: translateX(-14px) scaleX(0.72) scaleY(0.9);
}

.desktop-sidebar-panel-enter-active,
.desktop-sidebar-panel-leave-active,
.desktop-sidebar-rail-inline-enter-active,
.desktop-sidebar-rail-inline-leave-active {
  transition:
    opacity 280ms cubic-bezier(0.22, 1, 0.36, 1),
    transform 320ms cubic-bezier(0.22, 1, 0.36, 1),
    filter 280ms ease;
}

.desktop-sidebar-panel-enter-active,
.desktop-sidebar-panel-leave-active,
.desktop-sidebar-rail-inline-enter-active,
.desktop-sidebar-rail-inline-leave-active {
  transform-origin: top left;
}

.desktop-sidebar-panel-enter-from,
.desktop-sidebar-panel-leave-to {
  opacity: 0;
  filter: blur(5px);
  transform: translateX(0) translateY(-4px) scaleX(0.64) scaleY(0.16);
}

.desktop-sidebar-rail-inline-enter-from,
.desktop-sidebar-rail-inline-leave-to {
  opacity: 0;
  filter: blur(4px);
  transform: translateX(-10px) translateY(-8px) scaleX(0.92) scaleY(0.94);
}
</style>
